import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FileText, RefreshCw, ArrowUpRight, Calendar, Download, BarChart3, TrendingUp, ChevronDown, Settings2, X, Activity, Target, PieChart, Flag, Trophy } from 'lucide-react';
import Layout from '../components/Layout';
import IndexJumpBar, { cardId } from '../components/IndexJumpBar';
import AllocationDonut from '../components/AllocationDonut';
import IndexChipRows from '../components/IndexChipRows';
import SegmentControl from '../components/SegmentControl';
import AllocationLab, { sleeveColor } from '../components/AllocationLab';
import { PeriodHeaderRow, PeriodSummary, PERIOD_TONE, GroupBySelect } from '../components/TradePeriods';
import { computePeriods, parseTarget } from '../utils/tradePeriods';
import { FILTER_LABEL, FILTER_ROW, SelectorToggle, PeriodControl, RangeInputs, IncludeOpenToggle, SwitchControl, ThresholdSelect, WeightSlider, MixBar, EXECUTION_OPTIONS } from '../components/FilterControls';
import BestTrades from '../components/BestTrades';
import ModelDiffTable, { DIFF_COLUMNS } from '../components/ModelDiffTable';
import { Skel, SkelRows, TableSkeleton } from '../components/Skeleton';
import BusyIcon from '../components/BusyIcon';
import { ModelAge } from '../components/ModelVersion';
import ProcessingStatus, { dimClass } from '../components/ProcessingStatus';
import { useLatestRequest, isCanceled } from '../utils/latestRequest';
import UpdatingBar from '../components/UpdatingBar';
import { apiService, loadUniverseShorts } from '../services/api';
import { withStreaks, buildTransactionsCsv, transactionsFilename, downloadCsv } from '../utils/transactionsExport';
import { HORIZON_OPTIONS } from '../utils/horizons';
import { calendarYears } from '../utils/calendarYears';
import { makeTradeFilter, makeHeldFilter, isCarriedIn, isOpenAtEnd, compoundGain, indexReturnPct, computeSymbolStats, computeOverallSummary, computeExcludedSymbols, computeSystemReturns, computeInMarketBySymbol, computeInMarketPct, computeBriefView, promiseCheck } from '../utils/signalAnalysisCalcs';
import SignalBrief, { BriefSkeleton } from '../components/SignalBrief';
import { buildPairs, pairsInWindow, countedTrades, sliceSeries, basisBefore, seriesWindow } from '../utils/txRangeStats';
import { flagYears } from '../utils/flaggedYears';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';

// One visual language for the data-changing selections (Model / Strategy / Costs /
// Execution). Label above, pill group below — keeps the toolbar clean and consistent.
// Model-version colors — spread across distinct HUES so many versions stay tellable apart
// (v1 neutral baseline; then blue / amber / green / pink). Few versions overall (forward-only).
const VERSION_CHIP = {
  1: 'bg-slate-600/40 text-slate-200 border-slate-500/50',
  2: 'bg-indigo-500/25 text-indigo-200 border-indigo-400/50',
  3: 'bg-amber-500/25 text-amber-200 border-amber-400/50',
  4: 'bg-emerald-500/25 text-emerald-200 border-emerald-400/50',
  5: 'bg-rose-500/25 text-rose-200 border-rose-400/50',
};
const versionChipClass = (v) => VERSION_CHIP[v] || 'bg-fuchsia-500/25 text-fuchsia-200 border-fuchsia-400/50';
// Left-edge rail per row so each version block reads as one group; the boundary is where it flips.
const VERSION_RAIL = {
  1: 'border-l-slate-500/60',
  2: 'border-l-indigo-400/70',
  3: 'border-l-amber-400/70',
  4: 'border-l-emerald-400/70',
  5: 'border-l-rose-400/70',
};
const versionRailClass = (v) => VERSION_RAIL[v] || 'border-l-fuchsia-400/70';
// Boundary-band background (subtle, version-tinted) so the section break is unmistakable.
const VERSION_BAND = {
  1: 'bg-slate-500/10',
  2: 'bg-indigo-500/10',
  3: 'bg-amber-500/10',
  4: 'bg-emerald-500/10',
  5: 'bg-rose-500/10',
};
const versionBandClass = (v) => VERSION_BAND[v] || 'bg-fuchsia-500/10';

// Equal weight is exactly equal — 8 indexes are 12.5% each, never 16/12/12…
// (a floored split handed the remainder to the first index, so "Equal" on
// Compare didn't match Signal Analysis' true equal weight).
const equalAllocations = (syms) => Object.fromEntries(syms.map(s => [s, 100 / syms.length]));
const fmtWeight = (v) => (Math.abs(v - Math.round(v)) < 0.05 ? String(Math.round(v)) : v.toFixed(1));

const formatSingleDate = (dateStr) => {
  if (!dateStr) return '';
  const trimmed = dateStr.trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    const [y, m, d] = trimmed.substring(0, 10).split('-');
    return `${d}-${m}-${y}`;
  }
  return trimmed;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  if (dateStr.includes('→')) {
    const parts = dateStr.split('→');
    return parts.map(p => formatSingleDate(p.trim())).join(' → ');
  }
  return formatSingleDate(dateStr);
};

// One sort direction for the WHOLE table: under 'desc' (Trade # ↓) both the
// groups AND the rows inside each group reverse — never mixed ordering.
export function orderStreakGroups(groups, sortOrder) {
  if (sortOrder !== 'desc') return groups;
  return [...groups].reverse().map(g => ({ ...g, trades: [...g.trades].reverse() }));
}

// Consecutive-loss groups. A trailing FORCED exit is the OPEN position marked
// at the last stored close — that streak is ONGOING, not ended: endDate stays
// null and markedDate carries the mark date, so no fake exit date ever renders.
export function buildLossStreakGroups(allPairs) {
  const groups = []; let cur = null; let mult = 1;
  for (const t of allPairs) {
    if (t.gain !== null && t.gain < 0) {
      if (!cur) { mult = 1; cur = { id: 0, trades: [], totalLoss: 0, startDate: t.entry_date, endDate: t.exit_date, markedDate: null }; }
      mult *= (1 + t.gain / 100);
      cur.trades.push(t);
      cur.totalLoss = (mult - 1) * 100;
      const forced = String(t.exit_type || '').includes('Forced');
      cur.endDate = forced ? null : t.exit_date;
      cur.markedDate = forced ? t.exit_date : null;
    } else {
      if (cur && cur.trades.length >= 2) groups.push(cur);
      cur = null;
    }
  }
  if (cur && cur.trades.length >= 2) groups.push(cur);
  groups.forEach((g, i) => { g.id = i + 1; });
  return groups;
}

// "2026-03-21 20:11:18" → "8:11 PM, 21 Mar 2026"
const formatHumanTime = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr.replace(' ', 'T'));
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString('en-IN', {
      hour: 'numeric', minute: '2-digit', hour12: true,
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch { return dateStr; }
};

// ═══════════════════════════════════════════════════════════════
// Searchable Index Dropdown
// ═══════════════════════════════════════════════════════════════
const SearchableIndexDropdown = ({ regions, selectedSymbol, handleSymbolChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropRef = React.useRef(null);
  const selectedRef = React.useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Opening the list lands ON the current selection (Apal: "if HSI was
  // selected it should stay on HSI vs going to top") — until a search
  // narrows the list, which then starts from its own top.
  useEffect(() => {
    if (open && !search) selectedRef.current?.scrollIntoView?.({ block: 'center' });
  }, [open, search]);

  const filtered = regions.map(r => ({
    ...r,
    symbols: r.symbols.filter(s =>
      (s.label || s.name).toLowerCase().includes(search.toLowerCase()) ||
      s.name.toLowerCase().includes(search.toLowerCase())
    ),
  })).filter(r => r.symbols.length > 0);

  return (
    <div ref={dropRef} className="relative min-w-[220px]">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-dark-700 border border-dark-500/50 rounded-lg text-white text-sm font-semibold focus:ring-2 focus:ring-primary-500 cursor-pointer">
        <span>{selectedSymbol?.label || selectedSymbol?.name || 'Select Index'}</span>
        <ChevronDown className={`w-4 h-4 text-dark-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-dark-800 border border-dark-600 rounded-lg shadow-xl z-50 max-h-80 flex flex-col">
          <div className="p-2 border-b border-dark-700">
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search indexes..."
              className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white text-sm placeholder-dark-400 focus:outline-none focus:ring-1 focus:ring-primary-500" />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.map(r => (
              <div key={r.code}>
                <div className="px-3 py-1.5 text-[10px] text-dark-400 uppercase tracking-widest font-semibold bg-dark-800 sticky top-0">{r.name}</div>
                {r.symbols.map(s => (
                  <button key={s.id ?? s.name}
                    ref={selectedSymbol?.name === s.name ? selectedRef : null}
                    onClick={() => { handleSymbolChange({ target: { value: `${s.market_name}:${s.name}` } }); setOpen(false); setSearch(''); }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-dark-700 transition-colors ${selectedSymbol?.name === s.name ? 'text-primary-400 font-semibold bg-dark-700/50' : 'text-dark-200'}`}>
                    {s.label || s.name}
                  </button>
                ))}
              </div>
            ))}
            {filtered.length === 0 && <div className="px-4 py-3 text-sm text-dark-400">No indexes match "{search}"</div>}
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// Export Dropdown
// ═══════════════════════════════════════════════════════════════
const ExportDropdown = ({ selectedSymbol, allPairs, strategy, executionMode }) => {
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportRef = React.useRef(null);

  useEffect(() => {
    if (!exportOpen) return;
    const h = (e) => { if (exportRef.current && !exportRef.current.contains(e.target)) setExportOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [exportOpen]);

  const downloadTransactions = () => {
    try {
      if (!allPairs || allPairs.length === 0) return;
      // Shared exporter — same pipeline the dashboard "Download transactions" uses.
      const filename = transactionsFilename(selectedSymbol?.name, strategy, executionMode);
      downloadCsv(filename, buildTransactionsCsv(withStreaks(allPairs)));
    } catch (e) { console.error('Export failed:', e); }
    setExportOpen(false);
  };

  const downloadOHLC = async () => {
    setExportOpen(false);
    setExporting(true);
    try {
      const res = await apiService.getOHLCExport(selectedSymbol?.name);
      const rows = res.data.data;
      // Server says whether shorts are permitted for this symbol — without
      // that permission the download carries no Short Signal column at all.
      const withShorts = res.data.shorts_included !== false;
      const csv = (withShorts
        ? 'Date,Open,High,Low,Close,Score,Zone,Signal,Short Signal,Position\n'
        : 'Date,Open,High,Low,Close,Score,Zone,Signal,Position\n')
        + rows.map(r => `${r.date},${r.open ?? ''},${r.high ?? ''},${r.low ?? ''},${r.close ?? ''},${r.score ?? ''},${r.zone ?? ''},${r.signal ?? ''},${withShorts ? `${r.short_signal ?? ''},` : ''}${r.position ?? ''}`).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedSymbol?.name?.toLowerCase()}_ohlc.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error('OHLC export failed:', e); }
    finally { setExporting(false); }
  };

  return (
    <div ref={exportRef} className="relative">
      <button onClick={() => setExportOpen(!exportOpen)} disabled={exporting} aria-busy={exporting} data-testid="export-button"
        className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-80 disabled:cursor-wait">
        <BusyIcon busy={exporting} icon={Download} />
        <span>{exporting ? 'Exporting…' : 'Export'}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${exportOpen ? 'rotate-180' : ''}`} />
      </button>
      {exportOpen && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-dark-800 border border-dark-600 rounded-lg shadow-xl z-50 overflow-hidden">
          <button onClick={downloadTransactions} className="w-full text-left px-4 py-2.5 text-sm text-dark-200 hover:bg-dark-700 transition-colors">
            Export Transactions
          </button>
          <button onClick={downloadOHLC} className="w-full text-left px-4 py-2.5 text-sm text-dark-200 hover:bg-dark-700 transition-colors border-t border-dark-700">
            Export OHLC
          </button>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// Compare Indexes Component
// ═══════════════════════════════════════════════════════════════
const CompareIndexes = ({ strategy, netCosts, costConfig, executionMode, reportSource, reloadKey, filterSlot = null }) => {
  // Selection survives tab switches and reloads — nobody reselects ten
  // indexes twice (same convention as the Allocation lab).
  const [selected, setSelected] = useState(() => {
    try { return JSON.parse(localStorage.getItem('strategy.compareSelected')) || []; } catch { return []; }
  });
  const [allocations, setAllocations] = useState({});
  useEffect(() => { localStorage.setItem('strategy.compareSelected', JSON.stringify(selected)); }, [selected]);
  // Row reorder must arm ONLY from the grip handle — otherwise dragging a
  // weight slider hijacks into a row-drag and the % can't be changed.
  const [dragArmed, setDragArmed] = useState(null);
  const [compareData, setCompareData] = useState({});
  const [loading, setLoading] = useState(false);
  // the comparison for the current choices has been asked for and answered
  // (waiting shimmers; tried-and-empty says so — never "select indexes")
  const [compareTried, setCompareTried] = useState(false);
  const [regionSymbols, setRegionSymbols] = useState([]);
  const [regionsLoading, setRegionsLoading] = useState(true);
  const [selectorOpen, setSelectorOpen] = useState(true);
  const [investmentAmt, setInvestmentAmt] = useState(() => {
    // empty = no Amount column (Apal 2026-10-18); never saved → ₹1 Cr
    try { const v = localStorage.getItem('reports.investmentAmt'); return v === null ? 1 : (parseFloat(v) || 0); } catch { return 1; }
  });
  const [yearFrom, setYearFrom] = useState('');
  // the table's own scroll box — frozen panes; its width keeps the Invested
  // rows' pinned content exactly as wide as what's visible
  const tableBoxRef = useRef(null);
  const [tableBoxW, setTableBoxW] = useState(null);
  // Scroll handoff (Apal 2026-10-19: "I go to table then table starts
  // scrolling but it's 20% top area of stats and 80% for table"). While the
  // table hasn't reached the top of the page's scroll area, the wheel moves
  // the PAGE; once the table fills the view, the wheel scrolls the table; at
  // the table's top, scrolling up hands back to the page (scroll chaining).
  useEffect(() => {
    const box = tableBoxRef.current;
    if (!box) return undefined;
    const scrollerOf = (el) => {
      for (let p = el.parentElement; p; p = p.parentElement) {
        const oy = getComputedStyle(p).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight) return p;
      }
      return document.scrollingElement || document.documentElement;
    };
    const onWheel = (e) => {
      if (e.deltaY <= 0 || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;   // up / sideways: native
      const scroller = scrollerOf(box);
      const top = scroller === document.scrollingElement || scroller === document.documentElement ? 0 : scroller.getBoundingClientRect().top;
      const gap = box.getBoundingClientRect().top - top;
      const pageCanMove = scroller.scrollTop + scroller.clientHeight < scroller.scrollHeight - 1;
      if (gap > 1 && pageCanMove) {
        e.preventDefault();
        scroller.scrollBy({ top: Math.min(e.deltaY, gap) });
      }
    };
    box.addEventListener('wheel', onWheel, { passive: false });
    return () => box.removeEventListener('wheel', onWheel);
  });
  useEffect(() => {
    const el = tableBoxRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setTableBoxW(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  });
  const [yearTo, setYearTo] = useState('');
  // ONE Period control, the same as Quarterly Wins and Signal Analysis (Apal
  // 2026-10-15: "make things super consistent with other pages not random"):
  // All history · Year · Custom. Compare's rows are years, so Custom picks years.
  const [periodMode, setPeriodMode] = useState('all');
  const pickAllYears = () => { setPeriodMode('all'); setYearFrom(''); setYearTo(''); };
  const pickOneYear = (y) => { setPeriodMode('year'); setYearFrom(String(y)); setYearTo(String(y)); };
  // Custom starts from the range on screen, so its selects never show a year that isn't applied
  const pickCustomYears = (years) => {
    setPeriodMode('custom');
    if (!years.length) return;
    setYearFrom(f => f || String(years[years.length - 1]));
    setYearTo(t => t || String(years[0]));
  };
  const [perSymStrategy, setPerSymStrategy] = useState({}); // { NIFTY50: 'with_shorts', BTC: '1x' }
  // Horizon: rolling any-entry N-year periods (1 = plain years), persisted
  const [cmpHorizon, setCmpHorizon] = useState(() => {
    try { const v = JSON.parse(localStorage.getItem('strategy.compareHorizon')); return [1, 2, 3].includes(v) ? v : 1; } catch { return 1; }
  });
  useEffect(() => { localStorage.setItem('strategy.compareHorizon', JSON.stringify(cmpHorizon)); }, [cmpHorizon]);
  // Highlight years whose Diversified strategy return is under a bar (Apal
  // 2026-10-15: "a filter for highlighting years that doesn't have minimum
  // strategy return for example 10%"). null = off. A multi-year row is held to
  // the same bar per year, compounded — 10%/yr over 2Y is 21% — never to a
  // one-year bar.
  const [minStrat, setMinStrat] = useState(() => {
    try { const v = JSON.parse(localStorage.getItem('strategy.compareMinStrategy')); return [0, 5, 10, 15, 20].includes(v) ? v : null; } catch { return null; }
  });
  useEffect(() => { localStorage.setItem('strategy.compareMinStrategy', JSON.stringify(minStrat)); }, [minStrat]);
  useEffect(() => { localStorage.setItem('reports.investmentAmt', String(investmentAmt)); }, [investmentAmt]);
  // L+S verdict from THE central universe — capability ∩ tradability ∩ grant
  // null = grants not yet known. Fetches WAIT for the verdict — otherwise
  // the first request runs everything long-only and the numbers silently
  // change when the universe answer lands (the 6%-then-13.9% refresh bug).
  const [shortsSyms, setShortsSyms] = useState(null);
  useEffect(() => { loadUniverseShorts().then(s => setShortsSyms([...s])).catch(() => setShortsSyms([])); }, []);
  // The chips are the single source of truth for L/L+S on Strategy tabs:
  // shortable indexes default to the full book, everything else is long-only.
  // An index without L+S on its chip (Smallcap — not shortable) is ALWAYS long-only
  // here, whatever was chosen or stored before (Apal 2026-10-12).
  const getSymStrategy = (sym) => ((shortsSyms || []).includes(sym) ? (perSymStrategy[sym] || 'with_shorts') : '1x');

  const formatCr = (val) => {
    if (val == null || isNaN(val)) return '—';
    if (val >= 100000) return `₹${(val / 100000).toFixed(1)} Lakh Cr`;
    if (val >= 10000) return `₹${(val / 1000).toFixed(0)}K Cr`;
    if (val >= 1000) return `₹${val.toLocaleString('en-IN', { maximumFractionDigits: 0 })} Cr`;
    if (val >= 100) return `₹${val.toFixed(0)} Cr`;
    if (val >= 10) return `₹${val.toFixed(1)} Cr`;
    if (val >= 1) return `₹${val.toFixed(2)} Cr`;
    return `₹${(val * 100).toFixed(0)} L`;
  };

  useEffect(() => {
    apiService.getPeriodPerformance({ market: 'all', strategy: '1x' }).then(res => {
      setRegionSymbols((res.data?.regions || []).map(r => ({
        name: r.region, code: r.code,
        symbols: r.indexes.map(i => ({ name: i.symbol, label: i.label })),
      })));
    }).catch(() => {}).finally(() => setRegionsLoading(false));
  }, []);

  const labelMap = {};
  regionSymbols.forEach(r => r.symbols.forEach(s => { labelMap[s.name] = s.label; }));

  // Auto-distribute allocations equally when selection changes
  useEffect(() => {
    if (selected.length === 0) { setAllocations({}); return; }
    setAllocations(equalAllocations(selected));
  }, [selected]);

  const toggle = (sym) => {
    // No cap — compare as many as you like; the table scrolls sideways and
    // the Diversified verdict stays pinned left.
    setSelected(prev =>
      prev.includes(sym) ? prev.filter(s => s !== sym) : [...prev, sym]
    );
  };
  // The region label (INDIA / US / …) selects or clears a whole market — same
  // as Signal Analysis, Quarterly Wins and Allocation (Apal 2026-10-22: "else
  // i have to manually deselect whole market").
  const toggleRegion = (regionCode) => {
    const region = regionSymbols.find(r => r.code === regionCode);
    if (!region) return;
    const regionNames = region.symbols.map(s => s.name);
    const allSelected = regionNames.every(n => selected.includes(n));
    setSelected(prev => allSelected
      ? prev.filter(s => !regionNames.includes(s))
      : [...new Set([...prev, ...regionNames])]
    );
  };

  // Auto-fetch data when selection changes (2+ indexes)
  // Clear compare cache when strategy or netCosts changes
  useEffect(() => { setCompareData({}); setCompareTried(false); }, [strategy, netCosts, costConfig, executionMode, reportSource, reloadKey, perSymStrategy]);

  useEffect(() => {
    if (shortsSyms === null) return; // wait for the shorts verdict — see above
    if (selected.length < 2) { setCompareData({}); setCompareTried(false); return; }
    let cancelled = false;
    const abort = new AbortController();     // a newer selection cancels this batch
    const fetchAll = async () => {
      setLoading(true);
      const newData = {};
      await Promise.all(selected.map(async (sym) => {
        try {
          const symStrat = getSymStrategy(sym);
          const costParams = {};
          if (netCosts && ['1x', 'with_shorts', 'shorts_only'].includes(symStrat)) {
            costParams.net_costs = 'true';
            Object.entries(costConfig || {}).forEach(([s, cfg]) => {
              if (cfg && typeof cfg === 'object') { costParams[`cost_${s}`] = cfg.impact ?? 0; costParams[`carry_${s}`] = cfg.carry_pct ?? 0; costParams[`intrate_${s}`] = cfg.interest_rate ?? 0; costParams[`margin_${s}`] = cfg.margin_pct ?? 20; }
            });
          }
          const execParam = executionMode !== 'same_day_close' ? { execution: executionMode } : {};
          const res = await apiService.getYearlyStatistics({ symbol: sym, strategy: symStrat, ...execParam, ...costParams, ...(reportSource === 'latest' ? { source: 'latest' } : {}) }, { signal: abort.signal }); if (!cancelled) newData[sym] = res.data;
        }
        catch (e) { if (!isCanceled(e)) console.error(`Compare failed for ${sym}:`, e); }
      }));
      if (!cancelled) { setCompareData(newData); setCompareTried(true); setLoading(false); }
    };
    fetchAll();
    return () => { cancelled = true; abort.abort(); };
  }, [selected, strategy, netCosts, costConfig, executionMode, reportSource, reloadKey, perSymStrategy, shortsSyms]);

  const symbolsList = selected.filter(s => compareData[s]);
  const getYearData = (sym, year) => (compareData[sym]?.yearly_returns || []).find(y => y.year === year);
  const formatRet = (v) => {
    if (v == null || isNaN(v)) return { text: '—', color: 'text-dark-500', num: null };
    return { text: `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`, color: v >= 0 ? 'text-green-400' : 'text-red-400', num: v };
  };

  const allYearsUnfiltered = [...new Set(Object.values(compareData).flatMap(d => (d?.yearly_returns || []).map(s => s.year)))].sort((a, b) => b - a);
  const allYears = allYearsUnfiltered.filter(y => {
    if (yearFrom && y < parseInt(yearFrom)) return false;
    if (yearTo && y > parseInt(yearTo)) return false;
    return true;
  });

  // Diversified returns (weighted by allocation)
  // A year where a selected index has no data (crypto before 2017, India
  // before 2006) spreads that index's share over the ones that do — `held`
  // is the split the year actually ran with, shown under the year.
  const getDiversified = (year) => {
    let idxW = 0, strW = 0, timW = 0, totalW = 0;
    const present = [];
    symbolsList.forEach(sym => {
      const w = (allocations[sym] || 0) / 100;
      const yd = getYearData(sym, year);
      if (yd && yd.strategy_return != null) {
        idxW += (yd.index_return ?? 0) * w;
        strW += yd.strategy_return * w;
        timW += (yd.time_in_market_pct ?? 0) * w;
        totalW += w;
        if (w > 0) present.push([sym, w]);
      }
    });
    if (!(totalW > 0)) return null;
    return {
      index: idxW / totalW, strategy: strW / totalW, timeInMarket: timW / totalW,
      held: present.map(([sym, w]) => ({ sym, weight: (w / totalW) * 100 })),
    };
  };

  // Rolling any-entry periods over the displayed years. Values COMPOUND
  // (product of 1+r), never sum — the same math as the allocation lab.
  const displayPeriods = (() => {
    const asc = [...allYears].sort((x, y) => x - y);
    if (cmpHorizon === 1) return [...asc].reverse().map(y => ({ label: String(y), years: [y], endYear: y }));
    const out = [];
    for (let i = 0; i + cmpHorizon <= asc.length; i++) {
      const span = asc.slice(i, i + cmpHorizon);
      if (span[cmpHorizon - 1] - span[0] !== cmpHorizon - 1) continue;
      out.push({ label: `${span[0]}–${String(span[cmpHorizon - 1]).slice(2)}`, years: span, endYear: span[cmpHorizon - 1] });
    }
    return out.reverse();
  })();
  const barFor = (p) => (minStrat == null ? null : (Math.pow(1 + minStrat / 100, p.years.length) - 1) * 100);
  const isUnder = (p) => {
    const bar = barFor(p); const d = getDiversifiedP(p);
    return bar != null && d != null && d.strategy < bar - 1e-9;
  };
  const compOver = (yrs, getVal) => {
    let m = 1;
    for (const y of yrs) { const v = getVal(y); if (v == null) return null; m *= 1 + v / 100; }
    return (m - 1) * 100;
  };
  const periodSR = (sym, p) => compOver(p.years, y => getYearData(sym, y)?.strategy_return);
  const periodIR = (sym, p) => compOver(p.years, y => getYearData(sym, y)?.index_return);
  const getDiversifiedP = (p) => {
    const strategy = compOver(p.years, y => getDiversified(y)?.strategy);
    if (strategy == null) return null;
    const index = compOver(p.years, y => getDiversified(y)?.index);
    const tims = p.years.map(y => getDiversified(y)?.timeInMarket).filter(v => v != null);
    return { strategy, index: index ?? 0, timeInMarket: tims.length ? tims.reduce((x, y) => x + y, 0) / tims.length : 0 };
  };

  // Overall averages
  const overallDiv = (() => {
    let tIdx = 0, tStr = 0, tTim = 0, n = 0;
    allYears.forEach(y => { const d = getDiversified(y); if (d) { tIdx += d.index; tStr += d.strategy; tTim += d.timeInMarket; n++; } });
    return n > 0 ? { avgIndex: tIdx / n, avgStrategy: tStr / n, avgTimeInMarket: tTim / n, years: n } : null;
  })();

  // Worst year & profitable count
  let posYears = 0, negYears = 0, worstStr = Infinity, worstStrYr = null, worstIdx = Infinity, worstIdxYr = null;
  allYears.forEach(y => {
    const d = getDiversified(y);
    if (d) {
      if (d.strategy >= 0) posYears++; else negYears++;
      if (d.strategy < worstStr) { worstStr = d.strategy; worstStrYr = y; }
      if (d.index < worstIdx) { worstIdx = d.index; worstIdxYr = y; }
    }
  });

  const totalAlloc = Math.round(Object.values(allocations).reduce((s, v) => s + v, 0) * 100) / 100;
  const allocOk = Math.abs(totalAlloc - 100) < 0.01;

  // Outperformance marker — binary only: did the strategy beat the index this year?
  // Rendered as a corner dot so it costs zero column width however many indexes are compared.
  // Green = beat the index · grey = within 5 points behind (a close call — under
  // Net, costs mostly turn near-ties red) · red = clearly behind (Apal 2026-10-18)
  const CLOSE_CALL_PTS = 5;
  // Amount column only when an amount is set; the frozen % In Mkt slides left into its place
  const showAmount = investmentAmt > 0;
  // Frozen columns get EXACT widths and offsets computed from them, so they
  // line up in every view — 1Y/2Y/3Y (wider year labels), with or without
  // Amount (Apal 2026-10-19: "while horizontal scrolling diversified
  // portfolio column looks a bit weird" — offsets assumed the 1Y year width).
  const COLW = { year: cmpHorizon === 1 ? 72 : 104, idx: 84, strat: 96, amt: 108, mkt: 80, sym: 120 };
  const idxLeft = COLW.year;
  const stratLeft = idxLeft + COLW.idx;
  const amtLeft = stratLeft + COLW.strat;
  const inMktLeft = showAmount ? amtLeft + COLW.amt : amtLeft;
  const frozenW = inMktLeft + COLW.mkt;
  const beatMark = (strategyRet, indexRet) => {
    if (strategyRet == null || indexRet == null) return null;
    if (strategyRet > indexRet) return <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-green-400" title="Beat the index this year" />;
    return indexRet - strategyRet < CLOSE_CALL_PTS
      ? <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-dark-400" title={`Close call: within ${CLOSE_CALL_PTS} points of the index`} />
      : <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-red-500" title="Did not beat the index this year" />;
  };
  const beatChip = (beats, total) => {
    if (!total) return null;
    const good = beats / total >= 0.5;
    return (
      <span className={`ml-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full normal-case whitespace-nowrap inline-block ${good ? 'bg-green-500/15 text-green-300' : 'bg-amber-500/15 text-amber-300'}`}>
        beat {beats}/{total} yrs
      </span>
    );
  };
  const symBeats = (sym) => {
    let beats = 0, total = 0;
    displayPeriods.forEach(p => {
      const sr = periodSR(sym, p), ir = periodIR(sym, p);
      if (sr != null && ir != null) { total++; if (sr > ir) beats++; }
    });
    return { beats, total };
  };
  const divBeats = (() => {
    let beats = 0, total = 0;
    displayPeriods.forEach(p => {
      const d = getDiversifiedP(p);
      if (d) { total++; if (d.strategy > d.index) beats++; }
    });
    return { beats, total };
  })();

  return (
    <div className="space-y-5">
      {/* Selector — collapsible */}
      <div>
        <SelectorToggle open={selectorOpen} onToggle={() => setSelectorOpen(!selectorOpen)} count={selected.length} />
        {selectorOpen && (
          <div className="bg-dark-800/50 rounded-lg border border-dark-700">
            <div className="flex">
              {/* Left column — index selection (65%) */}
              <div className="w-[65%] p-4 border-r border-dark-700">
                <IndexChipRows
                  regions={regionSymbols}
                  loading={regionsLoading}
                  selected={selected}
                  onToggle={toggle}
                  onRegionToggle={toggleRegion}
                  canShort={n => (shortsSyms || []).includes(n)}
                  modeFor={n => (getSymStrategy(n) === 'with_shorts' ? 'ls' : 'l')}
                  onModeToggle={n => setPerSymStrategy(prev => ({ ...prev, [n]: getSymStrategy(n) === 'with_shorts' ? '1x' : 'with_shorts' }))}
                />
                <div className="flex items-center gap-3 mt-3 pt-2 border-t border-dark-700">
                  {selected.length > 0 && <button onClick={() => { setSelected([]); setCompareData({}); }} className="text-xs text-dark-400 hover:text-red-400">Clear all</button>}
                  {regionSymbols.length > 0 && (
                    <button onClick={() => setSelected(regionSymbols.flatMap(r => r.symbols.map(s => s.name)))} className="text-xs text-dark-400 hover:text-teal-400">Select all</button>
                  )}
                  {selected.length === 1 && <span className="text-xs text-dark-400">Select 1 more to compare</span>}
                </div>
              </div>

              {/* Right column — weightage (35%). One line per index with a
                  slider in the index's own colour, and the whole mix as one
                  bar on top (Apal 2026-10-15: "the weightage scrolls look
                  weird"). */}
              <div className="w-[35%] shrink-0 p-4" data-testid="compare-weightage">
                <div className="flex items-center justify-between mb-2.5">
                  <span className={`${FILTER_LABEL} !mb-0`}>
                    Weightage
                    <span className={`ml-1.5 tabular-nums ${allocOk ? 'text-emerald-400' : 'text-red-400'}`}>{fmtWeight(totalAlloc)}%</span>
                  </span>
                  {selected.length >= 2 && (
                    <button onClick={() => setAllocations(equalAllocations(selected))}
                      className="text-[11px] font-semibold text-primary-400 hover:text-primary-300">Equal weight</button>
                  )}
                </div>
                {selected.length === 0 ? (
                  <p className="text-xs text-dark-500">Select indexes to set allocation</p>
                ) : (
                  <>
                    <MixBar items={selected.map(sym => ({ key: sym, label: labelMap[sym] || sym, weight: allocations[sym] || 0, color: sleeveColor(sym) }))} />
                    <div className="mt-2.5">
                      {selected.map((sym, idx) => (
                        <div key={sym}
                          draggable={dragArmed === idx}
                          onDragStart={e => { e.dataTransfer.setData('text/plain', idx); e.currentTarget.style.opacity = '0.4'; }}
                          onDragEnd={e => { e.currentTarget.style.opacity = '1'; setDragArmed(null); }}
                          onDragOver={e => { e.preventDefault(); e.currentTarget.style.boxShadow = 'inset 0 2px 0 #14b8a6'; }}
                          onDragLeave={e => { e.currentTarget.style.boxShadow = ''; }}
                          onDrop={e => {
                            e.preventDefault();
                            e.currentTarget.style.boxShadow = '';
                            const fromIdx = parseInt(e.dataTransfer.getData('text/plain'));
                            if (fromIdx !== idx) {
                              setSelected(prev => {
                                const n = [...prev];
                                const [moved] = n.splice(fromIdx, 1);
                                n.splice(idx, 0, moved);
                                return n;
                              });
                            }
                          }}
                        >
                          <WeightSlider label={labelMap[sym] || sym} color={sleeveColor(sym)} testid={`weight-${sym}`}
                            title={investmentAmt > 0 ? `${labelMap[sym] || sym}: ${fmtWeight(allocations[sym] || 0)}% of ₹${investmentAmt} Cr = ${formatCr(investmentAmt * (allocations[sym] || 0) / 100)}` : undefined}
                            value={allocations[sym] || 0} display={`${fmtWeight(allocations[sym] || 0)}%`}
                            onChange={v => setAllocations(prev => ({ ...prev, [sym]: v }))}
                            handle={(
                              <span className="text-dark-600 hover:text-dark-300 text-xs shrink-0 cursor-grab active:cursor-grabbing select-none -ml-1"
                                title="Drag to reorder"
                                onMouseDown={() => setDragArmed(idx)}
                                onMouseUp={() => setDragArmed(null)}>⠿</span>
                            )} />
                        </div>
                      ))}
                    </div>
                    {!allocOk && <p className="text-[11px] text-red-400 mt-1.5">Total must equal 100%</p>}
                  </>
                )}
                {/* The amount the weights split — Portfolio value and the
                    Amount column grow from it */}
                <div className="mt-3 pt-3 border-t border-dark-700 flex items-center justify-between gap-3" data-testid="compare-amount">
                  <label htmlFor="compare-amount-input" className={`${FILTER_LABEL} !mb-0`}>Amount</label>
                  <div className="h-9 flex items-center bg-dark-700 border border-dark-600 rounded-lg focus-within:ring-1 focus-within:ring-primary-500 hover:border-dark-500">
                    <span className="pl-3 text-dark-400 text-sm">₹</span>
                    <input id="compare-amount-input" type="number" min={0.01} max={10000} step={0.5} value={investmentAmt || ''} placeholder="—"
                      onChange={e => setInvestmentAmt(parseFloat(e.target.value) || 0)}
                      className="w-16 bg-transparent px-1.5 text-white text-sm font-semibold tabular-nums focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                    <span className="pr-3 text-dark-400 text-sm">Cr</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Page filters — Horizon · Period · Highlight join Model · Costs ·
          Execution in the ONE top bar (Apal 2026-10-15: "I want one line
          filters at top then other relevant things in their sections").
          Amount lives with Weightage; the Highlight count sits on the table. */}
      {(() => {
        const filters = (
          <>
            <div data-testid="compare-horizon">
              <SegmentControl label="Horizon" value={cmpHorizon} onChange={setCmpHorizon}
                options={HORIZON_OPTIONS.map(op => ({ key: op.key, label: op.label,
                  title: op.key === 1 ? 'One row per calendar year' : `Every possible ${op.key}-year entry point — each row compounds what that hold delivered` }))} />
            </div>
            <PeriodControl id="compare-period-label" testid="compare-period" mode={periodMode}
              presets={[{ key: 'all', label: 'All history' }]} onPreset={pickAllYears}
              years={allYearsUnfiltered} pickedYear={yearFrom} onYear={pickOneYear}
              yearTestid="compare-year" yearLabelTestid="compare-year-label"
              onCustom={() => pickCustomYears(allYearsUnfiltered)}
              customYears={{ from: yearFrom, to: yearTo, onFrom: setYearFrom, onTo: setYearTo,
                years: [...allYearsUnfiltered].reverse(), fromTestid: 'compare-year-from', toTestid: 'compare-year-to' }} />
            <ThresholdSelect id="compare-min-strategy-select" label="Highlight" testid="compare-min-strategy" selectTestid="compare-min-strategy-select"
              value={minStrat == null ? 'off' : String(minStrat)} active={minStrat != null}
              onChange={v => setMinStrat(v === 'off' ? null : Number(v))}
              title={cmpHorizon > 1 ? `Highlight periods where the Diversified strategy made under this a year (compounded over ${cmpHorizon} years)` : 'Highlight years where the Diversified strategy made under this'}
              options={[{ value: 'off', label: 'Off' }, ...[0, 5, 10, 15, 20].map(v => ({ value: String(v), label: v === 0 ? 'Losing years' : `Under ${v}%` }))]} />
          </>
        );
        if (!selected.length) return null;
        return filterSlot ? createPortal(filters, filterSlot) : <div className={FILTER_ROW} data-testid="compare-page-filters">{filters}</div>;
      })()}

      {(() => {
        const hasData = Object.keys(compareData).length > 0;
        const waiting = selected.length >= 2 && !hasData && !compareTried;
        return (
          <>
            <ProcessingStatus active={loading || waiting} title={hasData ? undefined : 'Loading results'} />
            {waiting && (
              <div className="space-y-5" data-testid="compare-skeleton">
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                  {['Avg Index Return', 'Avg Strategy Return', 'Avg Time in Market', 'Profitable Years', 'Worst Year', 'Portfolio Value'].map(l => (
                    <div key={l} className="bg-dark-800/80 rounded-lg p-4 border border-dark-700">
                      <div className="text-xs text-dark-400 uppercase mb-1">{l}</div>
                      <div className="h-8 flex items-center"><Skel className="h-6 w-20" /></div>
                      <div className="h-4 flex items-center"><Skel className="h-2.5 w-16" /></div>
                    </div>
                  ))}
                </div>
                {/* the comparison table's own two-level head: Diversified, then each index */}
                <div className="overflow-x-auto rounded-lg border border-dark-700" data-testid="compare-table-skeleton">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs uppercase tracking-wider">
                        <th rowSpan={2} className="text-left py-3 px-3 text-dark-400 font-medium border-r border-dark-700">Year</th>
                        <th colSpan={4} className="py-3 px-3 text-white font-semibold bg-cyan-950/30 border-r border-dark-700">Diversified (equal weight)</th>
                        {selected.map(sym => <th key={sym} colSpan={2} className="py-3 px-3 text-white font-semibold normal-case text-sm border-r border-dark-700">{labelMap[sym] || sym}</th>)}
                      </tr>
                      <tr className="text-[11px] uppercase tracking-wider text-dark-400">
                        {['Index', 'Strategy', 'Amount', '% In Mkt'].map(c => <th key={c} className="py-2 px-3 font-medium bg-cyan-950/30">{c}</th>)}
                        {selected.flatMap(sym => ['Index', 'Strategy'].map(c => <th key={`${sym}-${c}`} className="py-2 px-3 font-medium">{c}</th>))}
                      </tr>
                    </thead>
                    <tbody><SkelRows cols={5 + selected.length * 2} rows={10} cellClass="py-3 px-3" /></tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* Summary cards — matches MV layout */}
      {!loading && overallDiv && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div className="bg-dark-800/80 rounded-lg p-4 border border-dark-700">
            <div className="text-xs text-dark-400 uppercase mb-1">Avg Index Return</div>
            <div className={`text-2xl font-bold ${overallDiv.avgIndex >= 0 ? 'text-green-400' : 'text-red-400'}`}>{overallDiv.avgIndex >= 0 ? '+' : ''}{overallDiv.avgIndex.toFixed(1)}%</div>
            <div className="text-xs text-dark-500">per year ({overallDiv.years}y)</div>
          </div>
          <div className="bg-cyan-950/40 rounded-lg p-4 border border-cyan-700/50">
            <div className="text-xs text-cyan-400 uppercase mb-1">Avg Strategy Return</div>
            <div className={`text-2xl font-bold ${overallDiv.avgStrategy >= 0 ? 'text-green-400' : 'text-red-400'}`}>{overallDiv.avgStrategy >= 0 ? '+' : ''}{overallDiv.avgStrategy.toFixed(1)}%</div>
            <div className="text-xs text-dark-500">per year ({overallDiv.years}y)</div>
          </div>
          <div className="bg-teal-950/40 rounded-lg p-4 border border-teal-700/50">
            <div className="text-xs text-teal-400 uppercase mb-1">Avg Time in Market</div>
            <div className="text-2xl font-bold text-teal-400">{overallDiv.avgTimeInMarket.toFixed(0)}%</div>
            <div className="text-xs text-dark-500">of year exposed</div>
          </div>
          <div className="bg-dark-800/80 rounded-lg p-4 border border-dark-700">
            <div className="text-xs text-dark-400 uppercase mb-1">Profitable Years</div>
            <div className="flex items-baseline gap-1"><span className="text-2xl font-bold text-green-400">{posYears}</span><span className="text-dark-500">/</span><span className="text-lg font-bold text-red-400">{negYears}</span></div>
            <div className="text-xs text-dark-500">positive / negative</div>
          </div>
          <div className="bg-red-950/30 rounded-lg p-4 border border-red-900/50">
            <div className="text-xs text-red-400 uppercase mb-2">Worst Year</div>
            <div className="flex items-center gap-3">
              <div className="flex-1"><div className="text-xs text-dark-500 mb-0.5">Strategy</div><div className="text-xl font-bold text-red-400">{worstStr !== Infinity ? `${worstStr.toFixed(1)}%` : '—'}</div><div className="text-[10px] text-dark-500">{worstStrYr || ''}</div></div>
              <div className="text-dark-600 text-lg">vs</div>
              <div className="flex-1 text-right"><div className="text-xs text-dark-500 mb-0.5">Index</div><div className="text-xl font-bold text-red-400">{worstIdx !== Infinity ? `${worstIdx.toFixed(1)}%` : '—'}</div><div className="text-[10px] text-dark-500">{worstIdxYr || ''}</div></div>
            </div>
          </div>
          {investmentAmt > 0 && allYears.length > 0 && (() => {
            const sortedAsc = [...allYears].sort((a, b) => a - b);
            let finalVal = investmentAmt;
            sortedAsc.forEach(y => { const d = getDiversified(y); if (d) finalVal *= (1 + d.strategy / 100); });
            const multiple = finalVal / investmentAmt;
            return (
              <div className="bg-emerald-950/40 rounded-lg p-4 border border-emerald-700/50">
                <div className="text-xs text-emerald-400 uppercase mb-1">Portfolio Value</div>
                <div className="text-2xl font-bold text-emerald-300">{formatCr(finalVal)}</div>
                <div className="text-xs text-dark-400 mt-0.5">{formatCr(investmentAmt)} → {formatCr(finalVal)} <span className="text-emerald-400 font-semibold">({multiple.toFixed(0)}x)</span></div>
                <div className="text-[10px] text-dark-500">{sortedAsc[0]} – {sortedAsc[sortedAsc.length - 1]} ({sortedAsc.length}y)</div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Comparison table — matches MV with Diversified column */}
      {!loading && allYears.length > 0 && (
        <div>
        {/* What the Highlight filter (top bar) found, said on the table it marks */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-3 text-sm" data-testid="compare-under-note">
          {minStrat != null && displayPeriods.length > 0 && (() => {
            const n = displayPeriods.filter(isUnder).length;
            const unit = cmpHorizon === 1 ? 'years' : `${cmpHorizon}-year periods`;
            return (
              <>
                <span className={`inline-block w-3 h-3 rounded-sm ${n ? 'bg-amber-400/30 ring-1 ring-amber-400/70' : 'bg-dark-600'}`} />
                <span data-testid="compare-under-count" className={n ? 'text-amber-200' : 'text-dark-400'}>
                  <b className="tabular-nums">{n}</b> of {displayPeriods.length} {unit} {minStrat === 0 ? 'lost money' : `under ${minStrat}%${cmpHorizon > 1 ? ' a year' : ''}`}
                </span>
                <span className="text-dark-500">— Diversified strategy{n ? ', highlighted below' : ''}</span>
              </>
            );
          })()}
        </div>
        <div className="bg-dark-800 rounded-xl border border-dark-700 overflow-hidden">
          {/* Frozen panes (Apal 2026-10-15: 30 years down, many indexes across
              — "looking at indexes when I scroll down"): the table scrolls in
              its own viewport-high box, so the index header row stays pinned
              on top while Year + Diversified stay pinned on the left. Every
              cell keeps its year and its index in view. Header fills are
              opaque so rows never show through. */}
          <div ref={tableBoxRef} className="overflow-auto max-h-[calc(100vh-96px)]" data-testid="compare-table-scroll">
            {/* fixed layout: the colgroup decides every width; index columns share what's left */}
            <table className="cb-frozen text-sm" style={{ tableLayout: 'fixed', width: Math.max(tableBoxW || 0, frozenW + symbolsList.length * 2 * COLW.sym) }}>
              <colgroup>
                <col style={{ width: COLW.year }} />
                <col style={{ width: COLW.idx }} />
                <col style={{ width: COLW.strat }} />
                {showAmount && <col style={{ width: COLW.amt }} />}
                <col style={{ width: COLW.mkt }} />
                {symbolsList.map(sym => <React.Fragment key={sym}><col /><col /></React.Fragment>)}
              </colgroup>
              <thead className="sticky top-0 z-30" data-testid="compare-table-head">
                <tr className="border-b border-dark-600 bg-[#17202f]">
                  <th className="py-4 px-4 text-left text-dark-400 font-medium text-xs uppercase sticky left-0 z-20 bg-dark-900">Year</th>
                  <th className="py-4 px-3 text-center text-white font-semibold text-xs uppercase border-l-2 border-r-2 border-cyan-600 bg-[#0a2e42] sticky z-20" style={{ left: idxLeft }} colSpan={showAmount ? 4 : 3}>
                    Diversified ({Object.values(allocations).every((v, _, a) => v === a[0]) ? 'Equal' : 'Custom'} Weight)
                    {beatChip(divBeats.beats, divBeats.total)}
                  </th>
                  {symbolsList.map(sym => {
                    const hasShorts = strategy === 'with_shorts' && compareData[sym]?.strategy === 'with_shorts';
                    return (
                      <th key={sym} className="py-4 px-4 text-center text-white font-semibold text-sm border-l border-dark-600" colSpan={2}>
                        {labelMap[sym] || sym}
                        {strategy === 'with_shorts' && (
                          hasShorts
                            ? <span className="ml-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-orange-600/30 text-orange-400 uppercase">+S</span>
                            : <span className="ml-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-dark-600 text-dark-400 uppercase">L</span>
                        )}
                        {(() => { const b = symBeats(sym); return beatChip(b.beats, b.total); })()}
                      </th>
                    );
                  })}
                </tr>
                <tr className="border-b border-dark-700 bg-[#1a2436] shadow-[0_1px_0_#334155]">
                  <th className="py-2 px-4 sticky left-0 z-20 bg-dark-900"></th>
                  <th className="py-2 px-3 text-center text-cyan-300 font-medium text-xs uppercase border-l-2 border-cyan-600 bg-[#0a2e42] sticky z-20" style={{ left: idxLeft }}>Index</th>
                  <th className="py-2 px-3 text-center text-cyan-300 font-medium text-xs uppercase bg-[#0a2e42] sticky z-20" style={{ left: stratLeft }}>Strategy</th>
                  {showAmount && <th className="py-2 px-3 text-center text-cyan-300 font-medium text-xs uppercase bg-[#0a2e42] sticky z-20" style={{ left: amtLeft }}>Amount</th>}
                  <th className="py-2 px-3 text-center text-teal-300 font-medium text-xs uppercase border-r-2 border-cyan-600 bg-[#07322f] sticky z-20" style={{ left: inMktLeft }}>% In Mkt</th>
                  {symbolsList.map(sym => (
                    <React.Fragment key={sym}>
                      <th className="py-2 px-4 text-center text-dark-400 font-medium text-xs uppercase border-l border-dark-700">Index</th>
                      <th className="py-2 px-4 text-center text-dark-400 font-medium text-xs uppercase">Strategy</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Compute cumulative portfolio value from oldest → newest filtered year
                  const sortedYearsAsc = [...allYears].sort((a, b) => a - b);
                  const cumValues = {};
                  let runningValue = investmentAmt;
                  sortedYearsAsc.forEach(year => {
                    const div = getDiversified(year);
                    if (div && div.strategy != null) {
                      runningValue = runningValue * (1 + div.strategy / 100);
                    }
                    cumValues[year] = runningValue;
                  });
                  // Allocation changes over the years — crypto from 2017, India
                  // from 2006 (Apal 2026-10-11: "you start from year 2016 showing
                  // allocation row changed, then … from 2005"). A divider marks
                  // each year the split changes, with the split it runs on.
                  const allocSig = (y) => (getDiversified(y)?.held || []).map(h => `${h.sym}:${fmtWeight(h.weight)}`).join('|');
                  const totalCols = (showAmount ? 5 : 4) + 2 * symbolsList.length;
                  const allocDivider = (idx) => {
                    if (cmpHorizon !== 1) return null;
                    const y = displayPeriods[idx].endYear;
                    const sig = allocSig(y);
                    if (idx > 0 && sig === allocSig(displayPeriods[idx - 1].endYear)) return null;
                    let j = idx;
                    while (j + 1 < displayPeriods.length && allocSig(displayPeriods[j + 1].endYear) === sig) j++;
                    const oldest = displayPeriods[j].endYear;
                    const held = getDiversified(y)?.held || [];
                    const zero = symbolsList.filter(sym => !held.some(h => h.sym === sym));
                    return (
                      <tr key={`alloc-${y}`} data-testid={`compare-alloc-${y}`} className="cb-divider bg-[#082636]">
                        <td colSpan={totalCols} className="p-0">
                          {/* as wide as the visible box, pinned left — it never scrolls out sideways */}
                          <div className="sticky left-0 flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3" style={{ maxWidth: tableBoxW || '100vw' }}>
                            <div className="shrink-0">
                              <div className="text-[10px] font-bold uppercase tracking-wider text-cyan-300">Invested {oldest === y ? `in ${y}` : `${oldest}–${y}`}</div>
                              <div className="text-[11px] text-cyan-100/70">{held.length} of {symbolsList.length} indexes</div>
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5" data-testid={`compare-alloc-held-${y}`}>
                              {held.map(h => (
                                <span key={h.sym} className="inline-flex items-baseline gap-1.5 rounded-md border border-cyan-400/40 bg-cyan-500/15 px-2 py-1">
                                  <span className="text-sm font-semibold text-white">{labelMap[h.sym] || h.sym}</span>
                                  <span className="text-sm font-bold tabular-nums text-cyan-200">{fmtWeight(h.weight)}%</span>
                                </span>
                              ))}
                            </div>
                            {zero.length > 0 && (
                              <div className="text-xs text-dark-400" data-testid={`compare-alloc-zero-${y}`}>
                                <span className="font-semibold text-dark-300">0%</span> — no reports yet: {zero.map(sym => labelMap[sym] || sym).join(' · ')}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  };
                  return displayPeriods.map((p, idx) => {
                  const year = p.endYear;
                  const div = getDiversifiedP(p);
                  const rowBg = idx % 2 === 0 ? 'bg-dark-800/50' : 'bg-dark-900/30';
                  const moneyVal = cumValues[year];
                  const under = isUnder(p);
                  return (
                    <React.Fragment key={p.label}>
                    {allocDivider(idx)}
                    {/* An under-target year lights the WHOLE row (Apal 2026-10-15:
                        "we should show whole row properly highlighted"): an
                        amber wash on every cell — the sticky Diversified cells
                        get opaque amber-mixed fills of their own colours so the
                        wash doesn't stop at them — plus an amber edge and rules. */}
                    <tr data-under={under ? 'true' : undefined}
                      className={under ? 'bg-amber-400/[0.12] border-y border-amber-400/50' : `border-b border-dark-700/50 ${rowBg}`}>
                      <td className={`py-4 px-4 font-semibold sticky left-0 z-10 whitespace-nowrap ${under ? 'bg-[#3a3424] text-amber-200 shadow-[inset_4px_0_0_#fbbf24]' : 'bg-dark-900 text-white'}`}
                        title={under ? `Diversified strategy under ${minStrat}%${cmpHorizon > 1 ? ' a year' : ''} — ${formatRet(getDiversifiedP(p)?.strategy).text}${cmpHorizon > 1 ? ` vs ${barFor(p).toFixed(1)}% over ${cmpHorizon} years` : ''}` : undefined}>
                        {p.label}
                        {(() => {
                          const yrs = p.years.map(y => ({ y, d: getDiversified(y) })).filter(x => x.d);
                          const counts = yrs.map(x => x.d.held.length);
                          if (cmpHorizon === 1 || !counts.length || Math.min(...counts) === symbolsList.length) return null; // 1Y: the dividers say it
                          const lo = Math.min(...counts), hi = Math.max(...counts);
                          const title = yrs.map(({ y, d }) => `${y}: ${d.held.map(h => `${labelMap[h.sym] || h.sym} ${fmtWeight(h.weight)}%`).join(' · ')}`).join('\n');
                          return (
                            <div data-testid={`compare-held-${p.label}`} title={`Indexes with data, and the share each held:\n${title}`}
                              className="text-[10px] font-medium text-amber-200/80 cursor-help">
                              {lo === hi ? lo : `${lo}–${hi}`} of {symbolsList.length}
                            </div>
                          );
                        })()}
                      </td>
                      <td className={`py-4 px-3 text-center border-l-2 border-cyan-600 ${under ? 'bg-[#2f4038]' : 'bg-[#0a2e42]'} sticky z-10 ${div ? (div.index >= 0 ? 'text-green-400' : 'text-red-400') : 'text-dark-500'}`} style={{ left: idxLeft }}>
                        {div ? formatRet(div.index).text : '—'}
                      </td>
                      <td className={`relative py-4 px-3 text-center ${under ? 'bg-[#2f4038]' : 'bg-[#0a2e42]'} sticky z-10 font-bold text-base ${div ? (div.strategy >= 0 ? 'text-green-400' : 'text-red-400') : 'text-dark-500'}`} style={{ left: stratLeft }}>
                        {div ? formatRet(div.strategy).text : '—'}
                        {div && beatMark(div.strategy, div.index)}
                      </td>
                      {showAmount && (
                        <td className={`py-4 px-4 text-center whitespace-nowrap ${under ? 'bg-[#2f4038]' : 'bg-[#0a2e42]'} sticky z-10 font-bold text-sm ${moneyVal >= investmentAmt ? 'text-emerald-300' : 'text-red-300'}`} style={{ left: amtLeft }}>
                          {formatCr(moneyVal)}
                        </td>
                      )}
                      <td className={`py-4 px-3 text-center border-r-2 border-cyan-600 ${under ? 'bg-[#2c4430]' : 'bg-[#07322f]'} sticky z-10 text-teal-400 font-medium`} style={{ left: inMktLeft }}>
                        {div ? `${div.timeInMarket.toFixed(0)}%` : '—'}
                      </td>
                      {(() => {
                        const returns = symbolsList.map(sym => periodSR(sym, p)).filter(r => r != null);
                        const bestReturn = returns.length > 1 ? Math.max(...returns) : null;
                        return symbolsList.map(sym => {
                          const ir = formatRet(periodIR(sym, p));
                          const sr = formatRet(periodSR(sym, p));
                          const cellBg = ir.num != null && ir.num < 0 ? 'bg-red-950/40' : '';
                          const isBest = bestReturn != null && sr.num === bestReturn;
                          return (
                            <React.Fragment key={sym}>
                              <td className={`py-4 px-4 text-center border-l border-dark-700/50 ${ir.color} ${cellBg}`}>{ir.text}</td>
                              {/* best of the year: a light green box (gold clashed with the amber
                                  under-target rows) with the classic gold ★ (Apal 2026-10-18) */}
                              <td className={`relative py-4 px-4 text-center font-semibold ${sr.color} ${cellBg} ${isBest ? 'bg-emerald-500/10 ring-1 ring-inset ring-emerald-400/40 rounded' : ''}`}>
                                {sr.text}
                                {beatMark(sr.num, ir.num)}
                                {isBest && <span className="ml-1 text-yellow-400 text-xs" title="Best strategy return this year">★</span>}
                              </td>
                            </React.Fragment>
                          );
                        });
                      })()}
                    </tr>
                    </React.Fragment>
                  );
                });
                })()}
              </tbody>
              {/* Summary footer — diversification insight */}
              {symbolsList.length > 1 && (
                <tfoot>
                  <tr className="cb-foot">
                    <td className="py-4 px-4 text-dark-400 text-xs font-semibold uppercase tracking-wider">★ Years as #1</td>
                    <td colSpan={showAmount ? 4 : 3} className="py-4 px-3 text-center border-l-2 border-r-2 border-cyan-600"></td>
                    {(() => {
                      // Count how many years each symbol was the top performer
                      const winCounts = {};
                      symbolsList.forEach(s => { winCounts[s] = 0; });
                      displayPeriods.forEach(p => {
                        let bestSym = null, bestRet = -Infinity;
                        symbolsList.forEach(sym => {
                          const ret = periodSR(sym, p);
                          if (ret != null && ret > bestRet) { bestRet = ret; bestSym = sym; }
                        });
                        if (bestSym) winCounts[bestSym]++;
                      });
                      const totalYears = allYears.length;
                      const maxWins = Math.max(...Object.values(winCounts));
                      return symbolsList.map(sym => {
                        const wins = winCounts[sym];
                        const pct = totalYears > 0 ? Math.round(wins / totalYears * 100) : 0;
                        const isTop = wins === maxWins && symbolsList.length > 1;
                        return (
                          <React.Fragment key={sym}>
                            <td className="py-4 px-4 text-center border-l border-dark-700/50"></td>
                            <td className={`py-4 px-4 text-center font-bold ${isTop ? 'text-emerald-300' : 'text-dark-300'}`}>
                              <div className="text-lg">{wins}<span className="text-xs text-dark-500 font-normal">/{totalYears}</span></div>
                              <div className="text-[10px] text-dark-500 mt-0.5">{pct}% of years</div>
                            </td>
                          </React.Fragment>
                        );
                      });
                    })()}
                  </tr>
                  {/* Diversification message */}
                  <tr>
                    <td colSpan={5 + symbolsList.length * 2} className="py-3 px-4">
                      {(() => {
                        const winCounts = {};
                        symbolsList.forEach(s => { winCounts[s] = 0; });
                        allYears.forEach(year => {
                          let bestSym = null, bestRet = -Infinity;
                          symbolsList.forEach(sym => {
                            const ret = getYearData(sym, year)?.strategy_return;
                            if (ret != null && ret > bestRet) { bestRet = ret; bestSym = sym; }
                          });
                          if (bestSym) winCounts[bestSym]++;
                        });
                        const maxWins = Math.max(...Object.values(winCounts));
                        const maxPct = allYears.length > 0 ? maxWins / allYears.length * 100 : 0;
                        const winnersCount = Object.values(winCounts).filter(w => w > 0).length;
                        if (winnersCount >= symbolsList.length * 0.7 && maxPct < 50) {
                          return <div className="text-xs text-amber-400/80 text-center">No single index dominates — leadership rotates across years, supporting diversification.</div>;
                        }
                        return null;
                      })()}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
        </div>
      )}

      {!loading && allYears.length === 0 && Object.keys(compareData).length === 0 && selected.length < 2 && (
        <div className="text-center py-12 text-dark-400">Select at least 2 indexes, set allocation, and click Compare.</div>
      )}
      {!loading && compareTried && selected.length >= 2 && Object.keys(compareData).length === 0 && (
        <div className="text-center py-12 text-dark-400" data-testid="compare-failed">The comparison could not be loaded · Reload to try again.</div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// All Indexes Tab Component
// ═══════════════════════════════════════════════════════════════
const NON_NEGOTIABLE_RULES = [
  'CAGR < 1.5x Index', '% In Mkt > 2/3 yr', 'Exp. Ratio < 1.0',
  'Win Rate < 50%', 'Avg Return ≤ 4.5%', 'Gain/Loss ≤ 2.75x',
  'Worst Loss ≥ -10%', 'MAE > 3x',
];

const SYMBOL_LABELS = {
  SENSEX: 'Sensex', NIFTY50: 'Nifty 50', NIFTYBANK: 'Nifty Bank', NIFTYIT: 'Nifty IT',
  NIFTYMIDCAP100: 'Nifty Midcap 100', NIFTYSMALLCAP100: 'Nifty Smallcap 100',
  IXIC: 'Nasdaq', SNP: 'S&P 500', DJI: 'Dow Jones', RUT: 'Russell 2000',
  HSI: 'Hang Seng', N225: 'Nikkei 225', DAX: 'DAX', CAC40: 'CAC 40', FTSE: 'FTSE 100', IMOEX: 'MOEX',
  BTC: 'Bitcoin', ETH: 'Ethereum',
};

const ALL_SYMBOLS = Object.keys(SYMBOL_LABELS);
const DEFAULT_SYMBOL_COSTS = { impact: 0, carry_pct: 0, interest_rate: 0, margin_pct: 20 };
const SYMBOL_REGIONS = {
  INDIA: ['SENSEX', 'NIFTY50', 'NIFTYBANK', 'NIFTYIT', 'NIFTYMIDCAP100', 'NIFTYSMALLCAP100'],
  US: ['IXIC', 'SNP', 'DJI', 'RUT'],
  ASIA: ['HSI', 'N225'],
  EUROPE: ['DAX', 'CAC40', 'FTSE', 'IMOEX'],
  CRYPTO: ['BTC', 'ETH'],
};

const INDIA_DEFAULT = { impact: 0.30, carry_pct: 1.0, interest_rate: 18, margin_pct: 20 };
const INTL_DEFAULT = { impact: 0.10, carry_pct: 0.5, interest_rate: 18, margin_pct: 10 };
const CRYPTO_DEFAULT = { impact: 0.15, carry_pct: 0.5, interest_rate: 18, margin_pct: 15 };

const DEFAULT_COST_CONFIG = {
  // India
  SENSEX: { ...INDIA_DEFAULT }, NIFTY50: { ...INDIA_DEFAULT }, NIFTYBANK: { ...INDIA_DEFAULT, impact: 0.40 },
  NIFTYIT: { ...INDIA_DEFAULT }, NIFTYMIDCAP100: { ...INDIA_DEFAULT }, NIFTYSMALLCAP100: { ...INDIA_DEFAULT },
  // US
  IXIC: { ...INTL_DEFAULT }, SNP: { ...INTL_DEFAULT }, DJI: { ...INTL_DEFAULT }, RUT: { ...INTL_DEFAULT },
  // Asia
  HSI: { ...INTL_DEFAULT }, N225: { ...INTL_DEFAULT },
  // Europe
  DAX: { ...INTL_DEFAULT }, CAC40: { ...INTL_DEFAULT }, FTSE: { ...INTL_DEFAULT }, IMOEX: { ...INTL_DEFAULT },
  // Crypto
  BTC: { ...CRYPTO_DEFAULT }, ETH: { ...CRYPTO_DEFAULT },
};

const getSymbolCost = (costConfig, sym) => ({ ...DEFAULT_SYMBOL_COSTS, ...(costConfig[sym] || {}) });
const hasAnyCost = (c) => c.impact > 0 || c.carry_pct > 0 || c.interest_rate > 0;

const computeTradeCost = (cfg, days) => {
  const impact = cfg.impact || 0;
  const carry = (days / 30) * (cfg.carry_pct || 0);
  const interest = ((days * (cfg.interest_rate || 0) / 100) / 360) * ((cfg.margin_pct || 0) / 100) * 100;
  return { impact, carry, interest, total: impact + carry + interest };
};

const SliderRow = ({ label, hint, value, onChange, min, max, step, unit = '%' }) => (
  <div className="py-3 border-b border-dark-700/40 last:border-0">
    <div className="flex items-center justify-between mb-2">
      <div><span className="text-sm font-medium text-white">{label}</span><p className="text-[10px] text-dark-500">{hint}</p></div>
      <div className="flex items-center gap-1.5">
        <input type="number" step={step} min={min} max={max} value={value}
          onChange={e => onChange(parseFloat(e.target.value) || 0)}
          className="w-[72px] px-2 py-1.5 text-sm text-right bg-dark-800 border border-dark-600 rounded text-white tabular-nums focus:outline-none focus:border-amber-500" />
        <span className="text-xs text-dark-500 w-3">{unit}</span>
      </div>
    </div>
    <input type="range" min={min} max={max} step={step} value={value}
      onChange={e => onChange(parseFloat(e.target.value))}
      className="w-full h-1.5 rounded-full appearance-none cursor-pointer bg-dark-600 accent-amber-500" />
  </div>
);

const CostSettingsModal = ({ costConfig, setCostConfig, onClose, allowedSymbols = [] }) => {
  const [draft, setDraft] = useState(JSON.parse(JSON.stringify(costConfig)));
  const [expandedSymbol, setExpandedSymbol] = useState(null);
  const [addingNew, setAddingNew] = useState(false);

  // Only show symbols user has permission for + are active
  const visibleSymbols = allowedSymbols.length > 0
    ? ALL_SYMBOLS.filter(s => allowedSymbols.includes(s))
    : ALL_SYMBOLS;

  const updateField = (sym, field, value) => {
    setDraft(d => ({ ...d, [sym]: { ...getSymbolCost(d, sym), [field]: value } }));
  };

  const handleSave = () => { setCostConfig(draft); localStorage.setItem('reports.costConfig', JSON.stringify(draft)); onClose(); };
  const handleReset = () => { setDraft(JSON.parse(JSON.stringify(DEFAULT_COST_CONFIG))); setExpandedSymbol(null); };

  const configuredSymbols = visibleSymbols.filter(s => hasAnyCost(getSymbolCost(draft, s)));
  const unconfiguredSymbols = visibleSymbols.filter(s => !hasAnyCost(getSymbolCost(draft, s)));

  // Group configured symbols by region
  const getRegion = (sym) => {
    for (const [region, syms] of Object.entries(SYMBOL_REGIONS)) {
      if (syms.includes(sym)) return region;
    }
    return 'OTHER';
  };

  const configuredByRegion = {};
  configuredSymbols.forEach(sym => {
    const r = getRegion(sym);
    (configuredByRegion[r] = configuredByRegion[r] || []).push(sym);
  });

  const addSymbol = (sym) => {
    const defaults = SYMBOL_REGIONS.INDIA.includes(sym) ? INDIA_DEFAULT
      : SYMBOL_REGIONS.CRYPTO.includes(sym) ? CRYPTO_DEFAULT : INTL_DEFAULT;
    setDraft(d => ({ ...d, [sym]: { ...defaults } }));
    setExpandedSymbol(sym);
    setAddingNew(false);
  };

  const removeSymbol = (sym) => {
    setDraft(d => { const n = { ...d }; delete n[sym]; return n; });
    if (expandedSymbol === sym) setExpandedSymbol(null);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-dark-800 rounded-xl border border-dark-600 shadow-2xl w-[540px] max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-dark-700 flex-shrink-0">
          <div><h3 className="text-lg font-bold text-white">Cost Settings</h3><p className="text-xs text-dark-400 mt-0.5">Per-index trading cost configuration</p></div>
          <button onClick={onClose} className="text-dark-400 hover:text-white p-1"><X className="w-5 h-5" /></button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-3">
          {configuredSymbols.length === 0 && <p className="text-dark-500 text-sm py-4 text-center">No indexes configured. Add one below.</p>}

          {Object.entries(SYMBOL_REGIONS).map(([region, _]) => {
            const regionSyms = configuredByRegion[region];
            if (!regionSyms || regionSyms.length === 0) return null;
            return (
              <div key={region} className="mb-2">
                <div className="text-[10px] text-primary-400 uppercase tracking-widest font-bold py-1.5">{region}</div>
                {regionSyms.map(sym => {
            const cfg = getSymbolCost(draft, sym);
            const preview = computeTradeCost(cfg, 100);
            const isExpanded = expandedSymbol === sym;
            return (
              <div key={sym} className="border-b border-dark-700/50 last:border-0">
                <button onClick={() => setExpandedSymbol(isExpanded ? null : sym)}
                  className="w-full flex items-center justify-between py-3 px-1 hover:bg-dark-700/30 transition-colors rounded gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-white font-medium text-sm whitespace-nowrap">{SYMBOL_LABELS[sym] || sym}</span>
                    <span className="text-dark-500 text-[11px] truncate">{cfg.impact}% · {cfg.carry_pct}%/mo · {cfg.interest_rate}%</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-amber-300 text-xs font-semibold tabular-nums whitespace-nowrap">{preview.total.toFixed(2)}%</span>
                    <ChevronDown className={`w-4 h-4 text-dark-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </div>
                </button>
                {isExpanded && (
                  <div className="pb-3 pl-1">
                    <div className="bg-dark-700/30 rounded-lg px-4 border border-dark-600/50">
                      <SliderRow label="Impact Cost" hint="Slippage on entry + exit" value={cfg.impact} onChange={v => updateField(sym, 'impact', v)} min={0} max={2} step={0.05} />
                      <SliderRow label="Carry Cost" hint="Cost of carry per month" value={cfg.carry_pct} onChange={v => updateField(sym, 'carry_pct', v)} min={0} max={2} step={0.1} />
                      <SliderRow label="Interest Rate" hint="Annual margin financing rate" value={cfg.interest_rate} onChange={v => updateField(sym, 'interest_rate', v)} min={0} max={30} step={0.5} />
                      <div className="flex items-center justify-between py-3">
                        <div><span className="text-sm font-medium text-white">Margin</span><p className="text-[10px] text-dark-500">% of notional</p></div>
                        <div className="flex items-center gap-1.5">
                          <input type="number" step={1} min={0} max={100} value={cfg.margin_pct}
                            onChange={e => updateField(sym, 'margin_pct', parseFloat(e.target.value) || 0)}
                            className="w-[72px] px-2 py-1.5 text-sm text-right bg-dark-800 border border-dark-600 rounded text-white tabular-nums focus:outline-none focus:border-amber-500" />
                          <span className="text-xs text-dark-500 w-3">%</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center mt-2 px-1">
                      <div className="text-[10px] text-dark-500">100d example: {preview.impact.toFixed(2)} + {preview.carry.toFixed(2)} + {preview.interest.toFixed(2)} = <span className="text-amber-300 font-semibold">{preview.total.toFixed(2)}%</span></div>
                      <button onClick={() => removeSymbol(sym)} className="text-[10px] text-red-400 hover:text-red-300">Remove</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
              </div>
            );
          })}

          {/* Add new index */}
          {unconfiguredSymbols.length > 0 && (
            <div className="pt-3">
              {addingNew ? (
                <div className="bg-dark-700 border border-amber-500/50 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                  <input autoFocus placeholder="Search indexes..." value={addingNew === true ? '' : addingNew}
                    onChange={e => setAddingNew(e.target.value || true)}
                    onBlur={() => setTimeout(() => setAddingNew(false), 200)}
                    className="w-full px-3 py-2 bg-dark-700 text-white text-sm focus:outline-none sticky top-0 border-b border-dark-600" />
                  {Object.entries(SYMBOL_REGIONS).map(([region, syms]) => {
                    const available = syms.filter(s => unconfiguredSymbols.includes(s) &&
                      (typeof addingNew !== 'string' || (SYMBOL_LABELS[s] || s).toLowerCase().includes(addingNew.toLowerCase()) || s.toLowerCase().includes(addingNew.toLowerCase())));
                    if (!available.length) return null;
                    return (
                      <div key={region}>
                        <div className="px-3 py-1 text-[10px] text-primary-400 uppercase tracking-widest font-bold bg-dark-800">{region}</div>
                        {available.map(sym => (
                          <button key={sym} onMouseDown={() => addSymbol(sym)}
                            className="w-full text-left px-4 py-2 text-sm text-dark-200 hover:bg-dark-600 transition-colors">
                            {SYMBOL_LABELS[sym] || sym}
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <button onClick={() => setAddingNew(true)} className="text-sm text-amber-400 hover:text-amber-300 font-medium">+ Add index</button>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-t border-dark-700 flex-shrink-0">
          <button onClick={handleReset} className="text-sm text-dark-400 hover:text-white underline underline-offset-2">Reset to defaults</button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-dark-300 bg-dark-700 rounded-lg hover:bg-dark-600">Cancel</button>
            <button onClick={handleSave} className="px-4 py-2 text-sm font-semibold text-dark-900 bg-amber-500 rounded-lg hover:bg-amber-400">Save</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// Signal Analysis Component
// ═══════════════════════════════════════════════════════════════
// Completed calendar years, newest first, back to the oldest history. The
// running year is the YTD pill.
const SIGNAL_YEARS = calendarYears(new Date().getFullYear() - 1);
// Signs follow the value: "+1.25%", "−" for losses, and a move that rounds
// to zero reads "0.00%" — never "-0.00%" (docs/UI_GUIDELINES.md §5).
const signedPct2 = (v) => {
  const r = Math.abs(v) < 0.005 ? 0 : v;
  return `${r > 0 ? '+' : ''}${r.toFixed(2)}%`;
};

// An open trade — and, in the Next open / Next close modes, what it does at
// that open when its exit has already been signalled (Apal 2026-10-24: "on
// admin here something is missing for next day open?"). Same words as the app.
const OpenChip = ({ pending }) => (pending
  ? <span data-testid="open-pending-chip" title={`Signalled at the ${formatSingleDate(pending.signal_date)} close; still held until then`}
      className="px-2 py-0.5 rounded text-xs font-semibold bg-amber-900/40 text-amber-300 border border-amber-600/50 whitespace-nowrap">{pending.words}</span>
  : <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-900/40 text-emerald-400 border border-emerald-700/50">OPEN</span>);

// A trade signalled at the last close and not yet made — no price until it is.
const PendingEntryRow = ({ p, colSpan }) => (
  <tr data-testid="pending-entry-row" className="border-t border-dark-700/50 bg-amber-400/[0.05]">
    <td colSpan={colSpan} className="py-3 px-2 text-sm">
      <span className={`px-2 py-0.5 rounded text-[10px] font-bold text-white mr-3 ${p.direction === 'SHORT' ? 'bg-orange-600' : 'bg-emerald-600'}`}>
        {p.direction === 'SHORT' ? '↘ SHORT' : '↗ LONG'}
      </span>
      <span className="font-semibold text-amber-300">{p.words}</span>
      <span className="text-dark-400"> · signalled at the {formatSingleDate(p.signal_date)} close — no price until it is traded</span>
    </td>
  </tr>
);

// Signal Analysis › the Index vs Strategy card while its returns load
const SignalHeadlineSkeleton = () => (
  <div className="bg-dark-800 border border-dark-700 rounded-lg px-5 py-4 mb-4" data-testid="signal-headline-skeleton">
    <div className="flex flex-wrap items-center justify-between gap-x-10 gap-y-4">
      <div className="flex items-center gap-8">
        {[['Index Return', 'buy & hold'], ['Strategy Return', 'ClarityBox strategy · compounded']].map(([label, note], i) => (
          <React.Fragment key={label}>
            {i === 1 && <div className="text-dark-600 text-2xl font-light">vs</div>}
            <div>
              <div className="text-dark-500 text-[10px] uppercase tracking-wider font-semibold mb-1">{label}</div>
              <div className="h-9 flex items-center"><Skel className="h-7 w-28" /></div>
              <div className="text-dark-500 text-[11px] whitespace-nowrap">{note}</div>
            </div>
          </React.Fragment>
        ))}
      </div>
      <div className="flex items-center gap-8">
        {[['% In Market', 'capital exposed · index beside is 100%'], ['Edge', 'strategy vs buy & hold']].map(([label, note]) => (
          <div key={label} className="text-right">
            <div className="text-dark-500 text-[10px] uppercase tracking-wider font-semibold mb-1">{label}</div>
            <div className="h-9 flex items-center justify-end"><Skel className="h-7 w-24" /></div>
            <div className="text-dark-500 text-[11px] whitespace-nowrap">{note}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Signal Analysis › Detailed while it loads: the twelve figures by name, the
// chart, the view bar, and each selected index's card with its trade columns
const SIG_DETAIL_FIGURES = [['Gross Gains', 'Gross Losses', 'Total Trades', 'Win Rate', 'Avg Win', 'Avg Loss'],
  ['Best Trade', 'Worst Trade', 'Max Cons. Wins', 'Max Cons. Losses', 'MAE', 'Avg Days / Trade']];
const SIG_TRADE_COLUMNS = ['Dir', 'Entry', 'Exit', 'Entry Price', 'Exit Price', 'Gain %', 'Streak', 'Days', 'MAE %'];
const SignalDetailedSkeleton = ({ labels }) => (
  <div data-testid="signal-detailed-skeleton">
    <div className="bg-dark-800 border border-dark-700 rounded-lg px-5 py-4 mb-6">
      <div className="grid grid-cols-6 gap-x-6 gap-y-4">
        {SIG_DETAIL_FIGURES.map((row, r) => (
          <React.Fragment key={r}>
            {r === 1 && <div className="col-span-6 border-t border-dark-700" />}
            {row.map(label => (
              <div key={label}>
                <div className="text-dark-500 text-[10px] uppercase tracking-wider font-semibold mb-1">{label}</div>
                <div className="h-8 flex items-center"><Skel className="h-6 w-20" /></div>
                <div className="h-4 flex items-center"><Skel className="h-2.5 w-24" /></div>
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
    </div>
    <div className="bg-dark-800 border border-dark-700 rounded-lg p-4 mb-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <span className="text-white text-sm font-semibold">Index Performance</span>
          <span className="text-dark-500 text-[11px] ml-2">every line starts at 100</span>
        </div>
      </div>
      <Skel className="h-[320px] w-full block" rounded="rounded-lg" />
    </div>
    <div className="flex items-center gap-4 mb-5">
      <span className="text-dark-400 text-xs uppercase tracking-wider font-medium">View</span>
      <Skel className="h-9 w-44" rounded="rounded-lg" />
      <Skel className="h-9 w-32" rounded="rounded-lg" />
    </div>
    <div className="space-y-4">
      {labels.map(label => (
        <div key={label} className="bg-dark-800/50 border border-dark-700 rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-dark-800 border-b border-dark-700">
            <div className="flex items-center justify-between">
              <span className="text-white font-semibold">{label}</span>
              <div className="flex items-center gap-5">{[0, 1, 2].map(i => <Skel key={i} className="h-8 w-14" />)}</div>
            </div>
            <div className="mt-2"><Skel className="h-2.5 w-3/4" /></div>
          </div>
          <table className="w-full">
            <thead><tr className="text-dark-400 text-[10px] uppercase tracking-wider">
              {SIG_TRADE_COLUMNS.map(c => <th key={c} className="text-left py-2 px-3 font-semibold">{c}</th>)}
            </tr></thead>
            <tbody><SkelRows cols={SIG_TRADE_COLUMNS.length} rows={3} cellClass="py-2.5 px-3" /></tbody>
          </table>
        </div>
      ))}
    </div>
  </div>
);

const SignalAnalysis = ({ strategy, executionMode, netCosts, costConfig, reportSource, reloadKey, userHasShorts, filterSlot = null }) => {
  const [signalPeriod, setSignalPeriod] = useState(() => {
    const saved = localStorage.getItem('reports.signalPeriod') || '6m';
    return saved === '12m' ? '1y' : saved; // 12M became 1Y
  });
  const [signalView, setSignalView] = useState('by_index');
  // Analysis switch + Group by — each index card's trades read as periods, the
  // same reading as All Transactions → Analysis (Apal 2026-10-22). Remembered.
  const [sigAnalysis, setSigAnalysisState] = useState(() => {
    try { return localStorage.getItem('strategy.signalAnalysis') === '1'; } catch { return false; }
  });
  const setSigAnalysis = (on) => {
    setSigAnalysisState(on);
    try { localStorage.setItem('strategy.signalAnalysis', on ? '1' : '0'); } catch { /* storage off */ }
  };
  // the same target key as All Transactions — one target per fund manager
  const [sigTarget, setSigTargetState] = useState(() => {
    try { return parseTarget(localStorage.getItem('reports.groupTarget')); } catch { return null; }
  });
  const setSigTarget = (v) => {
    setSigTargetState(v);
    try { localStorage.setItem('reports.groupTarget', v == null ? 'index' : String(v)); } catch { /* storage off */ }
  };
  // Brief (cards, one screen) | Detailed (everything below today) — remembered.
  const [sigTab, setSigTabState] = useState(() => (localStorage.getItem('strategy.signalTab') === 'detailed' ? 'detailed' : 'brief'));
  const setSigTab = (t) => { setSigTabState(t); try { localStorage.setItem('strategy.signalTab', t); } catch { /* storage off */ } };
  const [includeOpen, setIncludeOpen] = useState(true);
  // Selection survives tab switches and reloads (same as Compare/Allocation)
  const [perSymStrategy, setPerSymStrategy] = useState({}); // { NIFTY50: 'with_shorts' }
  // L+S verdict from THE central universe — capability ∩ tradability ∩ grant
  const [shortsSymbols, setShortsSymbols] = useState(null); // null = not yet known — fetches wait
  useEffect(() => {
    loadUniverseShorts().then(s => setShortsSymbols([...s])).catch(() => setShortsSymbols([]));
  }, []);
  const [signalData, setSignalData] = useState(null);
  const [signalLoading, setSignalLoading] = useState(false);
  const [signalFailed, setSignalFailed] = useState(false);
  const [customFrom, setCustomFrom] = useState(() => localStorage.getItem('reports.signalCustomFrom') || '');
  const [priceChart, setPriceChart] = useState(null);
  const [priceFailed, setPriceFailed] = useState(false);
  const [chartFullscreen, setChartFullscreen] = useState(false);
  const [chartHidden, setChartHidden] = useState({});
  const chartRef = useRef(null);
  const chartFullRef = useRef(null);
  const symbolColorsRef = useRef({});
  const chartHiddenRef = useRef({});
  useEffect(() => { chartHiddenRef.current = chartHidden; }, [chartHidden]);
  // chartHover STATE drives the JSX (tooltip ◀ row, line width/fade props);
  // the DOM styling below stays as the instant-feedback fast path.
  const [chartHover, setChartHoverState] = useState(null);
  const setChartHover = (sym) => {
    // Skip hover for hidden indexes — no line to highlight
    if (sym && chartHiddenRef.current[sym]) { sym = null; }
    const colors = symbolColorsRef.current;
    const hoverColor = sym ? colors[sym] : null;
    [chartRef.current, chartFullRef.current].forEach(el => {
      if (!el) return;
      const paths = el.querySelectorAll('.recharts-line-curve');
      paths.forEach(path => {
        if (!sym) {
          path.style.opacity = '';
          path.style.strokeWidth = '';
        } else if (path.getAttribute('stroke') === hoverColor) {
          path.style.opacity = '1';
          path.style.strokeWidth = '3.5';
        } else {
          path.style.opacity = '0.35';
          path.style.strokeWidth = '';
        }
      });
    });
    setChartHoverState(sym); // keep the React state in sync for the tooltip/JSX
  };

  // Lock body scroll when chart is fullscreen
  useEffect(() => {
    if (chartFullscreen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [chartFullscreen]);
  const [customTo, setCustomTo] = useState(() => localStorage.getItem('reports.signalCustomTo') || '');
  // A calendar year in one pick (Apal: "I have to add 1st Jan to 31st December
  // selecting two dates vs what could have been 2021, 2022 etc") — sent as a
  // custom 1 Jan – 31 Dec range, so the backend needs nothing new.
  const [signalYear, setSignalYear] = useState(() => {
    const y = Number(localStorage.getItem('reports.signalYear'));
    return SIGNAL_YEARS.includes(y) ? y : SIGNAL_YEARS[0];
  });
  const [selected, setSelected] = useState(() => {
    try { return JSON.parse(localStorage.getItem('strategy.signalSelected')) || []; } catch { return []; }
  });
  const [regionSymbols, setRegionSymbols] = useState([]);
  const [regionsLoading, setRegionsLoading] = useState(true);
  const [selectorOpen, setSelectorOpen] = useState(true);

  useEffect(() => { localStorage.setItem('strategy.signalSelected', JSON.stringify(selected)); }, [selected]);
  useEffect(() => { localStorage.setItem('reports.signalPeriod', signalPeriod); }, [signalPeriod]);
  // Persist custom dates too — a restored 'custom' period with lost dates used to
  // skip the fetch and dead-end the page on a permanent "Loading...".
  useEffect(() => { localStorage.setItem('reports.signalCustomFrom', customFrom); }, [customFrom]);
  useEffect(() => { localStorage.setItem('reports.signalCustomTo', customTo); }, [customTo]);
  useEffect(() => { localStorage.setItem('reports.signalYear', String(signalYear)); }, [signalYear]);

  // Fetch available symbols (same pattern as CompareIndexes)
  useEffect(() => {
    apiService.getPeriodPerformance({ market: 'all', strategy: '1x' }).then(res => {
      setRegionSymbols((res.data?.regions || []).map(r => ({
        name: r.region, code: r.code,
        symbols: r.indexes.map(i => ({ name: i.symbol, label: i.label })),
      })));
    }).catch(() => {}).finally(() => setRegionsLoading(false));
  }, []);

  const toggle = (sym) => {
    setSelected(prev => prev.includes(sym) ? prev.filter(s => s !== sym) : [...prev, sym]);
  };

  const toggleRegion = (regionCode) => {
    const region = regionSymbols.find(r => r.code === regionCode);
    if (!region) return;
    const regionNames = region.symbols.map(s => s.name);
    const allSelected = regionNames.every(n => selected.includes(n));
    setSelected(prev => allSelected
      ? prev.filter(s => !regionNames.includes(s))
      : [...new Set([...prev, ...regionNames])]
    );
  };

  const costParams = useMemo(() => {
    if (!netCosts || !['1x', 'with_shorts', 'shorts_only'].includes(strategy)) return {};
    const params = { net_costs: 'true' };
    Object.entries(costConfig || {}).forEach(([sym, cfg]) => {
      if (cfg && typeof cfg === 'object') {
        params[`cost_${sym}`] = cfg.impact ?? 0;
        params[`carry_${sym}`] = cfg.carry_pct ?? 0;
        params[`intrate_${sym}`] = cfg.interest_rate ?? 0;
        params[`margin_${sym}`] = cfg.margin_pct ?? 20;
      }
    });
    return params;
  }, [netCosts, costConfig, strategy]);

  useEffect(() => {
    if (shortsSymbols === null) return; // wait for the shorts verdict
    if (selected.length === 0) { setSignalData(null); setSignalLoading(false); return; }
    if (signalPeriod === 'custom' && (!customFrom || !customTo)) return;
    let cancelled = false;
    const abort = new AbortController();     // a newer filter click cancels this one
    setSignalLoading(true);
    setSignalFailed(false);
    const startTime = Date.now();
    const fetchSignalAnalysis = async () => {
      try {
        const symStrategies = selected.map(sym => {
          // no L+S on the chip → long-only, whatever was chosen before (Apal 2026-10-12)
          const s = (shortsSymbols || []).includes(sym) ? (perSymStrategy[sym] || 'with_shorts') : '1x';
          return `${sym}:${s}`;
        }).join(',');
        const params = {
          symbols: selected.join(','),
          strategy,
          sym_strategies: symStrategies,
          period: signalPeriod === 'year' ? 'custom' : signalPeriod,
          ...(executionMode !== 'same_day_close' ? { execution: executionMode } : {}),
          ...(reportSource === 'latest' ? { source: 'latest' } : {}),
          ...costParams,
          ...(signalPeriod === 'custom' ? { from_date: customFrom, to_date: customTo } : {}),
          ...(signalPeriod === 'year' ? { from_date: `${signalYear}-01-01`, to_date: `${signalYear}-12-31` } : {}),
        };
        const res = await apiService.getSignalAnalysis(params, { signal: abort.signal });
        if (cancelled) return;
        // Ensure loading indicator shows for at least 400ms
        const elapsed = Date.now() - startTime;
        if (elapsed < 400) await new Promise(r => setTimeout(r, 400 - elapsed));
        setSignalData(res.data);
      } catch (e) {
        if (!cancelled && !isCanceled(e)) { console.error('Signal analysis error:', e); setSignalFailed(true); }
      } finally {
        if (!cancelled) setSignalLoading(false);
      }
    };
    fetchSignalAnalysis();
    return () => { cancelled = true; abort.abort(); };
  }, [selected, strategy, perSymStrategy, shortsSymbols, signalPeriod, executionMode, costParams, reportSource, reloadKey, customFrom, customTo, signalYear]);

  // Fetch close prices for chart when signalData period is available
  useEffect(() => {
    if (!selected.length || !signalData?.period) { setPriceChart(null); return; }
    const abort = new AbortController();     // a newer period/selection cancels this one
    setPriceFailed(false);
    const fetchPrices = async () => {
      try {
        const res = await apiService.getMultiClosePrices({
          symbols: selected.join(','),
          from_date: signalData.period.from,
          to_date: signalData.period.to,
        }, { signal: abort.signal });
        if (!abort.signal.aborted) setPriceChart(res.data);
      } catch (e) { if (!isCanceled(e)) { setPriceChart(null); setPriceFailed(true); } }
    };
    fetchPrices();
    return () => abort.abort();
  }, [selected, signalData?.period?.from, signalData?.period?.to]);

  const periodFrom = signalData?.period?.from || '';
  // ALL calculation logic lives in utils/signalAnalysisCalcs.js (pure + unit-tested).
  // Do not re-implement any inclusion/return math inline here.
  // Two rules (signalAnalysisCalcs): TRADES of the period feed the trade
  // statistics; positions HELD in the period (carried-in ones too) feed every
  // return, exposure and allocation number.
  const tradeIncluded = makeTradeFilter({ includeOpen, periodFrom });
  const heldIncluded = makeHeldFilter({ includeOpen });

  // Overall summary stats over the included trades (see calcs module for the rules)
  const summary = useMemo(
    () => computeOverallSummary(signalData?.regions, tradeIncluded),
    [signalData, includeOpen]
  );

  // Indexes dropped from the analysis (no counted trade), with the reason for the banner
  const excludedSymbols = useMemo(
    () => computeExcludedSymbols(signalData?.regions, heldIncluded, { includeOpen }),
    [signalData, includeOpen]
  );

  // Headline INDEX vs STRATEGY — equal-weight buckets, both sides compounded
  const systemReturns = useMemo(
    () => computeSystemReturns(priceChart, signalData?.regions, heldIncluded, selected, signalData?.signal_start || null, signalData?.period?.to || null, signalData?.period?.from || null),
    [priceChart, signalData, includeOpen]
  );

  // Brief view: the same per-index numbers the Detailed cards show, by market
  // null until the prices land (they arrive after the trades) — Brief shows a
  // placeholder then, never a false "no trades" while it waits
  const brief = useMemo(
    () => (priceChart ? computeBriefView(signalData?.regions, priceChart, heldIncluded, signalData?.period) : null),
    // tradeIncluded is rebuilt every render from includeOpen + the period (signalData) — both listed
    [priceChart, signalData, includeOpen] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // % in market — per index (interval union, clipped to window) + equal-weight overall
  const inMarket = useMemo(
    () => computeInMarketBySymbol(signalData?.regions, heldIncluded, signalData?.period?.from, signalData?.period?.to),
    [signalData, includeOpen]
  );


  const periods = [
    { key: '3m', label: '3M' }, { key: '6m', label: '6M' }, { key: '9m', label: '9M' },
    { key: 'ytd', label: 'YTD' }, { key: '1y', label: '1Y' }, { key: '2y', label: '2Y' },
    { key: '3y', label: '3Y' }, { key: 'year', label: 'Year' },
    { key: 'custom', label: 'Custom' },
  ];

  const gainColor = (g) => g > 0 ? 'text-green-400' : g < 0 ? 'text-red-400' : 'text-dark-400';
  const dirBadge = (d) => d === 'SHORT'
    ? 'bg-orange-500/20 text-orange-300 border-orange-500/30'
    : 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30';

  // Compute streaks on a chronological list of trades (ascending by date)
  const addStreaks = (trades) => {
    let lossMult = 1, gainMult = 1, inLoss = false, inGain = false;
    return trades.map((t, i) => {
      const isLoss = t.gain != null && t.gain < 0;
      const isGain = t.gain != null && t.gain > 0;
      const prev = i > 0 ? trades[i - 1] : null;
      const prevLoss = prev && prev.gain != null && prev.gain < 0;
      const prevGain = prev && prev.gain != null && prev.gain > 0;
      let streak = null;
      if (isLoss) {
        if (prevLoss) { lossMult *= (1 + t.gain / 100); inLoss = true; }
        else { lossMult = (1 + t.gain / 100); inLoss = false; }
        streak = inLoss ? (lossMult - 1) * 100 : null;
        gainMult = 1; inGain = false;
      } else if (isGain) {
        if (prevGain) { gainMult *= (1 + t.gain / 100); inGain = true; }
        else { gainMult = (1 + t.gain / 100); inGain = false; }
        streak = inGain ? (gainMult - 1) * 100 : null;
        lossMult = 1; inLoss = false;
      } else {
        lossMult = 1; gainMult = 1; inLoss = false; inGain = false;
      }
      return { ...t, streak };
    });
  };

  // Timeline: every position held in the period, newest first. Streaks run
  // over the period's own trades only — a carried-in position is not one.
  const timelineTrades = useMemo(() => {
    if (!signalData?.regions) return [];
    const trades = [];
    signalData.regions.forEach(r => {
      r.symbols.forEach(s => {
        s.trades.forEach(t => {
          trades.push({ ...t, symbol: s.symbol, symbol_label: s.label, region: r.region });
        });
      });
    });
    const byEnd = (a, b) => (a.exit_date || a.entry_date || '').localeCompare(b.exit_date || b.entry_date || '');
    const ofPeriod = addStreaks(trades.filter(tradeIncluded).sort(byEnd));
    const carried = trades.filter(t => !tradeIncluded(t)).map(t => ({ ...t, streak: null }));
    return [...ofPeriod, ...carried].sort(byEnd).reverse(); // newest first for display
  }, [signalData, includeOpen]); // eslint-disable-line react-hooks/exhaustive-deps -- tradeIncluded follows includeOpen + the period

  const CARRIED_TAG = 'ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-medium bg-dark-700 text-dark-400 border border-dark-600 align-middle';
  const fmtPx = (v) => (v == null ? '' : v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const pct = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(2)}%`);
  // A position held INTO the period is shown as the trade it really is —
  // real dates, real prices, its own result — and the part this period counts
  // sits under the gain (Apal 2026-10-17: "ETH has closed in a profit from
  // previous trade, and it's showing in loss so it's very confusing": +38.98%
  // from 04-07, −7.86% of it in 1–17 Oct).
  const wholeGain = (t) => {
    if (t.orig_gain != null) return t.orig_gain;
    const e = t.orig_entry_price, x = t.orig_exit_price;
    if (!e || x == null) return null;
    return t.direction === 'SHORT' ? (e - x) / e * 100 : (x - e) / e * 100;
  };
  // One sentence for a trade the period only counts a slice of
  const carriedNote = (t) => {
    if (!t.carried_in && !t.carried_out) return '';
    const side = t.direction === 'SHORT' ? 'Short' : 'Long';
    const end = t.still_open
      ? `still open today at ${fmtPx(t.orig_exit_price)}`
      : `closed ${formatSingleDate(t.orig_exit_date)} @ ${fmtPx(t.orig_exit_price)}`;
    const trade = `${side} from ${formatSingleDate(t.orig_entry_date)} @ ${fmtPx(t.orig_entry_price)}, ${end}: ${pct(wholeGain(t))} ${t.still_open ? 'so far' : 'overall'}.`;
    const from = t.carried_in ? `the ${t.basis_date ? `${formatSingleDate(t.basis_date)} ` : 'period-start '}close (${fmtPx(t.entry_price)})` : `its entry (${fmtPx(t.entry_price)})`;
    const to = t.carried_out ? `the ${formatSingleDate(t.exit_date)} close (${fmtPx(t.exit_price)})` : `its exit (${fmtPx(t.exit_price)})`;
    return `${trade} This period counts its move from ${from} to ${to}: ${pct(t.gain)}.`;
  };
  const TradeRow = ({ t, showSymbol, tone = null }) => {
    const streakColor = t.streak != null
      ? (t.streak < 0 ? (t.streak <= -5 ? 'bg-red-600/40 text-red-300' : 'bg-orange-600/30 text-orange-300') : (t.streak >= 5 ? 'bg-green-600/40 text-green-300' : 'bg-teal-600/30 text-teal-300'))
      : 'text-dark-500';
    const held = !!t.carried_in;                       // shown as the real trade
    const open = held ? !!t.still_open : isOpenAtEnd(t);
    const gainShown = held ? wholeGain(t) : t.gain;
    return (
    <tr className={`border-t border-dark-700/50 hover:bg-dark-800/50 ${open ? 'bg-yellow-900/10' : ''} ${tone ? `border-l-4 ${tone.edge} ${tone.row}` : ''}`}
      data-testid="sa-trade-row" title={carriedNote(t) || undefined}>
      {showSymbol && <td className="py-2.5 px-2 text-dark-200 text-sm font-medium">{t.symbol_label || t.symbol}</td>}
      <td className="py-2.5 px-2 text-center">
        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${dirBadge(t.direction)}`}>
          {t.direction}
        </span>
      </td>
      <td className="py-2.5 px-2 text-dark-300 text-sm whitespace-nowrap" data-testid="sa-trade-entry">
        {formatSingleDate(held ? t.orig_entry_date : t.entry_date)}
        {held && <span className={CARRIED_TAG}>carried in</span>}
      </td>
      <td className="py-2.5 px-2 text-sm whitespace-nowrap" data-testid="sa-trade-exit">
        {/* Open = still held (a held-over trade: today; a trade of the period:
            on the period's last day — Apal 2026-10-16). The tooltip says the rest. */}
        {open
          ? <span className="px-2 py-0.5 rounded text-xs font-semibold bg-yellow-900/40 text-yellow-400 border border-yellow-700/50">OPEN</span>
          : <span className="text-dark-300">{formatSingleDate(held ? t.orig_exit_date : t.exit_date)}</span>}
      </td>
      <td className="py-2.5 px-2 text-right text-dark-300 text-sm">
        {fmtPx(held ? t.orig_entry_price : t.entry_price)}
      </td>
      <td className={`py-2.5 px-2 text-right text-sm ${open ? 'text-yellow-400' : 'text-dark-300'}`}>
        {fmtPx(held ? t.orig_exit_price : t.exit_price) || '—'}
        {!held && t.carried_out && <div className="text-[10px] text-dark-500 whitespace-nowrap">{formatSingleDate(t.exit_date)} close</div>}
      </td>
      <td className="py-2.5 px-2 text-right text-sm" data-testid="sa-trade-gain">
        <div className={`font-semibold ${gainShown != null ? gainColor(gainShown) : 'text-dark-500'}`}>{pct(gainShown)}</div>
        {held && (
          <div className="text-[11px] whitespace-nowrap text-dark-400" data-testid="sa-trade-period-gain">
            <span className={`font-semibold ${gainColor(t.gain)}`}>{pct(t.gain)}</span> this period
          </div>
        )}
      </td>
      <td className={`py-2.5 px-2 text-right text-sm font-bold rounded ${streakColor}`}>
        {t.streak != null ? `${t.streak > 0 ? '+' : ''}${t.streak.toFixed(2)}%` : '-'}
      </td>
      <td className="py-2.5 px-2 text-center text-dark-400 text-sm">{(held ? (t.orig_days_held ?? t.days_held) : t.days_held) ?? '—'}</td>
      <td className="py-2.5 px-2 text-right text-dark-400 text-sm">{t.mae != null ? `${t.mae.toFixed(2)}%` : '—'}</td>
    </tr>
    );
  };

  const tableHeaders = (showSymbol) => (
    <tr className="text-dark-400 text-xs uppercase tracking-wider">
      {showSymbol && <th className="text-left py-2.5 px-2 font-medium">Index</th>}
      <th className="text-center py-2.5 px-2 font-medium">Dir</th>
      <th className="text-left py-2.5 px-2 font-medium">Entry</th>
      <th className="text-left py-2.5 px-2 font-medium">Exit</th>
      <th className="text-right py-2.5 px-2 font-medium">Entry Price</th>
      <th className="text-right py-2.5 px-2 font-medium">Exit Price</th>
      <th className="text-right py-2.5 px-2 font-medium">Gain %</th>
      <th className="text-right py-2.5 px-2 font-medium">Streak</th>
      <th className="text-center py-2.5 px-2 font-medium">Days</th>
      <th className="text-right py-2.5 px-2 font-medium">MAE %</th>
    </tr>
  );

  // first load: the results' own shapes (Apal 2026-11-03: "appropriate skeleton
  // for each page … exactly what's gonna load") — per tab, per selected market
  const sigCustomMissing = signalPeriod === 'custom' && (!customFrom || !customTo);
  const sigWaiting = selected.length > 0 && !signalData && !signalFailed && !sigCustomMissing;
  const selMarkets = regionSymbols.filter(r => r.symbols.some(x => selected.includes(x.name))).map(r => ({ market: r.code, name: r.name }));
  const labelOfSel = (sym) => regionSymbols.flatMap(r => r.symbols).find(x => x.name === sym)?.label || sym;

  return (
    <div>
      {/* Index selector — collapsible */}
      <div className="mb-5">
        <SelectorToggle open={selectorOpen} onToggle={() => setSelectorOpen(!selectorOpen)} count={selected.length} />
        {selectorOpen && (
          <div className="bg-dark-800/50 rounded-lg border border-dark-700 flex">
            {/* Left — selection; right — where the money is (Apal 2026-10-11) */}
            <div className="w-[65%] p-4 border-r border-dark-700">
            <IndexChipRows
              regions={regionSymbols}
              loading={regionsLoading}
              selected={selected}
              onToggle={toggle}
              onRegionToggle={toggleRegion}
              canShort={n => (shortsSymbols || []).includes(n)}
              modeFor={n => ((perSymStrategy[n] || 'with_shorts') === 'with_shorts' ? 'ls' : 'l')}
              onModeToggle={n => setPerSymStrategy(prev => ({ ...prev, [n]: (prev[n] || 'with_shorts') === 'with_shorts' ? '1x' : 'with_shorts' }))}
            />
            <div className="flex items-center gap-3 mt-3 pt-2 border-t border-dark-700">
              {selected.length > 0 && <button onClick={() => setSelected([])} className="text-xs text-dark-400 hover:text-red-400">Clear all</button>}
              {regionSymbols.length > 0 && (
                <button onClick={() => setSelected(regionSymbols.flatMap(r => r.symbols.map(s => s.name)))} className="text-xs text-dark-400 hover:text-teal-400">Select all</button>
              )}
              {selected.length === 0 && <span className="text-xs text-dark-500">Select indexes to analyze signals</span>}
            </div>
            </div>
            <div className="w-[35%] shrink-0 p-4">
              {systemReturns?.members ? (() => {
                const labelOf = (sym) => regionSymbols.flatMap(r => r.symbols).find(x => x.name === sym)?.label || sym;
                const groups = {};
                // 0% = never invested in ANY year of the period (a range lists each year's split above)
                const everHeld = new Set((systemReturns.segments || [{ members: systemReturns.members }]).flatMap(sg => sg.members.map(m => m.symbol)));
                selected.filter(sym => !everHeld.has(sym)).forEach(sym => {
                  const traded = (signalData?.regions || []).some(r => r.symbols.some(x => x.symbol === sym));
                  const hasPrices = (priceChart?.[sym]?.prices?.length || 0) >= 2;
                  const start = signalData?.signal_start?.[sym];
                  const reason = traded ? 'only an open trade (Include Open is off)'
                    : !hasPrices ? 'no data in this period'
                    : start && start <= signalData.period.to ? 'no position held this period'
                    : 'no reports yet in this period';
                  (groups[reason] = groups[reason] || []).push(labelOf(sym));
                });
                return (
                  <AllocationDonut
                    members={systemReturns.members}
                    zero={Object.entries(groups).map(([reason, labels]) => ({ reason, labels }))}
                    segments={systemReturns.segments}
                  />
                );
              })() : (
                selected.length ? (
                  <div className="flex items-center gap-4" data-testid="allocation-pending">
                    <Skel className="h-24 w-24" rounded="rounded-full" />
                    <div className="flex flex-col gap-2"><Skel className="h-3 w-28" /><Skel className="h-3 w-20" /><Skel className="h-3 w-24" /></div>
                  </div>
                ) : <p className="text-xs text-dark-500">Select indexes to see the allocation</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Page filters — Period and Open trades join Model · Costs · Execution
          in the ONE top bar, exactly like Compare and Quarterly Wins */}
      {(() => {
        const filters = (
          <>
            <PeriodControl id="signal-period-label" testid="signal-period" mode={signalPeriod}
              presets={periods.filter(p => p.key !== 'year' && p.key !== 'custom')} onPreset={setSignalPeriod}
              years={SIGNAL_YEARS} pickedYear={signalYear}
              onYear={y => { setSignalYear(y); setSignalPeriod('year'); }}
              yearPillTestid="period-year" yearAriaLabel="Calendar year"
              onCustom={() => setSignalPeriod('custom')}
              custom={<RangeInputs kind="date" from={customFrom} to={customTo} onFrom={setCustomFrom} onTo={setCustomTo} />} />
            <IncludeOpenToggle checked={includeOpen} onChange={setIncludeOpen}
              title="On: open trades count as closed at the current price in all stats & returns. Off: open trades are excluded everywhere (and an index left with none is dropped)." />
          </>
        );
        return filterSlot ? createPortal(filters, filterSlot) : <div className={`${FILTER_ROW} mb-5`} data-testid="signal-page-filters">{filters}</div>;
      })()}
      {/* the page's one strip, under the filters — first load and every refresh */}
      {(signalLoading || sigWaiting) && <UpdatingBar label={signalData ? 'Updating results' : 'Loading results'} className="mb-4" />}
      {sigWaiting && <div className="h-4 flex items-center mb-2" data-testid="signal-dates-pending"><Skel className="h-2.5 w-56" /></div>}
      {/* The dates the period resolved to — said on the results they cover */}
      {signalData?.period && (
        <div className="text-dark-500 text-xs mb-2" data-testid="signal-period-dates">
          {/* custom/year labels are the dates themselves — don't print them twice */}
          {signalPeriod === 'custom' || signalPeriod === 'year' ? '' : `${signalData.period.label}: `}{formatSingleDate(signalData.period.from)} — {formatSingleDate(signalData.period.to)}
        </div>
      )}

      {/* System comparison: Index vs Strategy — its shape until the returns are in */}
      {!systemReturns && selected.length > 0 && (sigWaiting || (signalData && !priceFailed)) && <SignalHeadlineSkeleton />}
      {systemReturns && (
        <div className={`bg-dark-800 border border-dark-700 rounded-lg px-5 py-4 mb-4 transition-opacity duration-300 ${signalLoading ? 'opacity-40' : ''}`}>
          <div className="flex flex-wrap items-center justify-between gap-x-10 gap-y-4 [&_.text-\[11px\]]:whitespace-nowrap">
            <div className="flex items-center gap-8">
              <div>
                <div className="text-dark-500 text-[10px] uppercase tracking-wider font-semibold mb-1">Index Return</div>
                <div className={`text-3xl font-bold ${systemReturns.indexRet >= 0 ? 'text-blue-400' : 'text-blue-300'}`}>{systemReturns.indexRet > 0 ? '+' : ''}{systemReturns.indexRet}%</div>
                <div className="text-dark-500 text-[11px]">buy & hold</div>
              </div>
              <div className="text-dark-600 text-2xl font-light">vs</div>
              <div>
                <div className="text-dark-500 text-[10px] uppercase tracking-wider font-semibold mb-1">Strategy Return</div>
                <div className={`text-3xl font-bold ${systemReturns.stratRet >= 0 ? 'text-green-400' : 'text-red-400'}`}>{systemReturns.stratRet > 0 ? '+' : ''}{systemReturns.stratRet}%</div>
                <div className="text-dark-500 text-[11px]">ClarityBox strategy · compounded</div>
              </div>
            </div>
            {/* How the money is split in THIS period: equal across the selected
                indexes that existed then (Apal 2026-10-11: "use empty space to
                show allocation … one year you have 10 indexes available vs one
                time when you don't") */}
            <div className="flex items-center gap-8">
              {inMarket.overall != null && (
                <div className="text-right">
                  <div className="text-dark-500 text-[10px] uppercase tracking-wider font-semibold mb-1">% In Market</div>
                  <div className={`text-3xl font-bold ${inMarket.overall > 66.7 ? 'text-red-400' : 'text-teal-400'}`}>{inMarket.overall}%</div>
                  <div className="text-dark-500 text-[11px]">capital exposed · index beside is 100%</div>
                </div>
              )}
              <div className="text-right">
                <div className="text-dark-500 text-[10px] uppercase tracking-wider font-semibold mb-1">Edge</div>
                <div className={`text-3xl font-bold ${systemReturns.stratRet - systemReturns.indexRet >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {(systemReturns.stratRet - systemReturns.indexRet) > 0 ? '+' : ''}{Math.round((systemReturns.stratRet - systemReturns.indexRet) * 100) / 100}%
                </div>
                <div className="text-dark-500 text-[11px]">strategy vs buy & hold</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Brief | Detailed — under the Index vs Strategy block, which both share */}
      {selected.length > 0 && (signalData || sigWaiting) && (
        <div className="flex items-center gap-0 border-b border-dark-700 mb-4" role="tablist" data-testid="signal-tabs">
          {[['brief', 'Brief'], ['detailed', 'Detailed']].map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={sigTab === k} onClick={() => setSigTab(k)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-all ${
                sigTab === k ? 'border-primary-400 text-primary-300' : 'border-transparent text-dark-400 hover:text-dark-200'}`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {sigTab === 'brief' && sigWaiting && <BriefSkeleton markets={selMarkets} />}
      {sigTab === 'brief' && selected.length > 0 && signalData && (
        <div className={`transition-opacity duration-300 ${signalLoading ? 'opacity-40' : ''}`}>
          <SignalBrief brief={brief} markets={selMarkets} promise={promiseCheck(summary, signalData?.regions, tradeIncluded)} inMarket={inMarket.overall} onOpen={(sym) => {
            setSigTab('detailed');
            setSignalView('by_index');
            // the card renders on the next paint; then bring it into view
            setTimeout(() => document.getElementById(cardId(sym))?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
          }} />
        </div>
      )}

      {sigTab === 'detailed' && sigWaiting && <SignalDetailedSkeleton labels={selected.map(labelOfSel)} />}
      {sigTab === 'detailed' && (<>
      {/* Summary cards */}
      {summary && (
        <div className={`bg-dark-800 border border-dark-700 rounded-lg px-5 py-4 mb-6 transition-opacity duration-300 ${signalLoading ? 'opacity-40' : ''}`}>
          <div className="grid grid-cols-6 gap-x-6 gap-y-4">
            {/* Row 1 */}
            <div>
              <div className="text-dark-500 text-[10px] uppercase tracking-wider font-semibold mb-1">Gross Gains</div>
              <div className="text-2xl font-bold text-green-400">+{summary.gross_gains}%</div>
              <div className="text-dark-500 text-[11px]">{summary.winning_trades} winning trades</div>
            </div>
            <div>
              <div className="text-dark-500 text-[10px] uppercase tracking-wider font-semibold mb-1">Gross Losses</div>
              <div className="text-2xl font-bold text-red-400">{summary.gross_losses}%</div>
              <div className="text-dark-500 text-[11px]">{summary.losing_trades} losing trades</div>
            </div>
            <div>
              <div className="text-dark-500 text-[10px] uppercase tracking-wider font-semibold mb-1">Total Trades</div>
              <div className="text-2xl font-bold text-white">{summary.total_trades}</div>
              <div className="text-dark-500 text-[11px]">{summary.winning_trades}W / {summary.losing_trades}L</div>
            </div>
            <div>
              <div className="text-dark-500 text-[10px] uppercase tracking-wider font-semibold mb-1">Win Rate</div>
              <div className={`text-2xl font-bold ${summary.win_rate >= 50 ? 'text-green-400' : 'text-red-400'}`}>{summary.win_rate}%</div>
            </div>
            <div>
              <div className="text-dark-500 text-[10px] uppercase tracking-wider font-semibold mb-1">Avg Win</div>
              <div className="text-2xl font-bold text-green-400">+{summary.avg_win}%</div>
            </div>
            <div>
              <div className="text-dark-500 text-[10px] uppercase tracking-wider font-semibold mb-1">Avg Loss</div>
              <div className="text-2xl font-bold text-red-400">{summary.avg_loss}%</div>
            </div>

            {/* Divider */}
            <div className="col-span-6 border-t border-dark-700" />

            {/* Row 2 */}
            <div>
              <div className="text-dark-500 text-[10px] uppercase tracking-wider font-semibold mb-1">Best Trade</div>
              <div className="text-2xl font-bold text-green-400">{summary.best_trade ? `+${summary.best_trade.gain}%` : '—'}</div>
              {summary.best_trade && <div className="text-dark-500 text-[11px]">{summary.best_trade.symbol} · {formatSingleDate(summary.best_trade.exit_date)}</div>}
            </div>
            <div>
              <div className="text-dark-500 text-[10px] uppercase tracking-wider font-semibold mb-1">Worst Trade</div>
              <div className="text-2xl font-bold text-red-400">{summary.worst_trade ? `${summary.worst_trade.gain}%` : '—'}</div>
              {summary.worst_trade && <div className="text-dark-500 text-[11px]">{summary.worst_trade.symbol} · {formatSingleDate(summary.worst_trade.exit_date)}</div>}
            </div>
            <div>
              <div className="text-dark-500 text-[10px] uppercase tracking-wider font-semibold mb-1">Max Cons. Wins</div>
              <div className="text-2xl font-bold text-green-400">{summary.max_cons_wins || '—'}</div>
              {summary.max_cons_wins > 0 && <div className="text-green-600 text-[11px]">+{summary.max_cons_wins_gain}% total</div>}
            </div>
            <div>
              <div className="text-dark-500 text-[10px] uppercase tracking-wider font-semibold mb-1">Max Cons. Losses</div>
              <div className="text-2xl font-bold text-red-400">{summary.max_cons_losses || '—'}</div>
              {summary.max_cons_losses > 0 && <div className="text-red-600 text-[11px]">{summary.max_cons_losses_loss}% total</div>}
            </div>
            <div>
              <div className="text-dark-500 text-[10px] uppercase tracking-wider font-semibold mb-1">MAE</div>
              <div className="text-2xl font-bold text-amber-400">{summary.avg_mae != null ? `${summary.avg_mae}%` : '—'}</div>
              {summary.max_mae != null && <div className="text-dark-500 text-[11px]">worst {summary.max_mae}%</div>}
            </div>
            <div>
              <div className="text-dark-500 text-[10px] uppercase tracking-wider font-semibold mb-1">Avg Days / Trade</div>
              <div className="text-2xl font-bold text-white">{summary.avg_days != null ? summary.avg_days : '—'}</div>
              {(summary.avg_days_long != null || summary.avg_days_short != null) && (
                <div className="text-dark-500 text-[11px]">
                  {summary.avg_days_long != null && <span className="text-emerald-400">L {summary.avg_days_long}</span>}
                  {summary.avg_days_long != null && summary.avg_days_short != null && ' / '}
                  {summary.avg_days_short != null && <span className="text-orange-400">S {summary.avg_days_short}</span>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Price Chart — close prices of all selected indexes, rebased to 100 */}
      {signalData && selected.length > 0 && !priceChart && !priceFailed && (
        <div className="bg-dark-800 border border-dark-700 rounded-lg p-4 mb-5" data-testid="chart-skeleton">
          <div className="flex items-center justify-between mb-3">
            <div>
              <span className="text-white text-sm font-semibold">Index Performance</span>
              <span className="text-dark-500 text-[11px] ml-2">every line starts at 100</span>
            </div>
          </div>
          <Skel className="h-[320px] w-full block" rounded="rounded-lg" />
        </div>
      )}
      {priceChart && Object.keys(priceChart).length > 0 && (() => {
        const COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#a855f7', '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16', '#06b6d4', '#e11d48', '#8b5cf6', '#10b981', '#d946ef', '#facc15', '#0ea5e9', '#f43f5e'];
        const symbols = Object.keys(priceChart);
        const symbolColors = {};
        const labels = {};
        symbols.forEach((sym, i) => {
          symbolColors[sym] = COLORS[i % COLORS.length];
          const symStrat = (shortsSymbols || []).includes(sym) ? (perSymStrategy[sym] || 'with_shorts') : '1x';
          const canShort = (shortsSymbols || []).includes(sym);
          const isShortActive = canShort && (symStrat === 'with_shorts' || symStrat === 'shorts_only');
          labels[sym] = priceChart[sym].label;
          if (isShortActive) labels[sym] += symStrat === 'shorts_only' ? '\n(Shorts)' : '\n(L+S)';
        });
        symbolColorsRef.current = symbolColors;
        // Rebase each to 100 at first data point
        const rebased = {};
        symbols.forEach(sym => {
          const prices = priceChart[sym].prices;
          if (!prices.length) return;
          const base = prices[0].close;
          rebased[sym] = {};
          prices.forEach(p => { rebased[sym][p.date] = Math.round(p.close / base * 10000) / 100; });
        });
        // Merge all dates
        const allDates = [...new Set(symbols.flatMap(sym => priceChart[sym].prices.map(p => p.date)))].sort();
        // Sample dates to avoid rendering thousands of points (max ~200)
        const step = Math.max(1, Math.floor(allDates.length / 200));
        const sampledDates = allDates.filter((_, i) => i % step === 0 || i === allDates.length - 1);
        const chartData = sampledDates.map(d => {
          const row = { date: d };
          symbols.forEach(sym => {
            if (rebased[sym]?.[d] != null) row[sym] = rebased[sym][d];
            else {
              // Find nearest previous date
              const prices = priceChart[sym].prices;
              let val = null;
              for (let i = prices.length - 1; i >= 0; i--) {
                if (prices[i].date <= d) { val = Math.round(prices[i].close / prices[0].close * 10000) / 100; break; }
              }
              if (val != null) row[sym] = val;
            }
          });
          return row;
        });
        // Period return per index: (last close / first close - 1) * 100
        // Build strategy return per symbol from trade data
        const stratReturns = {};
        const stratTradeCounts = {};
        const stratWinCounts = {};
        (signalData?.regions || []).forEach(r => r.symbols.forEach(s => {
          const held = s.trades.filter(t => t.gain != null && heldIncluded(t));
          const trades = s.trades.filter(t => t.gain != null && tradeIncluded(t));
          stratReturns[s.symbol] = compoundGain(held); // what the money did, compounded, matching indexRet
          stratTradeCounts[s.symbol] = trades.length;   // the period's own trades
          stratWinCounts[s.symbol] = trades.filter(t => t.gain > 0).length;
        }));

        // Ranked leaderboard: traded indexes by strategy return; indexes that
        // sat out the whole period go last ("no trades" is cash, not a loss).
        const periodReturns = symbols.map(sym => {
          const prices = priceChart[sym].prices;
          if (prices.length < 2) return { sym, label: labels[sym], indexRet: null, stratRet: null, trades: 0 };
          const first = prices[0].close;
          const last = prices[prices.length - 1].close;
          const indexRet = Math.round((last / first - 1) * 10000) / 100;
          const stratRet = stratReturns[sym] || 0;
          return {
            sym, label: labels[sym], indexRet, stratRet,
            trades: stratTradeCounts[sym] || 0,
            wins: stratWinCounts[sym] || 0,
            beat: Math.round((stratRet - indexRet) * 100) / 100,
          };
        }).filter(r => r.indexRet != null).sort((a, b) => {
          if ((a.trades > 0) !== (b.trades > 0)) return a.trades > 0 ? -1 : 1;
          return b.stratRet - a.stratRet;
        });
        const traded = periodReturns.filter(r => r.trades > 0);
        const topPerformers = traded.slice(0, 3);
        // Losers strip only when there ARE losers — quiet when everything won
        const worstPerformers = traded.filter(r => r.stratRet < 0).slice(-3).reverse()
          .map(r => ({ ...r, rank: traded.indexOf(r) + 1 }));

        const chartContent = (height) => (
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={chartData}>
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={d => formatSingleDate(d)} minTickGap={50} />
              <YAxis tick={{ fontSize: 10, fill: '#64748b' }} domain={['auto', 'auto']} />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const items = payload.filter(p => p.value != null && p.stroke !== 'transparent').sort((a, b) => b.value - a.value);
                  if (!items.length) return null;
                  return (
                    <div style={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
                      <div style={{ color: '#94a3b8', marginBottom: 4 }}>{formatSingleDate(label)}</div>
                      {items.map((p, i) => {
                        const hovered = p.dataKey === chartHover; // the line under the mouse
                        return (
                          <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '1px 4px', fontWeight: hovered || i === 0 ? 700 : 400, backgroundColor: hovered ? 'rgba(148,163,184,0.18)' : 'transparent', borderRadius: 4 }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: p.stroke, flexShrink: 0 }} />
                            <span style={{ color: hovered || i === 0 ? '#fff' : '#94a3b8', flex: 1 }}>{labels[p.dataKey] || p.dataKey}</span>
                            <span style={{ color: p.value >= 100 ? '#4ade80' : '#f87171', fontWeight: 600 }}>{p.value?.toFixed(1)}</span>
                            {hovered && <span style={{ color: '#e2e8f0' }}>◀</span>}
                          </div>
                        );
                      })}
                    </div>
                  );
                }}
              />
              <Legend
                formatter={(val) => labels[val] || val}
                wrapperStyle={{ fontSize: 11, cursor: 'pointer' }}
                onClick={(e) => { if (e?.dataKey) setChartHidden(prev => ({ ...prev, [e.dataKey]: !prev[e.dataKey] })); }}
                onMouseEnter={(e) => { if (e?.dataKey) setChartHover(e.dataKey); }}
                onMouseLeave={() => setChartHover(null)}
                payload={symbols.map(sym => ({
                  value: labels[sym] || sym,
                  dataKey: sym,
                  type: 'line',
                  color: chartHidden[sym] ? '#475569' : symbolColors[sym],
                }))}
              />
              {symbols.map(sym => (
                <Line key={sym} type="monotone" dataKey={sym}
                  stroke={chartHidden[sym] ? 'transparent' : symbolColors[sym]}
                  strokeWidth={chartHover === sym ? 3 : 1.5}
                  strokeOpacity={chartHover && chartHover !== sym ? 0.15 : 1}
                  dot={false} connectNulls
                  isAnimationActive={false}
                  onMouseEnter={() => setChartHover(sym)}
                  onMouseLeave={() => setChartHover(null)} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );

        return (
          <>
            <div ref={chartRef} className={`bg-dark-800 border border-dark-700 rounded-lg p-4 mb-5 transition-opacity duration-300 ${signalLoading ? 'opacity-40' : ''}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span className="text-white text-sm font-semibold" title="Every line starts at 100 — the higher it ends, the stronger the period.">Index Performance</span>
                  <span className="text-dark-500 text-[11px] ml-2">every line starts at 100</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-dark-500 text-[10px]">{symbols.length} indexes · {allDates.length} days</span>
                  <button onClick={() => setChartFullscreen(true)} className="text-dark-400 hover:text-white text-xs px-2 py-1 bg-dark-700 rounded hover:bg-dark-600 transition-colors">Expand</button>
                </div>
              </div>
              {/* Top performers — the "banger indexes" get read first; the
                  wall of numbers moves into ranked chips + hover detail */}
              {topPerformers.length > 0 && (
                <>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-dark-500 text-[11px] uppercase tracking-wider">Top performers
                    <span className="text-dark-600 ml-2 normal-case tracking-normal">each number = that index's trades this period, compounded</span>
                  </span>
                  <button onClick={() => setChartFullscreen(true)} className="text-dark-400 hover:text-white text-[11px] underline decoration-dotted">Full ranking →</button>
                </div>
                <div className="grid gap-2 mb-3 [grid-template-columns:repeat(auto-fit,minmax(190px,1fr))]" data-testid="top-performers">
                  {topPerformers.map((r, i) => (
                    <button key={r.sym}
                      onClick={() => setChartHidden(prev => ({ ...prev, [r.sym]: !prev[r.sym] }))}
                      onMouseEnter={() => setChartHover(r.sym)} onMouseLeave={() => setChartHover(null)}
                      className={`text-left bg-dark-700/40 rounded-lg px-3 py-2 border transition-opacity ${i === 0 ? 'border-emerald-500/50' : 'border-dark-600/40'} ${chartHidden[r.sym] ? 'opacity-30' : ''}`}>
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5 text-xs text-white font-medium">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: symbolColors[r.sym] }} />
                          {r.label}
                        </span>
                        <span className={`text-[10px] font-bold px-1.5 rounded-full ${i === 0 ? 'bg-emerald-500/15 text-emerald-400' : 'bg-dark-600/60 text-dark-300'}`}>#{i + 1}</span>
                      </div>
                      <div className="flex items-baseline justify-between mt-0.5">
                        <span>
                          <span className={`text-xl font-bold ${r.stratRet >= 0 ? 'text-green-400' : 'text-red-400'}`}>{r.stratRet > 0 ? '+' : ''}{r.stratRet}%</span>
                          <span className="text-dark-500 text-[10px] ml-1">MV</span>
                        </span>
                        <span className="text-dark-600 text-sm">vs</span>
                        <span>
                          <span className="text-xl font-bold text-blue-400">{r.indexRet > 0 ? '+' : ''}{r.indexRet}%</span>
                          <span className="text-dark-500 text-[10px] ml-1">Index</span>
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-dark-400 mt-0.5">
                        <span>{r.trades} trade{r.trades === 1 ? '' : 's'} · {r.wins} win{r.wins === 1 ? '' : 's'}</span>
                        {inMarket.perSymbol[r.sym] != null && <span>{inMarket.perSymbol[r.sym]}% in market</span>}
                      </div>
                    </button>
                  ))}
                </div>
                {worstPerformers.length > 0 && (
                  <>
                  <div className="text-red-400/70 text-[11px] uppercase tracking-wider mb-2">In the red</div>
                  <div className="grid gap-2 mb-3 [grid-template-columns:repeat(auto-fit,minmax(190px,1fr))]" data-testid="worst-performers">
                    {worstPerformers.map(r => (
                      <button key={r.sym}
                        onClick={() => setChartHidden(prev => ({ ...prev, [r.sym]: !prev[r.sym] }))}
                        onMouseEnter={() => setChartHover(r.sym)} onMouseLeave={() => setChartHover(null)}
                        className={`text-left bg-dark-700/40 rounded-lg px-3 py-2 border border-red-500/30 transition-opacity ${chartHidden[r.sym] ? 'opacity-30' : ''}`}>
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1.5 text-xs text-white font-medium">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: symbolColors[r.sym] }} />
                            {r.label}
                          </span>
                          <span className="text-[10px] font-bold px-1.5 rounded-full bg-red-500/15 text-red-400">#{r.rank} of {traded.length}</span>
                        </div>
                        <div className="flex items-baseline justify-between mt-0.5">
                          <span>
                            <span className="text-xl font-bold text-red-400">{r.stratRet}%</span>
                            <span className="text-dark-500 text-[10px] ml-1">MV</span>
                          </span>
                          <span className="text-dark-600 text-sm">vs</span>
                          <span>
                            <span className="text-xl font-bold text-blue-400">{r.indexRet > 0 ? '+' : ''}{r.indexRet}%</span>
                            <span className="text-dark-500 text-[10px] ml-1">Index</span>
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-[11px] text-dark-400 mt-0.5">
                          <span>{r.trades} trade{r.trades === 1 ? '' : 's'} · {r.wins} win{r.wins === 1 ? '' : 's'}</span>
                          {inMarket.perSymbol[r.sym] != null && <span>{inMarket.perSymbol[r.sym]}% in market</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                  </>
                )}
                </>
              )}
              {chartContent(320)}
            </div>
            {/* Fullscreen popup */}
            {chartFullscreen && (
              <div className="fixed inset-0 z-[60] bg-dark-900 flex flex-col overflow-hidden" style={{ isolation: 'isolate' }}>
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-dark-700 shrink-0">
                  <div>
                    <span className="text-white text-lg font-semibold">Close Price</span>
                    <span className="text-dark-500 text-sm ml-3">every line starts at 100 · {symbols.length} indexes · {signalData?.period?.label}</span>
                  </div>
                  <button onClick={() => setChartFullscreen(false)} className="text-dark-400 hover:text-white text-sm px-4 py-2 bg-dark-700 rounded-lg hover:bg-dark-600 transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {/* Body: chart + side panel */}
                <div className="flex flex-1 min-h-0">
                  {/* Chart */}
                  <div ref={chartFullRef} className="flex-1 min-w-0 p-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={d => formatSingleDate(d)} minTickGap={60} />
                        <YAxis tick={{ fontSize: 10, fill: '#64748b' }} domain={['auto', 'auto']} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }}
                          labelFormatter={d => formatSingleDate(d)}
                          formatter={(val, name) => [`${val?.toFixed(1)}`, labels[name] || name]}
                        />
                        {symbols.filter(sym => !chartHidden[sym]).map(sym => (
                          <Line key={sym} type="monotone" dataKey={sym} stroke={symbolColors[sym]}
                            strokeWidth={chartHover === sym ? 3 : 1.5}
                            strokeOpacity={chartHover && chartHover !== sym ? 0.15 : 1}
                            dot={false} connectNulls isAnimationActive={false}
                            onMouseEnter={() => setChartHover(sym)}
                            onMouseLeave={() => setChartHover(null)} />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  {/* Side panel — leaderboard */}
                  <div className="w-[280px] shrink-0 border-l border-dark-700 overflow-y-auto">
                    <div className="px-4 py-3 border-b border-dark-700 sticky top-0 bg-dark-900 z-10">
                      <div className="flex items-center justify-between">
                        <span className="text-dark-400 text-[10px] uppercase tracking-wider font-semibold">Period Return by Region</span>
                        <div className="flex items-center gap-2">
                          <button onClick={() => setChartHidden({})} className="text-[10px] text-dark-500 hover:text-teal-400">Show All</button>
                          <button onClick={() => { const h = {}; symbols.forEach(s => { h[s] = true; }); setChartHidden(h); }} className="text-[10px] text-dark-500 hover:text-red-400">Hide All</button>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-green-400 text-[10px] font-semibold">● MV Strategy</span>
                        <span className="text-blue-400 text-[10px] font-semibold">● Index</span>
                      </div>
                    </div>
                    {(signalData?.regions || []).map(region => {
                      const regionReturns = periodReturns.filter(r => region.symbols.some(s => s.symbol === r.sym));
                      if (!regionReturns.length) return null;
                      const regionSyms = regionReturns.map(r => r.sym);
                      const allHidden = regionSyms.every(s => chartHidden[s]);
                      return (
                        <div key={region.code}>
                          <button onClick={() => {
                            setChartHidden(prev => {
                              const next = { ...prev };
                              regionSyms.forEach(s => { next[s] = !allHidden; });
                              return next;
                            });
                          }} className={`w-full px-4 py-2 bg-dark-800/50 border-b border-dark-700 sticky top-[41px] z-[5] text-left hover:bg-dark-700/50 transition-colors ${allHidden ? 'opacity-40' : ''}`}>
                            <span className="text-dark-400 text-[10px] uppercase tracking-widest font-bold">{region.region}</span>
                            <span className="text-dark-600 text-[10px] ml-2">{allHidden ? 'show' : 'hide'}</span>
                          </button>
                          {regionReturns.map(r => (
                            <button key={r.sym} onClick={() => setChartHidden(prev => ({ ...prev, [r.sym]: !prev[r.sym] }))}
                              onMouseEnter={() => setChartHover(r.sym)} onMouseLeave={() => setChartHover(null)}
                              className={`flex items-center gap-3 px-4 py-2.5 hover:bg-dark-700/50 border-b border-dark-800 w-full text-left transition-opacity ${chartHidden[r.sym] ? 'opacity-30' : ''}`}>
                              <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: symbolColors[r.sym] }} />
                              <span className="text-dark-200 text-sm flex-1 truncate">{r.label}</span>
                              <div className="text-right shrink-0">
                                <div className={`text-sm font-bold tabular-nums ${r.stratRet >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  {r.stratRet > 0 ? '+' : ''}{r.stratRet}%
                                </div>
                                <div className={`text-[10px] tabular-nums ${r.indexRet >= 0 ? 'text-blue-400' : 'text-blue-300'}`}>
                                  idx {r.indexRet > 0 ? '+' : ''}{r.indexRet}%
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* View toggle + Export — only shown once analysis data is loaded */}
      {signalData?.regions?.length > 0 && (
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-dark-400 text-xs uppercase tracking-wider font-medium">View</span>
            <div className="flex items-center gap-1 bg-dark-700 p-1 rounded-lg">
              {[{ key: 'by_index', label: 'By Index' }, { key: 'timeline', label: 'Timeline' }].map(v => (
                <button key={v.key} onClick={() => setSignalView(v.key)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${signalView === v.key ? 'bg-primary-600 text-white shadow-md' : 'text-dark-300 hover:text-white hover:bg-dark-600'}`}>
                  {v.label}
                </button>
              ))}
            </div>
          </div>
          {signalView === 'by_index' && (
            <>
              <SwitchControl inline label="Analysis" checked={sigAnalysis} onChange={setSigAnalysis} testid="sa-analysis"
                title="Splits each index's trades into periods — Exceptional, Good, Average or Poor — with how long each lasted, what it made and how the index did" />
              {sigAnalysis && <GroupBySelect id="sa-group-by" value={sigTarget} onChange={setSigTarget} testid="sa-group-by" />}
            </>
          )}
        </div>
        {signalData && (
          <button onClick={() => {
            try {
              const lines = [];
              const p = signalData?.period;

              // Summary section
              lines.push('Signal Analysis Report');
              lines.push(`Period,${p?.label || ''},${p?.from ? formatSingleDate(p.from) : ''},to,${p?.to ? formatSingleDate(p.to) : ''}`);
              lines.push('');
              if (summary) {
                lines.push('SUMMARY');
                lines.push(`Gross Gains,+${summary.gross_gains}%`);
                lines.push(`Gross Losses,${summary.gross_losses}%`);
                lines.push(`Total Trades,${summary.total_trades}`);
                lines.push(`Winning,${summary.winning_trades}`);
                lines.push(`Losing,${summary.losing_trades}`);
                lines.push(`Win Rate,${summary.win_rate}%`);
                lines.push(`Avg Win,+${summary.avg_win}%`);
                lines.push(`Avg Loss,${summary.avg_loss}%`);
                if (summary.best_trade) lines.push(`Best Trade,+${summary.best_trade.gain}%,${summary.best_trade.symbol},${formatSingleDate(summary.best_trade.exit_date)}`);
                if (summary.worst_trade) lines.push(`Worst Trade,${summary.worst_trade.gain}%,${summary.worst_trade.symbol},${formatSingleDate(summary.worst_trade.exit_date)}`);
                lines.push(`Max Cons. Wins,${summary.max_cons_wins},+${summary.max_cons_wins_gain}% total`);
                lines.push(`Max Cons. Losses,${summary.max_cons_losses},${summary.max_cons_losses_loss}% total`);
                if (summary.avg_mae != null) lines.push(`Avg MAE,${summary.avg_mae}%`);
                if (summary.max_mae != null) lines.push(`Worst MAE,${summary.max_mae}%`);
                lines.push('');
              }

              const tradeHeader = 'Symbol,Direction,Entry Date,Exit Date,Entry Price,Exit Price,Gain %,Streak,Days Held,MAE %,Status,Counted In Period %';
              const tradeRow = (t) => {
                // a held-over position is exported as the trade it is; the last column is what the period counts
                const held = !!t.carried_in;
                const open = held ? !!t.still_open : isOpenAtEnd(t);
                const g = held ? wholeGain(t) : t.gain;
                const status = [open ? 'OPEN' : 'CLOSED', held ? 'carried in' : ''].filter(Boolean).join(' · ');
                const cells = [
                  t.symbol_label || t.symbol || '', t.direction || '',
                  formatSingleDate(held ? t.orig_entry_date : t.entry_date) || '',
                  open ? 'OPEN' : (formatSingleDate(held ? t.orig_exit_date : t.exit_date) || ''),
                  (held ? t.orig_entry_price : t.entry_price)?.toFixed(2) || '',
                  (held ? t.orig_exit_price : t.exit_price)?.toFixed(2) || '',
                  g != null ? pct(g) : '',
                  t.streak != null ? `${t.streak > 0 ? '+' : ''}${t.streak.toFixed(2)}%` : '',
                  (held ? (t.orig_days_held ?? t.days_held) : t.days_held) ?? '',
                  t.mae != null ? `${t.mae.toFixed(2)}%` : '',
                  status,
                  t.gain != null ? pct(t.gain) : '',
                ];
                return cells.map(c => `"${c}"`).join(',');
              };

              if (signalView === 'timeline') {
                lines.push('TRADES (Timeline)');
                lines.push(tradeHeader);
                timelineTrades.filter(heldIncluded).forEach(t => lines.push(tradeRow(t)));
              } else {
                (signalData?.regions || []).forEach(r => {
                  r.symbols.forEach(sym => {
                    const visTrades = sym.trades.filter(heldIncluded);
                    if (!visTrades.length) return;
                    const comp = visTrades.filter(t => t.gain != null);
                    const w = comp.filter(t => t.gain > 0);
                    const l = comp.filter(t => t.gain <= 0);
                    lines.push(`${sym.label} (${sym.symbol})`);
                    lines.push(`Gross Gains,+${w.length ? Math.round(w.reduce((s, t) => s + t.gain, 0) * 100) / 100 : 0}%,Gross Losses,${l.length ? Math.round(l.reduce((s, t) => s + t.gain, 0) * 100) / 100 : 0}%,Trades,${w.length}W / ${l.length}L,Win Rate,${comp.length ? Math.round(w.length / comp.length * 1000) / 10 : 0}%`);
                    lines.push(tradeHeader);
                    const asc = [...visTrades].sort((a, b) => (a.exit_date || a.entry_date || '').localeCompare(b.exit_date || b.entry_date || ''));
                    addStreaks(asc).reverse().forEach(t => lines.push(tradeRow({ ...t, symbol: sym.symbol, symbol_label: sym.label })));
                    lines.push('');
                  });
                });
              }

              const csv = lines.join('\n');
              const blob = new Blob([csv], { type: 'text/csv' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              const period = p?.label?.replace(/\s+/g, '_') || signalPeriod;
              link.download = `signal_analysis_${period}_${new Date().toISOString().slice(0, 10)}.csv`;
              document.body.appendChild(link);
              link.click();
              link.remove();
              window.URL.revokeObjectURL(url);
            } catch (e) { console.error('Export failed:', e); }
          }}
            className="flex items-center gap-2 px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors">
            <Download className="w-3.5 h-3.5" />
            Export
          </button>
        )}
      </div>
      )}

      </>)}

      {/* No index selected — prompt the user to pick one before anything renders */}
      {selected.length === 0 && (
        <div className="text-center py-20 px-4">
          <BarChart3 className="w-10 h-10 text-dark-600 mx-auto mb-4" />
          <p className="text-base font-medium text-dark-200">Select one or more indexes to see the analysis</p>
          <p className="text-sm text-dark-500 mt-1.5">Use the index selector above to choose which indexes to analyze.</p>
        </div>
      )}

      {/* No data */}
      {!signalLoading && signalData && timelineTrades.length === 0 && (
        <div className="text-center py-12 text-dark-500">No trades found for this period.</div>
      )}
      {!signalData && !signalLoading && selected.length > 0 && (
        signalPeriod === 'custom' && (!customFrom || !customTo)
          ? <div className="text-center py-12 text-dark-400">Pick a start and end date to run the custom period.</div>
          : signalFailed ? <div className="text-center py-12 text-dark-400" data-testid="signal-failed">The analysis could not be loaded · Reload to try again.</div>
          : null
      )}

      {sigTab === 'detailed' && (<>
      {/* Timeline View */}
      {signalView === 'timeline' && timelineTrades.length > 0 && (
        <div className={`overflow-x-auto transition-opacity duration-300 ${signalLoading ? 'opacity-40' : ''}`}>
          <table className="w-full">
            <thead>{tableHeaders(true)}</thead>
            <tbody>
              {/* The period's own trades first; positions held into it below a
                  divider — same as each By Index card (Apal 2026-10-17: lead
                  with the period's trades and what is open at its close) */}
              {(() => {
                const ofPeriod = timelineTrades.filter(tradeIncluded);
                const carried = timelineTrades.filter(t => heldIncluded(t) && !tradeIncluded(t));
                return (
                  <>
                    {ofPeriod.map((t, i) => <TradeRow key={`${t.symbol}-${t.trade_number}-${i}`} t={t} showSymbol={true} />)}
                    {carried.length > 0 && (
                      <tr data-testid="sa-carried-head">
                        <td colSpan={10} className="pt-3 pb-1 px-2 text-[10px] uppercase tracking-wider font-semibold text-dark-500 border-t border-dashed border-dark-600">
                          Held into this period — counted in the return, not in trade stats
                        </td>
                      </tr>
                    )}
                    {carried.map((t, i) => <TradeRow key={`c-${t.symbol}-${t.trade_number}-${i}`} t={t} showSymbol={true} />)}
                  </>
                );
              })()}
            </tbody>
          </table>
        </div>
      )}

      {/* By Index View */}
      {signalView === 'by_index' && signalData?.regions?.length > 0 && (
        <>
        {/* One chip per card actually rendered — the same inclusion rule the
            cards below use, so a chip never points at a card that is not
            there. OUTSIDE the container below: that fades to 40% while data
            reloads, and a pinned panel inside it faded too, letting the page
            show through it — the ghosting this panel exists to avoid. */}
        <IndexJumpBar
          items={signalData.regions.flatMap(region => region.symbols
            .filter(sym => sym.trades.some(t => t.gain != null && heldIncluded(t)))
            .map(sym => ({ symbol: sym.symbol, label: sym.label, region: region.code, regionLabel: region.region })))}
        />
        <div className={`space-y-6 transition-opacity duration-300 ${signalLoading ? 'opacity-40' : ''}`}>
          {excludedSymbols.length > 0 && (
            <div className="flex flex-col gap-1.5 px-4 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-[11px] text-amber-300">
              <span className="font-semibold">Excluded from this analysis ({excludedSymbols.length}) — not part of the overall comparison either:</span>
              {[
                { key: 'open_only', label: 'only a position still open at the period’s end — switch Open trades on to count it' },
                { key: 'no_trades', label: 'no trade in this period' },
              ].map(g => {
                const items = excludedSymbols.filter(e => e.reason === g.key);
                if (!items.length) return null;
                return (
                  <span key={g.key}><span className="font-medium">{items.map(e => e.label).join(', ')}</span> — {g.label}.</span>
                );
              })}
            </div>
          )}
          {signalData.regions.map(region => (
            <div key={region.code}>
              <h3 className="text-sm font-bold text-dark-400 uppercase tracking-wider mb-3">{region.region}</h3>
              <div className="space-y-4">
                {region.symbols.map(sym => {
                  // what the money did here (carried-in positions too) vs the period's own trades
                  const held = sym.trades.filter(t => t.gain != null && heldIncluded(t));
                  if (held.length === 0) return null;  // nothing counted -> excluded (surfaced in the banner at top)
                  const comp = sym.trades.filter(t => t.gain != null && tradeIncluded(t));
                  const st = computeSymbolStats(comp); // trade statistics: the period's own trades only
                  const carriedOnly = comp.length === 0;
                  const firstCarried = held.find(t => isCarriedIn(t, periodFrom));
                  // Always show the block for selected symbols (even with 0 trades)
                  return (
                  <div key={sym.symbol} id={cardId(sym.symbol)} data-jump-symbol={sym.symbol}
                       className="bg-dark-800/50 border border-dark-700 rounded-lg overflow-hidden">
                    {/* Symbol header */}
                    {(() => {
                      const idxRet = indexReturnPct(priceChart?.[sym.symbol]?.prices, priceChart?.[sym.symbol]?.basis);
                      // Strategy return compounds the counted trades (∏(1+gain) − 1), matching
                      // the INDEX % beside it. Open trades are valued at current price when
                      // Include Open is on; excluded from `comp` entirely when off.
                      const stratRet = compoundGain(held);
                      return (
                    <div className="px-4 py-3 bg-dark-800 border-b border-dark-700">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <span className="text-white font-semibold">{sym.label}</span>
                          <span className="text-dark-500 text-xs">({sym.symbol})</span>
                        </div>
                        <div className="flex items-center gap-5">
                          {inMarket.perSymbol[sym.symbol] != null && (
                            <div className="text-right">
                              <div className="text-dark-500 text-[9px] uppercase tracking-wider">% In Mkt</div>
                              <div className={`text-lg font-bold tabular-nums ${inMarket.perSymbol[sym.symbol] > 66.7 ? 'text-red-400' : 'text-teal-400'}`}>{inMarket.perSymbol[sym.symbol]}%</div>
                            </div>
                          )}
                          {idxRet != null && (
                            <div className="text-right">
                              <div className="text-dark-500 text-[9px] uppercase tracking-wider">Index</div>
                              <div className={`text-lg font-bold tabular-nums ${idxRet >= 0 ? 'text-blue-400' : 'text-blue-300'}`}>{idxRet > 0 ? '+' : ''}{idxRet}%</div>
                            </div>
                          )}
                          <div className="text-right">
                            <div className="text-dark-500 text-[9px] uppercase tracking-wider">Strategy</div>
                            <div className={`text-lg font-bold tabular-nums ${stratRet >= 0 ? 'text-green-400' : 'text-red-400'}`}>{stratRet > 0 ? '+' : ''}{stratRet}%</div>
                          </div>
                        </div>
                      </div>
                      {carriedOnly ? (
                        <div className="text-xs text-dark-400" data-testid="sa-card-carried-only">
                          No new trade in this period — the {firstCarried?.direction === 'SHORT' ? 'short' : 'long'} held since{' '}
                          <span className="text-dark-200 font-medium">{formatSingleDate(firstCarried?.orig_entry_date || firstCarried?.entry_date)}</span>{' '}
                          counts in the return above, not in trade stats.
                        </div>
                      ) : (
                      <div className="flex items-center gap-5 text-xs">
                        <span className="text-dark-400">Gross Gains: <span className="text-green-400 font-semibold">+{st.gross_gains}%</span></span>
                        <span className="text-dark-400">Gross Losses: <span className="text-red-400 font-semibold">{st.gross_losses}%</span></span>
                        <span className="text-dark-600">|</span>
                        <span className="text-dark-400">Trades: <span className="text-white font-semibold">{st.winning}W / {st.losing}L</span></span>
                        <span className="text-dark-400">WR: <span className={`font-semibold ${st.win_rate >= 50 ? 'text-green-400' : 'text-red-400'}`}>{st.win_rate}%</span></span>
                        <span className="text-dark-400">Avg Win: <span className="text-green-400 font-semibold">+{st.avg_win}%</span></span>
                        <span className="text-dark-400">Avg Loss: <span className="text-red-400 font-semibold">{st.avg_loss}%</span></span>
                        {st.best_trade != null && <span className="text-dark-400">Best: <span className={`font-semibold ${st.best_trade >= 0 ? 'text-green-400' : 'text-red-400'}`}>{st.best_trade > 0 ? '+' : ''}{st.best_trade}%</span></span>}
                        {st.worst_trade != null && <span className="text-dark-400">Worst: <span className={`font-semibold ${st.worst_trade >= 0 ? 'text-green-400' : 'text-red-400'}`}>{st.worst_trade > 0 ? '+' : ''}{st.worst_trade}%</span></span>}
                        {st.avg_mae != null && <span className="text-dark-400">MAE: <span className="text-amber-400 font-semibold">{st.avg_mae}%</span></span>}
                        {st.avg_days != null && (
                          <span className="text-dark-400">Avg Days: <span className="text-white font-semibold">{st.avg_days}</span>
                            {st.avg_days_long != null && st.avg_days_short != null && (
                              <span className="text-dark-500"> (<span className="text-emerald-400">L {st.avg_days_long}</span> / <span className="text-orange-400">S {st.avg_days_short}</span>)</span>
                            )}
                          </span>
                        )}
                      </div>
                      )}
                    </div>
                      );
                    })()}
                    {/* Trades table */}
                    {(() => {
                      const byEnd = (a, b) => (a.exit_date || a.entry_date || '').localeCompare(b.exit_date || b.entry_date || '');
                      const ofPeriod = addStreaks(sym.trades.filter(tradeIncluded).sort(byEnd)).reverse();
                      const carried = sym.trades.filter(t => heldIncluded(t) && !tradeIncluded(t)).map(t => ({ ...t, streak: null }));
                      // Analysis: the period's own CLOSED trades, grouped like All Transactions;
                      // still-open and held-over positions stay outside any period
                      const keyOf = (t) => `${t.direction || ''}|${t.trade_number}|${t.entry_date}`;
                      const pp = sigAnalysis ? computePeriods(
                        ofPeriod.filter(t => !isOpenAtEnd(t)).map(t => ({ ...t, is_open: false })),
                        (priceChart?.[sym.symbol]?.prices || []).map(p => [p.date, p.close]),
                        { target: sigTarget }) : null;
                      const periodOf = new Map();
                      (pp?.periods || []).forEach((p, i) => p.trades.forEach(t => periodOf.set(keyOf(t), i)));
                      return (
                    <>
                    {pp && pp.periods.length > 0 && <div className="px-4 pt-3"><PeriodSummary {...pp} /></div>}
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>{tableHeaders(false)}</thead>
                        <tbody>
                              <>
                                {ofPeriod.map((t, i) => {
                                  const pi = periodOf.get(keyOf(t));
                                  const startsPeriod = pi != null && (i === 0 || periodOf.get(keyOf(ofPeriod[i - 1])) !== pi);
                                  return (
                                    <React.Fragment key={`${t.trade_number}-${i}`}>
                                      {startsPeriod && <PeriodHeaderRow period={pp.periods[pi]} colSpan={9} bar={pp.bar} target={pp.target} />}
                                      <TradeRow t={t} showSymbol={false} tone={pi != null ? PERIOD_TONE[pp.periods[pi].grade] : null} />
                                    </React.Fragment>
                                  );
                                })}
                                {carried.length > 0 && (
                                  <tr data-testid="sa-carried-head">
                                    <td colSpan={9} className="pt-3 pb-1 px-2 text-[10px] uppercase tracking-wider font-semibold text-dark-500 border-t border-dashed border-dark-600">
                                      Held into this period — counted in the return, not in trade stats
                                    </td>
                                  </tr>
                                )}
                                {carried.map((t, i) => <TradeRow key={`c-${t.trade_number}-${i}`} t={t} showSymbol={false} />)}
                              </>
                        </tbody>
                      </table>
                    </div>
                    </>
                      );
                    })()}
                  </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        </>
      )}
      </>)}
    </div>
  );
};

// All Indexes regions and their index counts from the last visit — the shape of
// the first-load skeleton (per-browser convenience; a generic shape without it)
const REGIONS_KEY = 'allIndexes.regions.v1';
const DEFAULT_REGIONS = [['India', 6], ['US', 4], ['Crypto', 2]];
const rememberedRegions = () => {
  try { const r = JSON.parse(localStorage.getItem(REGIONS_KEY) || 'null'); return Array.isArray(r) && r.length ? r : DEFAULT_REGIONS; } catch { return DEFAULT_REGIONS; }
};

// Period Performance's columns from the last visit (the backend's rolling windows)
const PERIODS_KEY = 'allIndexes.periods.v1';
const DEFAULT_PERIODS = ['YTD', '1Y', '2Y', '3Y', '5Y', '10Y', '20Y'];
const rememberedPeriods = () => {
  try { const p = JSON.parse(localStorage.getItem(PERIODS_KEY) || 'null'); return Array.isArray(p) && p.length ? p : DEFAULT_PERIODS; } catch { return DEFAULT_PERIODS; }
};

// Period Performance still loading: each region with its From/To, the reading
// key, the real period columns and two-line cells (return / % in market)
const PeriodSkeleton = () => {
  const periods = rememberedPeriods();
  return (
    <div data-testid="period-skeleton">
      {rememberedRegions().map(([name, n]) => (
        <div key={name} className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white flex items-center gap-2"><span>{name}</span><Skel className="h-3.5 w-20" /></h3>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-dark-500 uppercase tracking-wider font-semibold">From</span><Skel className="h-6 w-16" />
              <span className="text-[10px] text-dark-500 uppercase tracking-wider font-semibold">To</span><Skel className="h-6 w-16" />
            </div>
          </div>
          <div className="text-xs text-dark-500 px-3 mb-3">Reading: <span className="text-green-400 font-semibold">Strategy</span> / <span className="text-dark-200">Index</span> <span className="text-dark-600 mx-1">|</span> % time in market</div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="text-dark-400">
                <th className="text-left py-3 px-3 text-xs uppercase tracking-wider font-medium">Index</th>
                {periods.map(p => (
                  <th key={p} className="py-3 px-2 text-center">
                    <div className="text-white text-base font-bold">{p}</div>
                    <div className="h-4 flex items-center justify-center"><Skel className="h-2.5 w-24" /></div>
                  </th>
                ))}
              </tr></thead>
              <tbody>
                {Array.from({ length: n }, (_, r) => (
                  <tr key={r} className="border-t border-dark-700/50">
                    <td className="py-4 px-3"><Skel className="h-3.5 w-24" /></td>
                    {periods.map(p => (
                      <td key={p} className="py-3 px-2 text-center">
                        <div className="h-5 flex items-center justify-center"><Skel className="h-3.5 w-24" /></div>
                        <div className="h-4 flex items-center justify-center"><Skel className="h-2.5 w-16" /></div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

// Region tables still loading (Apal 2026-11-03: "anything that's loading must
// give loading effect … be clear about each section"): each region's name and
// the table's real header stay; the rows shimmer. rowsPer = 2 for the Diff.
const RegionTablesSkeleton = ({ head, cols, rowsPer = 1, testId = 'regions-skeleton' }) => (
  <div className="space-y-8" data-testid={testId}>
    {rememberedRegions().map(([name, n]) => (
      <div key={name}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <span>{name}</span>
            <Skel className="h-3.5 w-20" />
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            {head}
            <tbody><SkelRows cols={cols} rows={n * rowsPer} cellClass="py-3 px-2" testId={`${testId}-rows`} /></tbody>
          </table>
        </div>
      </div>
    ))}
  </div>
);

// Yearly Stats still loading: its title, legend and filter, the year table's
// real eleven columns, rows grouped the way the years will be
// the columns follow the page's Strategy, as the real table does (Both adds L / S)
const yearlyColumns = (strategy) => ['Year', 'Trades', ...(strategy === 'with_shorts' ? ['L / S'] : []),
  'Strategy Return', 'Index Return', 'Wins', 'Losses', 'Win Rate', '% In Market', 'Avg MAE', 'Max MAE', 'Flag'];
const YearlySkeleton = ({ strategy }) => {
  const YEARLY_COLUMNS = yearlyColumns(strategy);
  return (
  <div className="space-y-4" data-testid="yearly-skeleton">
    <div className="flex items-center justify-between">
      <h3 className="text-lg font-semibold text-white">Year-by-Year Comparison</h3>
      <div className="flex items-center gap-3 text-xs">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-green-500/30 border border-green-500/50 inline-block"></span>Beat</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-500/30 border border-red-500/50 inline-block"></span>Under</span>
        <Skel className="h-3 w-16" />
      </div>
    </div>
    <div className="flex items-center space-x-1 bg-dark-700 rounded-lg p-1 w-fit">
      {['All Years', 'Critical Years', 'Best', 'Worst'].map(l => <span key={l} className="px-3 py-1.5 rounded text-xs font-medium text-dark-400">{l}</span>)}
    </div>
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead><tr className="text-dark-400 text-xs uppercase tracking-wider">
          {YEARLY_COLUMNS.map(c => <th key={c} className="text-left py-3 px-2 font-medium">{c}</th>)}
        </tr></thead>
        <tbody>
          {[2, 1, 2, 4].map((n, g) => (
            <React.Fragment key={g}>
              <tr className="bg-dark-800/60"><td colSpan={YEARLY_COLUMNS.length} className="py-2.5 px-4"><Skel className="h-4 w-36" /></td></tr>
              <SkelRows cols={YEARLY_COLUMNS.length} rows={n} cellClass="py-3.5 px-2" />
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  </div>
  );
};

// Performance still loading: the trading period, both cards with their titles.
const PerfSkeleton = () => (
    <div className="space-y-5" data-testid="perf-skeleton">
      <div className="flex items-center justify-between bg-dark-800/50 rounded-lg px-4 py-3 border border-dark-700">
        <div>
          <span className="text-dark-400 text-xs uppercase tracking-wider">Trading Period</span>
          <div className="h-6 flex items-center"><Skel className="h-3.5 w-64" /></div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
          <h4 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <TrendingUp className="w-5 h-5 text-primary-400" />
            <span>Performance Comparison</span>
          </h4>
          <TableSkeleton rows={5} columns={['Period', 'Index CAGR', 'CL CAGR']} />
        </div>
        <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
          <h4 className="text-lg font-semibold text-white mb-4">Trade Statistics</h4>
          <table className="w-full"><tbody><SkelRows cols={2} rows={9} cellClass="py-3.5 px-4" /></tbody></table>
        </div>
      </div>
    </div>
);

// the trade table's columns; Both adds Direction, as the real table does
const txColumns = (strategy) => ['Trade #', 'Version', ...(strategy === 'with_shorts' ? ['Direction'] : []),
  'Entry Date', 'Exit Date', 'Entry Price', 'Exit Price', 'Gain %', 'Streak', 'Days Held', 'MAE %'];

// the band of range figures over the trade table, by name
const TxRangeStatsSkeleton = () => (
  <div data-testid="tx-range-stats-pending" className="bg-dark-800 border border-dark-600/60 rounded-lg px-4 py-3 mb-5">
    <div className="flex items-end justify-between flex-wrap gap-y-3">
      <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
        {['Gross Gains', 'Gross Losses', 'Trades', 'Win Rate', 'Avg Win', 'Avg Loss', 'Best', 'Worst', 'MAE', 'Avg Days'].map(l => (
          <div key={l} className="min-w-[68px]">
            <div className="text-dark-500 text-[9px] uppercase tracking-wider whitespace-nowrap">{l}</div>
            <div className="h-6 flex items-center"><Skel className="h-4 w-14" /></div>
          </div>
        ))}
      </div>
      <div className="ml-auto flex items-end gap-5 pl-5 border-l border-dark-600/60">
        {['% In Mkt', 'Index', 'Strategy'].map(l => (
          <div key={l} className="text-right">
            <div className="text-dark-500 text-[9px] uppercase tracking-wider">{l}</div>
            <div className="h-7 flex items-center justify-end"><Skel className="h-5 w-20" /></div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// All Transactions before the index list: its header, its filters, the band, the table
const TxSkeleton = ({ strategy }) => (
  <div data-testid="tx-first-skeleton">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-semibold text-white">Transaction History <Skel className="h-3.5 w-24 ml-1" /></h3>
      <Skel className="h-9 w-28" rounded="rounded-lg" />
    </div>
    <div className="flex items-center justify-between flex-wrap gap-3 mb-5 pb-4 border-b border-dark-700">
      <div className="flex items-center space-x-1 bg-dark-700 rounded-lg p-1">
        {['All', 'Losses', 'Top 10 Losses', 'Consecutive Losses', 'Top 10 Profits'].map(l => <span key={l} className="px-4 py-2 rounded-md text-sm font-medium text-dark-400">{l}</span>)}
      </div>
      <Skel className="h-9 w-80" rounded="rounded-lg" />
    </div>
    <TxRangeStatsSkeleton />
    <TableSkeleton columns={txColumns(strategy)} />
  </div>
);

const INDIVIDUAL_TABS = [
  { id: 'yearly_stats', label: 'Yearly Statistics', icon: BarChart3 },
  { id: 'performance', label: 'Performance Summary', icon: TrendingUp },
  { id: 'transactions', label: 'All Transactions', icon: FileText },
];

// Individual Indexes before the index list arrives: the filter line's labels
// with their controls as shapes, then the report area as a table shape.
const IndividualSkeleton = ({ tab, onTab, strategy }) => (
  <div data-testid="individual-skeleton" className="space-y-4">
    <div className="flex items-center justify-between bg-dark-800 border border-dark-600/40 rounded-xl px-5 py-4 shadow-sm">
      <div className="flex flex-wrap items-end gap-5">
        {[['Index', 'w-48'], ['Model', 'w-40'], ['Strategy', 'w-40'], ['Costs', 'w-28'], ['Execution', 'w-56']].map(([label, w]) => (
          <div key={label}>
            <span className="text-dark-400 text-[11px] uppercase tracking-widest font-semibold mb-1.5 block">{label}</span>
            <Skel className={`h-9 ${w}`} rounded="rounded-lg" />
          </div>
        ))}
      </div>
    </div>
    <div className="flex items-center gap-3 px-3 py-2"><Skel className="h-3 w-[28rem]" /></div>
    <div className="dashboard-card">
      {/* the real sub-tabs: a click swaps the shape to that tab's */}
      <div className="flex items-center gap-0 border-b border-dark-700 -mx-6 px-6 mb-5">
        {INDIVIDUAL_TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => onTab && onTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px ${
                tab === t.id ? 'border-primary-400 text-primary-300' : 'border-transparent text-dark-500 hover:text-dark-300 hover:border-dark-600'}`}>
              <Icon className="w-3.5 h-3.5" /><span>{t.label}</span>
            </button>
          );
        })}
      </div>
      <ProcessingStatus active title="Loading report" />
      {tab === 'yearly_stats' ? <YearlySkeleton strategy={strategy} /> : tab === 'performance' ? <PerfSkeleton /> : <TxSkeleton strategy={strategy} />}
    </div>
  </div>
);

const AllIndexesTab = ({ section = 'facts', strategy, setStrategy, allIdxTab, setAllIdxTab, netCosts, setNetCosts, costConfig, setCostConfig, costSettingsOpen, setCostSettingsOpen, userHasShorts, executionMode, setExecutionMode, reportSource, setReportSource, reloadKey }) => {
  const [allData, setAllData] = useState(null);
  const [allLoading, setAllLoading] = useState(true);
  const [allGeneratedAt, setAllGeneratedAt] = useState(null);
  const [availableMarkets, setAvailableMarkets] = useState([]);
  const [periodData, setPeriodData] = useState(null);
  const [periodLoading, setPeriodLoading] = useState(false);
  const [costInfoSymbol, setCostInfoSymbol] = useState(null);
  const [regionYears, setRegionYears] = useState({}); // { INDIA: { from: '2010', to: '' } }
  // A page's own filters render INTO the top bar (portal), so every filter
  // sits on ONE line (Apal 2026-10-15: "I want one line filters at top then
  // other relevant things in their sections")
  const [pageFilterSlot, setPageFilterSlot] = useState(null);
  const getRegionYear = (code) => regionYears[code] || { from: '', to: '' };
  const setRegionYear = (code, field, value) => setRegionYears(prev => ({ ...prev, [code]: { ...getRegionYear(code), [field]: value } }));

  // Click-outside dismiss for cost popover
  useEffect(() => {
    if (!costInfoSymbol) return;
    const handler = (e) => {
      if (!e.target.closest || !e.target.closest('[data-cost-popover]')) setCostInfoSymbol(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [costInfoSymbol]);
  const [marketFilter, setMarketFilter] = useState('all');
  // Global since/to from any region that has it set (use the first one found)
  const _sinceParam = () => {
    const p = {};
    const active = Object.values(regionYears).find(r => r.from || r.to);
    if (!active) return p;
    const from = active.from ? parseInt(active.from) : null;
    const to = active.to ? parseInt(active.to) : null;
    // Validate: from must be <= to
    if (from && to && from > to) return p;
    if (from) p.since_year = String(from);
    if (to) p.to_year = String(to);
    return p;
  };
  const _allCostParam = () => {
    if (!netCosts || !['1x', 'with_shorts', 'shorts_only'].includes(strategy)) return {};
    const params = { net_costs: 'true' };
    Object.entries(costConfig).forEach(([sym, cfg]) => {
      if (cfg && typeof cfg === 'object') {
        params[`cost_${sym}`] = cfg.impact ?? 0;
        params[`carry_${sym}`] = cfg.carry_pct ?? 0;
        params[`intrate_${sym}`] = cfg.interest_rate ?? 0;
        params[`margin_${sym}`] = cfg.margin_pct ?? 20;
      }
    });
    return params;
  };
  const _allExecutionParam = () => executionMode !== 'same_day_close' ? { execution: executionMode } : {};
  const _allSourceParam = () => reportSource === 'latest' ? { source: 'latest' } : {};
  // Model: Diff — Performance Summary only. Published and Latest side by side,
  // from the server's diff mode (same prices, same last day, only the model differs)
  const [diffOn, setDiffOn] = useState(false);
  const [diffData, setDiffData] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  // only the newest request counts — a filter click cancels the one before
  const allReq = useLatestRequest();
  const periodReq = useLatestRequest();
  const showDiff = diffOn && allIdxTab === 'performance';
  // what a running update is getting — the filters in force, in their own words
  const filtersWhat = [
    marketFilter !== 'all' ? (availableMarkets.find(m => m.key === marketFilter) || {}).label : null,
    reportSource === 'latest' ? 'Latest' : null,
    strategy === 'with_shorts' ? 'Both' : strategy === 'shorts_only' ? 'Shorts' : 'Longs',
    netCosts ? 'Net' : 'Gross',
    (EXECUTION_OPTIONS.find(o => o.key === executionMode) || {}).label,
  ].filter(Boolean).join(' · ');
  // the normal table is hidden under the Diff: it isn't recomputed meanwhile
  // (a filter click was three heavy computations, not two) — leaving the Diff
  // fetches it fresh
  useEffect(() => { if (!showDiff) fetchAll(); fetchPeriod(); }, [marketFilter, strategy, netCosts, costConfig, executionMode, reportSource, reloadKey, regionYears, showDiff]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!showDiff) return;
    let cancelled = false;
    const abort = new AbortController();     // a newer filter click cancels both models' requests
    setDiffLoading(true);
    const base = { market: marketFilter, strategy, diff: '1', ..._allExecutionParam(), ..._sinceParam(), ..._allCostParam() };
    Promise.all([apiService.getAllIndexesSummary(base, { signal: abort.signal }), apiService.getAllIndexesSummary({ ...base, source: 'latest' }, { signal: abort.signal })])
      .then(([p, l]) => { if (!cancelled) setDiffData({ published: p.data?.regions || p.data, latest: l.data?.regions || l.data }); })
      .catch(() => { if (!cancelled) setDiffData(null); })
      .finally(() => { if (!cancelled) setDiffLoading(false); });
    return () => { cancelled = true; abort.abort(); };
  }, [showDiff, marketFilter, strategy, netCosts, costConfig, executionMode, reloadKey, regionYears]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    const req = allReq();
    try {
      setAllLoading(true);
      const res = await apiService.getAllIndexesSummary({ market: marketFilter, strategy, ..._allExecutionParam(), ..._sinceParam(), ..._allCostParam(), ..._allSourceParam() }, { signal: req.signal });
      if (!req.isCurrent()) return;           // a newer click owns the page now
      const regions = res.data?.regions || res.data;
      setAllData(regions);
      if (res.data?.generated_at) setAllGeneratedAt(res.data.generated_at);
      // Store available markets from the "all" fetch (don't update when filtered)
      if (marketFilter === 'all' && Array.isArray(regions)) {
        setAvailableMarkets(regions.map(r => ({ key: r.code?.toLowerCase(), label: r.region || r.name })).filter(m => m.key));
        try { localStorage.setItem(REGIONS_KEY, JSON.stringify(regions.map(r => [r.region || r.name, (r.indexes || []).length]))); } catch { /* generic shape */ }
      }
    } catch { } finally { if (req.isCurrent()) setAllLoading(false); }
  };

  const fetchPeriod = async () => {
    const req = periodReq();
    try {
      setPeriodLoading(true);
      const res = await apiService.getPeriodPerformance({ market: marketFilter, strategy, ..._allExecutionParam(), ..._sinceParam(), ..._allCostParam(), ..._allSourceParam() }, { signal: req.signal });
      if (req.isCurrent()) {
        setPeriodData(res.data);
        try { if (Array.isArray(res.data?.periods) && res.data.periods.length) localStorage.setItem(PERIODS_KEY, JSON.stringify(res.data.periods)); } catch { /* default columns */ }
      }
    } catch { } finally { if (req.isCurrent()) setPeriodLoading(false); }
  };

  // a region's From / To years — the same control on the normal table and the Diff
  const regionYearsControl = (region) => {
    const ry = getRegionYear(region.code);
    const fromVal = ry.from ? parseInt(ry.from) : null;
    const toVal = ry.to ? parseInt(ry.to) : null;
    const years = Array.from({ length: 31 }, (_, i) => 1996 + i); // 1996-2026
    return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] text-dark-500 uppercase tracking-wider font-semibold">From</span>
      <select value={ry.from} onChange={e => setRegionYear(region.code, 'from', e.target.value)}
        className={`bg-dark-700 border border-dark-600 rounded px-2 py-1 text-xs focus:outline-none cursor-pointer ${ry.from ? 'text-primary-400 font-medium' : 'text-dark-500'}`}>
        <option value="">All</option>
        {years.filter(y => !toVal || y <= toVal).map(y => <option key={y} value={y}>{y}</option>)}
      </select>
      <span className="text-[10px] text-dark-500 uppercase tracking-wider font-semibold">To</span>
      <select value={ry.to} onChange={e => setRegionYear(region.code, 'to', e.target.value)}
        className={`bg-dark-700 border border-dark-600 rounded px-2 py-1 text-xs focus:outline-none cursor-pointer ${ry.to ? 'text-primary-400 font-medium' : 'text-dark-500'}`}>
        <option value="">Now</option>
        {years.filter(y => !fromVal || y >= fromVal).map(y => <option key={y} value={y}>{y}</option>)}
      </select>
      {(ry.from || ry.to) && (
        <button onClick={() => setRegionYears(prev => { const n = { ...prev }; delete n[region.code]; return n; })} className="text-dark-500 hover:text-red-400 text-xs">✕</button>
      )}
    </div>
    );
  };

  const getPeriodCellColor = (outperf) => {
    if (outperf === null || outperf === undefined) return '';
    if (outperf >= 10) return 'bg-green-900/30 text-green-400';
    if (outperf > 0) return 'bg-emerald-900/20 text-emerald-400';
    if (outperf > -10) return 'bg-orange-900/20 text-orange-400';
    return 'bg-red-900/30 text-red-400';
  };

  // the Performance table's header — the real one, and over its skeleton
  const perfHead = (
                <thead>
                  <tr className="text-dark-400 text-xs uppercase tracking-wider">
                    <th className="text-left py-3 px-2 font-medium">Index</th>
                    <th className="text-center py-3 px-2 font-medium">Since</th>
                    <th className="text-right py-3 px-2 font-medium">Index</th>
                    <th className="text-right py-3 px-2 font-medium">MV 1X</th>
                    <th className="text-center py-3 px-2 font-medium">Trades/Yr</th>
                    <th className="text-center py-3 px-2 font-medium">Avg Hold</th>
                    <th className="text-right py-3 px-2 font-medium">% In Mkt</th>
                    <th className="text-right py-3 px-2 font-medium">Win Rate</th>
                    <th className="text-right py-3 px-2 font-medium">Avg Ret</th>
                    <th className="text-center py-3 px-2 font-medium">Gain/Loss</th>
                    <th className="text-center py-3 px-2 font-medium">Exp. Ratio</th>
                    <th className="text-right py-3 px-2 font-medium">Best</th>
                    <th className="text-right py-3 px-2 font-medium">Worst</th>
                    <th className="text-right py-3 px-2 font-medium" title="Worst run of back-to-back losing trades, compounded">Worst Streak</th>
                    <th className="text-right py-3 px-2 font-medium">MAE</th>
                  </tr>
                </thead>
  );

  return (
    <div className={`dashboard-card ${reportSource === 'latest' && !showDiff ? 'ring-2 ring-fuchsia-500/50' : ''}`}>
      {/* Sub-tabs — facts on Reports > All Indexes; decision tools on Strategy */}
      <div className="flex items-center gap-0 border-b border-dark-700 -mx-6 px-6 mb-5">
        {section === 'facts' ? (
          <>
            <button onClick={() => setAllIdxTab('performance')}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-all ${allIdxTab === 'performance' ? 'border-primary-400 text-primary-300' : 'border-transparent text-dark-500 hover:text-dark-300'}`}>
              <TrendingUp className="w-3.5 h-3.5" />Performance Summary
            </button>
            <button onClick={() => setAllIdxTab('period')}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-all ${allIdxTab === 'period' ? 'border-primary-400 text-primary-300' : 'border-transparent text-dark-500 hover:text-dark-300'}`}>
              <BarChart3 className="w-3.5 h-3.5" />Period Performance
            </button>
          </>
        ) : (
          <>
            <button onClick={() => setAllIdxTab('compare')}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-all ${allIdxTab === 'compare' ? 'border-primary-400 text-primary-300' : 'border-transparent text-dark-500 hover:text-dark-300'}`}>
              <ArrowUpRight className="w-3.5 h-3.5" />Compare Indexes
            </button>
            <button onClick={() => setAllIdxTab('signal_analysis')}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-all ${allIdxTab === 'signal_analysis' ? 'border-primary-400 text-primary-300' : 'border-transparent text-dark-500 hover:text-dark-300'}`}>
              <Activity className="w-3.5 h-3.5" />Signal Analysis
            </button>
            <button onClick={() => setAllIdxTab('best_trades')}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-all ${allIdxTab === 'best_trades' ? 'border-primary-400 text-primary-300' : 'border-transparent text-dark-500 hover:text-dark-300'}`}>
              <Trophy className="w-3.5 h-3.5" />Quarterly Wins
            </button>
            <button onClick={() => setAllIdxTab('allocation')}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-all ${allIdxTab === 'allocation' ? 'border-primary-400 text-primary-300' : 'border-transparent text-dark-500 hover:text-dark-300'}`}>
              <PieChart className="w-3.5 h-3.5" />Allocation
            </button>
          </>
        )}
        {/* Timestamp — on the tab line, so the filter bar below holds one line */}
        {allIdxTab !== 'allocation' && (allGeneratedAt || periodData?.generated_at) && (
          <span className="ml-auto pl-4 text-xs text-dark-500 whitespace-nowrap" data-testid="all-updated">
            Updated: <span className="text-dark-300 font-medium">{formatHumanTime(allIdxTab === 'period' ? periodData?.generated_at : allGeneratedAt)}</span>
          </span>
        )}
      </div>

      {/* Latest-mode banner — same preview cue as the individual index view */}
      {reportSource === 'latest' && allIdxTab !== 'allocation' && !showDiff && (
        <div className="flex flex-wrap items-center gap-3 mb-4 px-4 py-2.5 rounded-lg bg-fuchsia-500/10 border border-fuchsia-500/40">
          <div className="flex items-center gap-2 text-fuchsia-200 text-sm font-semibold">
            <span className="px-2 py-0.5 rounded bg-fuchsia-600 text-white text-[11px] uppercase tracking-wide">Latest</span>
            Current-model preview — not the published report
          </div>
          <span className="text-xs text-dark-400">Every index below uses MarketVibes' latest current-model scores.</span>
        </div>
      )}

      {/* Filters bar — hidden on Allocation: the lab has its OWN fixed
          assumptions and controls; mixing the global filters in was confusing */}
      <div className={`flex flex-wrap items-end justify-between gap-4 mb-5 ${allIdxTab === 'allocation' ? 'hidden' : ''}`} data-testid="top-filter-bar">
        <div className="flex flex-wrap items-end gap-x-3 gap-y-3">
          {/* Market (scope) — hidden on tabs with their own index selector */}
          {allIdxTab !== 'signal_analysis' && allIdxTab !== 'compare' && allIdxTab !== 'allocation' && allIdxTab !== 'best_trades' && (
            <>
              <SegmentControl label="Market" value={marketFilter} onChange={setMarketFilter}
                options={[{ key: 'all', label: 'All Markets' }, ...availableMarkets].map(m => ({ ...m, activeBg: 'bg-primary-600 text-white' }))} />
              <div className="w-px h-10 bg-dark-600/50 self-stretch" />
            </>
          )}

          {/* The 4 data-changing selections — Model / Strategy / Costs / Execution */}
          <SegmentControl label="Model" value={showDiff ? 'diff' : reportSource}
            onChange={(v) => { if (v === 'diff') { setDiffOn(true); return; } setDiffOn(false); setReportSource(v); }}
            options={[
              { key: 'published', label: 'Published', activeBg: 'bg-sky-600 text-white', title: 'Report from the stored history — what clients see today' },
              { key: 'latest', label: 'Latest', activeBg: 'bg-fuchsia-600 text-white', title: "MV's latest current-model scores — may differ from Published" },
              ...(allIdxTab === 'performance' ? [{ key: 'diff', label: 'Diff', activeBg: 'bg-gradient-to-r from-sky-600 to-fuchsia-600 text-white', title: 'Published and Latest together — how the two models behave' }] : []),
            ]} />
          {allIdxTab !== 'compare' && allIdxTab !== 'signal_analysis' && allIdxTab !== 'best_trades' && (
            <SegmentControl label="Strategy" value={strategy} onChange={setStrategy}
              options={[
                { key: '1x', label: 'Longs', activeBg: 'bg-emerald-600 text-white' },
                ...(userHasShorts ? [
                  { key: 'shorts_only', label: 'Shorts', activeBg: 'bg-orange-600 text-white' },
                  { key: 'with_shorts', label: 'Both', activeBg: 'bg-violet-600 text-white' },
                ] : []),
              ]} />
          )}
          <SegmentControl label="Costs" value={netCosts ? 'net' : 'gross'} onChange={(v) => setNetCosts(v === 'net')}
            options={[
              { key: 'gross', label: 'Gross', activeBg: 'bg-dark-500 text-white', title: 'Raw strategy returns, no costs' },
              { key: 'net', label: 'Net', activeBg: 'bg-amber-500 text-white', title: 'Apply impact + carry + interest costs to strategy returns' },
            ]}
            trailing={
              <button onClick={() => setCostSettingsOpen(true)} className="p-1.5 rounded-md text-dark-400 hover:text-amber-300 hover:bg-dark-700 transition-colors" title="Configure cost inputs">
                <Settings2 className="w-4 h-4" />
              </button>
            } />
          <SegmentControl label="Execution" value={executionMode} onChange={setExecutionMode}
            options={EXECUTION_OPTIONS} />
          <div ref={setPageFilterSlot} className="contents" data-testid="page-filter-slot" />
        </div>

      </div>

      {/* PERFORMANCE SUMMARY TAB */}
      {allIdxTab === 'performance' && (
      <div className="relative">
      {/* Non-negotiable rules bar */}
      <div className="flex items-center gap-2 flex-wrap mb-5 pb-4 border-b border-dark-700">
        <span className="text-orange-400 font-semibold text-xs uppercase tracking-wider">Non-Negotiables</span>
        {NON_NEGOTIABLE_RULES.map(rule => (
          <span key={rule} className="px-2.5 py-1 bg-dark-700 border border-dark-600 rounded-full text-xs text-dark-300">{rule}</span>
        ))}
      </div>

      {/* a filter click never looks like nothing happened (Apal 2026-11-02:
          "i clicked on net it's not changing results"): the old numbers dim
          under the shared Updating bar until BOTH models are back */}
      {(() => {
        const busy = showDiff ? diffLoading : allLoading;
        const first = showDiff ? !diffData : !allData;
        const what = [showDiff ? 'Published vs Latest' : null, filtersWhat].filter(Boolean).join(' · ');
        return allIdxTab === 'performance' && (
          <ProcessingStatus active={busy} title={first ? (showDiff ? 'Loading both models' : 'Loading results') : undefined} what={what} />
        );
      })()}
      {showDiff && (diffData
        ? <div data-testid="model-diff-wrap" className={dimClass(diffLoading)}>
            <ModelDiffTable published={diffData.published} latest={diffData.latest} regionExtra={regionYearsControl} />
          </div>
        : diffLoading && (
          <RegionTablesSkeleton cols={DIFF_COLUMNS.length + 1} rowsPer={2} testId="diff-skeleton"
            head={<thead><tr className="text-dark-400 text-xs uppercase tracking-wider">
              <th className="text-left py-3 px-2 font-medium">Index</th>
              {DIFF_COLUMNS.map(c => <th key={c.key} className="text-right py-3 px-2 font-medium">{c.label}</th>)}
            </tr></thead>} />
        ))}
      {!showDiff && !allData && allLoading && <RegionTablesSkeleton head={perfHead} cols={15} />}

      {/* Region tables */}
      <div className={`space-y-8 ${dimClass(allLoading)} ${showDiff ? 'hidden' : ''}`}>
        {allData?.map(region => (
          <div key={region.code}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <span>{region.region}</span>
                <span className="text-dark-400 font-normal text-sm">({region.indexes.length} indexes)</span>
              </h3>
              {regionYearsControl(region)}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                {perfHead}
                <tbody>
                  {region.indexes.map(idx => {
                    const hasNN = idx.non_negotiables.length > 0;
                    if (idx.has_shorts_data === false) {
                      return (
                        <tr key={idx.symbol} className="border-t border-dark-700/50 opacity-40">
                          <td className="py-3 px-2 text-dark-400 font-medium">{idx.label}</td>
                          <td colSpan={14} className="py-3 px-2 text-center text-dark-500 text-xs italic">
                            Shorts not available for this index
                          </td>
                        </tr>
                      );
                    }
                    return (
                      <React.Fragment key={idx.symbol}>
                      <tr className={`border-t border-dark-700/50 hover:bg-dark-800/50 ${hasNN ? 'bg-red-900/5' : ''}`}>
                        <td className="py-3 px-2 text-white font-medium">
                          <div className="flex items-center gap-1.5">
                            {idx.label}
                            {idx.strategy_cagr_net != null && (() => {
                              const code = idx.symbol.toUpperCase();
                              const symCfg = getSymbolCost(costConfig, code);
                              if (!hasAnyCost(symCfg)) return null;
                              const label = SYMBOL_LABELS[code] || code;
                              const days = idx.avg_days_held || 60;
                              const impactCost = symCfg.impact;
                              const pc = computeTradeCost(symCfg, days);
                              const carryCost = pc.carry;
                              const interestCost = pc.interest;
                              const totalCost = impactCost + carryCost + interestCost;
                              const isOpen = costInfoSymbol === code;
                              return (
                                <div className="relative" data-cost-popover>
                                  <button type="button"
                                    onClick={(e) => { e.stopPropagation(); setCostInfoSymbol(isOpen ? null : code); }}
                                    className={`select-none w-4 h-4 rounded-full border text-[10px] leading-none flex items-center justify-center transition-colors ${isOpen ? 'bg-amber-500 text-dark-900 border-amber-400' : 'border-amber-400/60 text-amber-300/80 hover:bg-amber-500/20'}`}
                                    title="Click to see cost breakdown"
                                  >ⓘ</button>
                                  {isOpen && (
                                    <div className="absolute left-0 top-full mt-2 w-[340px] z-30 text-left bg-dark-900 border border-amber-700/60 rounded-lg shadow-2xl p-4" data-cost-popover onClick={(e) => e.stopPropagation()}>
                                      <div className="flex items-center justify-between mb-3">
                                        <h5 className="text-sm font-bold text-amber-200">After Costs — {label}</h5>
                                        <button type="button" onClick={() => setCostInfoSymbol(null)} className="text-dark-400 hover:text-white text-xs" aria-label="Close">✕</button>
                                      </div>
                                      <p className="text-[11px] text-dark-300 mb-3 normal-case font-normal">
                                        We subtract three frictions from every completed trade's gross % gain. Numbers below use this row's <b>avg hold of {days} days</b>.
                                      </p>
                                      <table className="w-full text-[11px] normal-case font-normal mb-3">
                                        <tbody>
                                          <tr className="border-b border-dark-700/50">
                                            <td className="py-1.5 pr-2 text-dark-300"><div className="font-semibold text-white">Impact</div><div className="text-[10px] text-dark-400">slippage on entry + exit (one-time)</div></td>
                                            <td className="py-1.5 text-right text-amber-300 font-semibold">{impactCost.toFixed(2)}%</td>
                                          </tr>
                                          <tr className="border-b border-dark-700/50">
                                            <td className="py-1.5 pr-2 text-dark-300"><div className="font-semibold text-white">Carry</div><div className="text-[10px] text-dark-400">{symCfg.carry_pct}%/mo × {days}d</div></td>
                                            <td className="py-1.5 text-right text-amber-300 font-semibold">{carryCost.toFixed(2)}%</td>
                                          </tr>
                                          <tr>
                                            <td className="py-1.5 pr-2 text-dark-300"><div className="font-semibold text-white">Interest</div><div className="text-[10px] text-dark-400">{symCfg.interest_rate}% rate × {symCfg.margin_pct}% margin</div></td>
                                            <td className="py-1.5 text-right text-amber-300 font-semibold">{interestCost.toFixed(2)}%</td>
                                          </tr>
                                          <tr className="border-t-2 border-amber-700/50">
                                            <td className="py-1.5 pr-2 text-white font-bold">Total drag / trade</td>
                                            <td className="py-1.5 text-right text-amber-200 font-bold">{totalCost.toFixed(2)}%</td>
                                          </tr>
                                        </tbody>
                                      </table>
                                      <div className="bg-dark-800/70 rounded p-2 text-[11px] normal-case font-normal">
                                        <div className="flex justify-between"><span className="text-dark-300">Gross CAGR</span><span className="text-white font-semibold">{idx.strategy_cagr_gross?.toFixed(2) || idx.strategy_cagr?.toFixed(2)}%</span></div>
                                        <div className="flex justify-between mt-1"><span className="text-dark-300">After-Cost CAGR</span><span className="text-amber-300 font-semibold">{idx.strategy_cagr?.toFixed(2)}%</span></div>
                                        <div className="flex justify-between mt-1"><span className="text-dark-300">Index CAGR</span><span className="text-blue-400 font-semibold">{idx.index_cagr?.toFixed(2)}%</span></div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </td>
                        <td className="py-3 px-2 text-center text-dark-300">{idx.since}</td>
                        <td className="py-3 px-2 text-right text-blue-400 font-semibold">{idx.index_cagr?.toFixed(2)}%</td>
                        <td className={`py-3 px-2 text-right font-bold ${idx.strategy_cagr > idx.index_cagr ? 'text-green-400' : 'text-red-400'}`}>
                          {idx.strategy_cagr?.toFixed(2)}%
                        </td>
                        <td className="py-3 px-2 text-center text-dark-300">{idx.trades_per_year}</td>
                        <td className="py-3 px-2 text-center text-dark-300">{idx.avg_days_held}d</td>
                        <td className={`py-3 px-2 text-right font-medium ${idx.time_in_market_pct > 66.7 ? 'text-red-400 bg-red-900/30 rounded' : 'text-teal-400'}`}>{idx.time_in_market_pct}%</td>
                        <td className={`py-3 px-2 text-right font-semibold ${idx.win_rate >= 50 ? 'text-green-400' : 'text-red-400'}`}>{idx.win_rate}%</td>
                        <td className="py-3 px-2 text-right text-dark-300">{idx.avg_return}%</td>
                        <td className="py-3 px-2 text-center">
                          <div className="text-xs">
                            <span className="text-green-400">{idx.avg_gain}%</span>
                            <span className="text-dark-500"> / </span>
                            <span className="text-red-400">{idx.avg_loss}%</span>
                          </div>
                          <div className="text-yellow-400 text-xs font-semibold">{idx.win_loss_ratio}x</div>
                        </td>
                        <td className={`py-3 px-2 text-center font-semibold ${idx.exp_ratio >= 1.0 ? 'text-white' : 'text-red-400'}`}>{idx.exp_ratio?.toFixed(2)}</td>
                        <td className="py-3 px-2 text-right text-green-400 font-semibold">{idx.best_gain}%</td>
                        <td className="py-3 px-2 text-right">
                          <div className="text-red-400 font-semibold">{idx.worst_loss}%</div>
                          {idx.worst_loss_date && <div className="text-dark-500 text-[10px]">{formatDate(idx.worst_loss_date)}</div>}
                        </td>
                        <td className="py-3 px-2 text-right" title={idx.worst_cons_loss_date ? formatDate(idx.worst_cons_loss_date) : undefined}>
                          {idx.worst_cons_loss != null ? (
                            <>
                              <div className="text-red-400 font-semibold">{idx.worst_cons_loss}%</div>
                              <div className="text-dark-500 text-[10px]">{idx.worst_cons_loss_trades} loss{idx.worst_cons_loss_trades === 1 ? '' : 'es'} in a row</div>
                            </>
                          ) : <span className="text-dark-500">—</span>}
                        </td>
                        <td className="py-3 px-2 text-right">
                          <div className="text-xs">
                            <span className="text-orange-400">{idx.avg_mae}%</span>
                            <span className="text-dark-500"> / </span>
                            <span className="text-orange-400">{idx.max_mae}%</span>
                          </div>
                        </td>
                      </tr>
                      {idx.shorts_stats && (
                        <tr className="border-t border-dark-700/30 bg-orange-900/5">
                          <td className="py-2 px-2 text-orange-400 text-xs font-semibold">↳ SHORTS ({idx.shorts_stats.total_trades})</td>
                          <td></td>
                          <td></td>
                          <td></td>
                          <td className="py-2 px-2 text-center text-dark-400 text-xs">{idx.shorts_stats.trades_per_year}</td>
                          <td className="py-2 px-2 text-center text-dark-400 text-xs">{idx.shorts_stats.avg_days_held}d</td>
                          <td className="py-2 px-2 text-right text-dark-400 text-xs">{idx.shorts_stats.time_in_market_pct}%</td>
                          <td className={`py-2 px-2 text-right text-xs font-semibold ${idx.shorts_stats.win_rate >= 50 ? 'text-green-400' : 'text-red-400'}`}>{idx.shorts_stats.win_rate}%</td>
                          <td className="py-2 px-2 text-right text-dark-400 text-xs">{idx.shorts_stats.avg_return}%</td>
                          <td className="py-2 px-2 text-center text-xs">
                            <span className="text-green-400">{idx.shorts_stats.avg_gain}%</span>
                            <span className="text-dark-500"> / </span>
                            <span className="text-red-400">{idx.shorts_stats.avg_loss}%</span>
                          </td>
                          <td className="py-2 px-2 text-center text-dark-400 text-xs">{idx.shorts_stats.exp_ratio?.toFixed(2)}</td>
                          <td className="py-2 px-2 text-right text-green-400 text-xs">{idx.shorts_stats.best_gain}%</td>
                          <td className="py-2 px-2 text-right text-red-400 text-xs">{idx.shorts_stats.worst_loss}%</td>
                          <td className="py-2 px-2 text-right text-xs" title={idx.shorts_stats.worst_cons_loss_date ? formatDate(idx.shorts_stats.worst_cons_loss_date) : undefined}>
                            {idx.shorts_stats.worst_cons_loss != null ? (
                              <>
                                <span className="text-red-400">{idx.shorts_stats.worst_cons_loss}%</span>
                                <span className="text-dark-500 text-[10px]"> ({idx.shorts_stats.worst_cons_loss_trades})</span>
                              </>
                            ) : <span className="text-dark-500">—</span>}
                          </td>
                          <td className="py-2 px-2 text-right text-xs">
                            <span className="text-orange-400">{idx.shorts_stats.avg_mae}%</span>
                            <span className="text-dark-500"> / </span>
                            <span className="text-orange-400">{idx.shorts_stats.max_mae}%</span>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
      </div>
      )}

      {/* PERIOD PERFORMANCE TAB */}
      {allIdxTab === 'period' && (<>
      <ProcessingStatus active={periodLoading} title={periodData ? undefined : 'Loading results'} what={filtersWhat} />
      <div className={dimClass(periodLoading && !!periodData)}>
        {/* Header + Legend */}
        <div className="bg-primary-900/20 border border-primary-700/30 rounded-lg px-4 py-3 mb-4">
          <p className="text-primary-300 font-semibold">
            Period Performance — ClarityBox {strategy === '1x' ? 'Long Only' : 'With Shorts'} vs Buy & Hold
            {periodData && <span className="text-dark-400 font-normal ml-2">— {periodData.total_indexes} indexes · rolling returns</span>}
          </p>
        </div>
        <div className="flex items-center gap-3 mb-5 text-xs">
          <span className="text-dark-400">Cell color (MV vs Index):</span>
          <span className="px-2 py-0.5 bg-green-900/30 text-green-400 rounded">Beats by ≥10%</span>
          <span className="px-2 py-0.5 bg-emerald-900/20 text-emerald-400 rounded">Beats</span>
          <span className="px-2 py-0.5 bg-orange-900/20 text-orange-400 rounded">Lags</span>
          <span className="px-2 py-0.5 bg-red-900/30 text-red-400 rounded">Lags by ≥10%</span>
        </div>

        {/* Region tables */}
        {!periodData && periodLoading && <PeriodSkeleton />}
        {periodData?.regions?.map(region => (
          <div key={region.code} className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <span>{region.region}</span>
                <span className="text-dark-400 font-normal text-sm">({region.indexes.length} indexes)</span>
              </h3>
              {(() => {
                const ry = getRegionYear(region.code);
                const fromVal = ry.from ? parseInt(ry.from) : null;
                const toVal = ry.to ? parseInt(ry.to) : null;
                const years = Array.from({ length: 31 }, (_, i) => 1996 + i); // 1996-2026
                return (
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-dark-500 uppercase tracking-wider font-semibold">From</span>
                  <select value={ry.from} onChange={e => setRegionYear(region.code, 'from', e.target.value)}
                    className={`bg-dark-700 border border-dark-600 rounded px-2 py-1 text-xs focus:outline-none cursor-pointer ${ry.from ? 'text-primary-400 font-medium' : 'text-dark-500'}`}>
                    <option value="">All</option>
                    {years.filter(y => !toVal || y <= toVal).map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                  <span className="text-[10px] text-dark-500 uppercase tracking-wider font-semibold">To</span>
                  <select value={ry.to} onChange={e => setRegionYear(region.code, 'to', e.target.value)}
                    className={`bg-dark-700 border border-dark-600 rounded px-2 py-1 text-xs focus:outline-none cursor-pointer ${ry.to ? 'text-primary-400 font-medium' : 'text-dark-500'}`}>
                    <option value="">Now</option>
                    {years.filter(y => !fromVal || y >= fromVal).map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                  {(ry.from || ry.to) && (
                    <button onClick={() => setRegionYears(prev => { const n = { ...prev }; delete n[region.code]; return n; })} className="text-dark-500 hover:text-red-400 text-xs">✕</button>
                  )}
                </div>
                );
              })()}
            </div>
            <div className="flex items-center gap-1 mb-3 text-xs bg-dark-800/50 rounded-lg px-3 py-2 w-fit">
              <span className="text-dark-500 mr-1">Reading:</span>
              <span className="font-bold text-green-400">Strategy</span>
              <span className="text-dark-600 mx-0.5">/</span>
              <span className="text-dark-300">Index</span>
              <span className="text-dark-600 mx-1">|</span>
              <span className="text-dark-400">% time in market</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="text-left py-3 px-3 text-dark-400 text-xs font-medium w-[140px]">INDEX</th>
                    {periodData.periods.map(p => {
                      const now = new Date();
                      const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                      let fromLabel = '', toLabel = '';
                      if (p === 'YTD') {
                        fromLabel = `Jan 1, ${now.getFullYear()}`;
                        toLabel = fmt(now);
                      } else {
                        const years = parseInt(p);
                        if (!isNaN(years)) {
                          const from = new Date(now);
                          from.setFullYear(from.getFullYear() - years);
                          fromLabel = fmt(from);
                          toLabel = fmt(now);
                        }
                      }
                      return (
                        <th key={p} className="text-center py-3 px-2 border-b border-dark-700">
                          <div className="text-white font-bold text-base">{p}</div>
                          {fromLabel && <div className="text-dark-400 text-xs font-normal mt-1">{fromLabel} → Today</div>}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {region.indexes.map(idx => (
                    idx.has_shorts_data === false ? (
                      <tr key={idx.symbol} className="border-t border-dark-700/50 opacity-40">
                        <td className="py-3 px-3 text-dark-400 font-medium">{idx.label}</td>
                        <td colSpan={periodData.periods.length} className="py-3 px-2 text-center text-dark-500 text-xs italic">Shorts not available for this index</td>
                      </tr>
                    ) : <tr key={idx.symbol} className="border-t border-dark-700/50 hover:bg-dark-800/50">
                      <td className="py-3 px-3 w-[140px]">
                        <div className="text-white font-semibold text-sm">{idx.label}</div>
                      </td>
                      {periodData.periods.map(p => {
                        const r = idx.returns[p];
                        // Window longer than this index's history: say so plainly
                        // instead of a bare dash (or worse, a since-inception
                        // number dressed as a 10/20-year return).
                        if (r?.insufficient_history) {
                          return (
                            <td key={p} className="py-3 px-2 text-center" title={`This index's data starts ${r.data_since} — a ${p} return can't be computed.`}>
                              <div className="text-dark-600">—</div>
                              <div className="text-dark-500 text-[10px] mt-0.5">data since {r.data_since ? r.data_since.slice(0, 4) : '?'}</div>
                            </td>
                          );
                        }
                        // != null catches BOTH null and undefined — every numeric
                        // field from this endpoint can legitimately be absent.
                        if (!r || r.strategy_return == null) {
                          return <td key={p} className="py-3 px-2 text-center text-dark-600">—</td>;
                        }
                        return (
                          <td key={p} className={`py-4 px-3 text-center rounded-sm ${getPeriodCellColor(r.outperf)}`}>
                            <div className="font-bold text-base leading-tight">
                              <span>{r.strategy_return > 0 ? '+' : ''}{r.strategy_return.toFixed(1)}%</span>
                              <span className="text-dark-400 font-normal text-xs mx-1">/</span>
                              <span className="text-dark-400 font-normal text-sm">{r.index_return != null ? `${r.index_return > 0 ? '+' : ''}${r.index_return.toFixed(1)}%` : '—'}</span>
                            </div>
                            {r.in_market_pct != null && (
                              <div className="text-dark-400 text-xs mt-1">{r.in_market_pct}% in market</div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        {!periodData && !periodLoading && (
          <div className="text-center py-12 text-dark-400">No period performance data. Run: python manage.py generate_reports</div>
        )}
      </div></>
      )}

      {/* COMPARE INDEXES TAB */}
      {allIdxTab === 'compare' && <CompareIndexes filterSlot={pageFilterSlot} strategy={strategy} netCosts={netCosts} costConfig={costConfig} executionMode={executionMode} reportSource={reportSource} reloadKey={reloadKey} />}

      {/* SIGNAL ANALYSIS TAB */}
      {allIdxTab === 'signal_analysis' && <SignalAnalysis filterSlot={pageFilterSlot} strategy={strategy} executionMode={executionMode} netCosts={netCosts} costConfig={costConfig} reportSource={reportSource} reloadKey={reloadKey} userHasShorts={userHasShorts} />}
      {allIdxTab === 'best_trades' && <BestTrades filterSlot={pageFilterSlot} strategy={strategy} executionMode={executionMode} netCosts={netCosts} costConfig={costConfig} reportSource={reportSource} reloadKey={reloadKey} />}
      {allIdxTab === 'allocation' && <AllocationLab reloadKey={reloadKey} />}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════
// Main Reports Component
// ═══════════════════════════════════════════════════════════════
const TOP_TAB_SLUGS = { 'individual': 'individual', 'all-indexes': 'all_indexes' };
const TOP_SLUG_FROM_ID = { 'individual': 'individual', 'all_indexes': 'all-indexes' };
const SUB_TAB_SLUGS = {
  'transactions': 'transactions', 'yearly-stats': 'yearly_stats', 'performance': 'performance', 'period': 'period', 'compare': 'compare', 'signal-analysis': 'signal_analysis', 'allocation': 'allocation', 'quarterly-wins': 'best_trades',
  'best-trades': 'best_trades',   // the tab's old address (renamed "Quarterly Wins" 2026-10-07) — redirected below
};
const SUB_SLUG_FROM_ID = {
  'transactions': 'transactions', 'yearly_stats': 'yearly-stats', 'performance': 'performance', 'period': 'period', 'compare': 'compare', 'signal_analysis': 'signal-analysis', 'allocation': 'allocation', 'best_trades': 'quarterly-wins',
};
// Strategy is its own left-nav page (/strategy/*) but shares this component:
// same global filters (Model / Strategy / Costs / Execution), same machinery.
const STRATEGY_TABS = new Set(['compare', 'signal_analysis', 'best_trades', 'allocation']);

const Reports = () => {
  const { topTab: topSlug, subTab: subSlug } = useParams();
  const location = useLocation();
  const isStrategy = location.pathname.startsWith('/strategy');
  // On /strategy/:subTab the single param arrives as topSlug — treat it as the sub
  const strategySub = isStrategy ? (topSlug || subSlug) : null;
  const navigate = useNavigate();

  const topTab = isStrategy ? 'strategy' : ((topSlug && TOP_TAB_SLUGS[topSlug]) || 'individual');
  const reportTab = (subSlug && SUB_TAB_SLUGS[subSlug]) || (topTab === 'all_indexes' ? 'performance' : 'transactions');

  const setTopTab = (id) => {
    const defaultSub = id === 'all_indexes' ? 'performance' : 'transactions';
    navigate(`/reports/${TOP_SLUG_FROM_ID[id]}/${SUB_SLUG_FROM_ID[defaultSub]}`, { replace: true });
  };
  const setReportTab = (id) => {
    navigate(`/reports/${TOP_SLUG_FROM_ID[topTab]}/${SUB_SLUG_FROM_ID[id]}`, { replace: true });
  };

  // allIdxTab drives the section sub-tab: facts (performance/period) on
  // /reports/all-indexes, decision tools on /strategy
  const allIdxTab = isStrategy
    ? ((strategySub && SUB_TAB_SLUGS[strategySub]) || 'compare')
    : ((topTab === 'all_indexes' && subSlug && SUB_TAB_SLUGS[subSlug]) || 'performance');
  // Old /strategy/best-trades links land on the renamed tab's own address
  useEffect(() => {
    if (isStrategy && strategySub === 'best-trades') navigate('/strategy/quarterly-wins', { replace: true });
  }, [isStrategy, strategySub]); // eslint-disable-line react-hooks/exhaustive-deps

  const setAllIdxTab = (id) => {
    if (STRATEGY_TABS.has(id)) navigate(`/strategy/${SUB_SLUG_FROM_ID[id]}`, { replace: true });
    else navigate(`/reports/all-indexes/${SUB_SLUG_FROM_ID[id]}`, { replace: true });
  };

  const [regions, setRegions] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [txLoading, setTxLoading] = useState(false);
  const [strategy, setStrategyState] = useState(() => {
    const saved = localStorage.getItem('reports.strategy');
    return ['with_shorts', 'shorts_only'].includes(saved) ? saved : '1x';
  });
  const setStrategy = (s) => { setStrategyState(s); localStorage.setItem('reports.strategy', s); };
  const [yearlyData, setYearlyData] = useState(null);
  const [yearlyLoading, setYearlyLoading] = useState(false);
  const [perfFilter, setPerfFilter] = useState('critical');
  const [perfData, setPerfData] = useState(null);
  const [perfLoading, setPerfLoading] = useState(false);
  const [yearlySortOrder, setYearlySortOrder] = useState('desc');
  const [filterType, setFilterType] = useState('all');
  // Analysis switch: the same trade sheet, read as Exceptional / Good / Average /
  // Poor periods (Apal 2026-10-17). Off by default.
  const [txAnalysis, setTxAnalysisState] = useState(() => {
    try { return localStorage.getItem('reports.txAnalysis') === '1'; } catch { return false; }
  });
  // remembered across refreshes (Apal 2026-10-17: "on refresh it must remember if toggle was selected")
  const setTxAnalysis = (on) => {
    setTxAnalysisState(on);
    try { localStorage.setItem('reports.txAnalysis', on ? '1' : '0'); } catch { /* storage off: still works this session */ }
  };
  // Group by: null = the index's own pace; a number = a target % a year.
  // One key for All Transactions and Signal Analysis — a fund manager has one target.
  const [txTarget, setTxTargetState] = useState(() => {
    try { return parseTarget(localStorage.getItem('reports.groupTarget')); } catch { return null; }
  });
  const setTxTarget = (v) => {
    setTxTargetState(v);
    try { localStorage.setItem('reports.groupTarget', v == null ? 'index' : String(v)); } catch { /* storage off */ }
  };
  const [selectedYear, setSelectedYear] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [netCosts, setNetCosts] = useState(() => {
    try { return JSON.parse(localStorage.getItem('reports.netCosts')) || false; } catch { return false; }
  });
  const [costConfig, setCostConfig] = useState(() => {
    try { return { ...DEFAULT_COST_CONFIG, ...JSON.parse(localStorage.getItem('reports.costConfig')) }; } catch { return { ...DEFAULT_COST_CONFIG }; }
  });
  const [costSettingsOpen, setCostSettingsOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [yearPopup, setYearPopup] = useState(null); // clicked yearly-stats row object (has strategy_return) or null
  // Next open is the go-to (Apal 2026-11-01: "next day open should be our
  // go-to … keep the same thing everywhere"). .v2: every browser starts there
  // once — the old key was saved on every visit, chosen or not.
  const [executionMode, setExecutionMode] = useState(() => {
    try { return localStorage.getItem('reports.executionMode.v2') || 'next_day_open'; } catch { return 'next_day_open'; }
  });
  // Published (DB history) vs Latest (MV's pushed current-model scores). Composes
  // with strategy/costs/execution — it only swaps the score source fed to the generator.
  const [reportSource, setReportSourceState] = useState(() => {
    try { return localStorage.getItem('reports.source') === 'latest' ? 'latest' : 'published'; } catch { return 'published'; }
  });
  const setReportSource = (s) => { setReportSourceState(s); localStorage.setItem('reports.source', s); };
  // Published vs Latest comparison for the selected symbol ("did the model change").
  const [modelDiff, setModelDiff] = useState(null);
  const [modelDiffLoading, setModelDiffLoading] = useState(false);
  const [userHasShorts, setUserHasShorts] = useState(false);
  const [userShortsSymbols, setUserShortsSymbols] = useState([]);
  useEffect(() => { localStorage.setItem('reports.netCosts', JSON.stringify(netCosts)); }, [netCosts]);
  useEffect(() => { localStorage.setItem('reports.executionMode.v2', executionMode); }, [executionMode]);
  // what a running update is getting — the filters in force, in their own words
  const indivWhat = [
    selectedSymbol?.label || selectedSymbol?.name,
    reportSource === 'latest' ? 'Latest' : null,
    strategy === 'with_shorts' ? 'Both' : strategy === 'shorts_only' ? 'Shorts' : 'Longs',
    netCosts ? 'Net' : 'Gross',
    (EXECUTION_OPTIONS.find(o => o.key === executionMode) || {}).label,
  ].filter(Boolean).join(' · ');

  // Fetch shorts permissions from server (not localStorage)
  useEffect(() => {
    const fetchShortsPerms = async () => {
      try {
        const token = localStorage.getItem('adminToken');
        if (!token) return;
        const res = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/analytics/me/`, {
          headers: { 'Authorization': `Basic ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const hasShorts = data.has_shorts || false;
          setUserHasShorts(hasShorts);
          setUserShortsSymbols(data.shorts_symbols || []);
          if (!hasShorts && ['with_shorts', 'shorts_only'].includes(localStorage.getItem('reports.strategy'))) {
            setStrategy('1x');
          }
        }
      } catch {}
    };
    fetchShortsPerms();
  }, []);

  // Redirect bare /reports to /reports/individual/transactions; bare
  // /strategy to /strategy/compare; and the MOVED tabs (compare, signal
  // analysis) from their old /reports/all-indexes/ homes to /strategy.
  useEffect(() => {
    if (isStrategy) {
      if (!strategySub) navigate('/strategy/compare', { replace: true });
      return;
    }
    if (topSlug === 'all-indexes' && (subSlug === 'compare' || subSlug === 'signal-analysis')) {
      navigate(`/strategy/${subSlug}`, { replace: true });
      return;
    }
    if (!topSlug) navigate('/reports/individual/transactions', { replace: true });
    else if (!subSlug) {
      const defaultSub = topTab === 'all_indexes' ? 'performance' : 'transactions';
      navigate(`/reports/${topSlug}/${SUB_SLUG_FROM_ID[defaultSub]}`, { replace: true });
    }
  }, [topSlug, subSlug, topTab, navigate, isStrategy, strategySub]);

  useEffect(() => { fetchRegions(); }, []);
  useEffect(() => {
    if (selectedSymbol) {
      // Filters deliberately survive an index switch (Apal: "if I change
      // index don't reset all filters") — studying the same window across
      // indexes is the whole point of the range card.
      fetchTransactions();
      fetchYearlyStats();
      fetchPerfSummary();
    }
  }, [selectedSymbol, strategy, netCosts, costConfig, executionMode, reportSource, reloadKey]);

  // Compare Published vs Latest for the selected symbol (independent of which is shown).
  useEffect(() => {
    if (!selectedSymbol) { setModelDiff(null); return; }
    let cancelled = false;
    const abort = new AbortController();
    // the comparison is always both books at next-day open (the backend
    // fixes it), so the page's Execution toggle doesn't refetch it
    setModelDiff(null);
    setModelDiffLoading(true);
    apiService.getModelDiff({ symbol: selectedSymbol.name, strategy }, { signal: abort.signal })
      .then(res => { if (!cancelled) setModelDiff(res.data); })
      .catch(() => { if (!cancelled) setModelDiff(null); })
      .finally(() => { if (!cancelled) setModelDiffLoading(false); });
    return () => { cancelled = true; abort.abort(); };
  }, [selectedSymbol, strategy, reloadKey]);

  const _sourceParam = () => reportSource === 'latest' ? { source: 'latest' } : {};

  const _costParam = () => {
    if (!netCosts || !['1x', 'with_shorts', 'shorts_only'].includes(strategy)) return {};
    const params = { net_costs: 'true' };
    Object.entries(costConfig).forEach(([sym, cfg]) => {
      if (cfg && typeof cfg === 'object') {
        params[`cost_${sym}`] = cfg.impact ?? 0;
        params[`carry_${sym}`] = cfg.carry_pct ?? 0;
        params[`intrate_${sym}`] = cfg.interest_rate ?? 0;
        params[`margin_${sym}`] = cfg.margin_pct ?? 20;
      }
    });
    return params;
  };

  const _executionParam = () => executionMode !== 'same_day_close' ? { execution: executionMode } : {};
  // only the newest request counts — a filter click cancels the one before
  const yearlyReq = useLatestRequest();
  const perfReq = useLatestRequest();
  const txReq = useLatestRequest();

  const fetchYearlyStats = async () => {
    if (!selectedSymbol) return;
    const req = yearlyReq();
    try { setYearlyLoading(true); const res = await apiService.getYearlyStatistics({ symbol: selectedSymbol.name, strategy, ..._executionParam(), ..._costParam(), ..._sourceParam() }, { signal: req.signal }); if (req.isCurrent()) setYearlyData(res.data); }
    catch (e) { if (req.isCurrent() && !yearlyData) setYearlyData(null); }
    finally { if (req.isCurrent()) setYearlyLoading(false); }
  };

  const fetchPerfSummary = async () => {
    if (!selectedSymbol) return;
    const req = perfReq();
    try { setPerfLoading(true); const res = await apiService.getPerformanceSummary({ symbol: selectedSymbol.name, strategy, ..._executionParam(), ..._costParam(), ..._sourceParam() }, { signal: req.signal }); if (req.isCurrent()) setPerfData(res.data); }
    catch (e) { if (req.isCurrent() && !perfData) setPerfData(null); }
    finally { if (req.isCurrent()) setPerfLoading(false); }
  };

  const fetchRegions = async () => {
    try {
      setLoading(true);
      const res = await apiService.getRegions();
      setRegions(res.data);
      if (res.data.length > 0 && res.data[0].symbols?.length > 0) {
        const saved = localStorage.getItem('reportsSymbol');
        let found = null;
        if (saved) {
          for (const r of res.data) {
            found = r.symbols.find(s => s.name === saved);
            if (found) break;
          }
        }
        setSelectedSymbol(found || res.data[0].symbols[0]);
      }
    } catch {} finally { setLoading(false); }
  };

  const fetchTransactions = async () => {
    if (!selectedSymbol) return;
    const req = txReq();
    try {
      setTxLoading(true);
      const res = await apiService.getAllTransactions({ symbol: selectedSymbol.name, strategy, ..._executionParam(), ..._costParam(), ..._sourceParam() }, { signal: req.signal });
      if (req.isCurrent()) setData(res.data);
    } catch (e) {
      if (!req.isCurrent() || isCanceled(e)) return;    // replaced by a newer click — not an error
      const errMsg = e.response?.data?.error || 'Failed to load transactions';
      setData({ error: errMsg, transactions: [], total_trades: 0 });
    } finally { if (req.isCurrent()) setTxLoading(false); }
  };

  const handleSymbolChange = (e) => {
    const [mkt, sym] = e.target.value.split(':');
    for (const r of regions) {
      const found = r.symbols.find(s => s.name === sym && s.market_name === mkt);
      if (found) { setSelectedSymbol(found); localStorage.setItem('reportsSymbol', found.name); break; }
    }
  };

  const availableYears = useMemo(() => {
    if (!data?.transactions) return [];
    const years = new Set();
    data.transactions.forEach(t => { if (t.date) years.add(t.date.substring(0, 4)); });
    return [...years].sort().reverse();
  }, [data]);

  // Window bounds from the Year / date-range filters (entry-date attribution,
  // the Signal Analysis rule — the two pages must agree on any custom range).
  const txWindow = useMemo(() => {
    if (selectedYear) return { from: `${selectedYear}-01-01`, to: `${selectedYear}-12-31` };
    if (startDate || endDate) return { from: startDate || null, to: endDate || null };
    return { from: null, to: null };
  }, [selectedYear, startDate, endDate]);

  // Pairs come from the FULL history first, THEN get windowed. Building pairs
  // from date-filtered rows was the fake-open bug: a trade exiting after the
  // window lost its exit row and rendered as an open position.
  const fullPairs = useMemo(() => buildPairs(data?.transactions, data?.open_trade), [data]);
  const allPairs = useMemo(() => pairsInWindow(fullPairs, txWindow.from, txWindow.to), [fullPairs, txWindow]);
  // Periods over the whole history (a period doesn't change because a filter hides some of its trades)
  const txPeriods = useMemo(() => computePeriods(fullPairs, data?.price_series, { target: txTarget }), [fullPairs, data, txTarget]);
  const periodOfTrade = useMemo(() => {
    const m = new Map();
    txPeriods.periods.forEach((p, i) => p.trades.forEach(t => m.set(t.trade_number, i)));
    return m;
  }, [txPeriods]);

  // Range stat card — same math as the Signal Analysis per-index card, computed
  // from the exact trades the table below renders (open trade valued at its
  // unrealized gain, like Signal's Include Open).

  const filteredPairs = useMemo(() => {
    if (filterType === 'losses') return allPairs.filter(t => t.gain !== null && t.gain < 0);
    if (filterType === 'top10losses') return [...allPairs].filter(t => t.gain !== null && t.gain < 0).sort((a, b) => a.gain - b.gain).slice(0, 10);
    if (filterType === 'top10profits') return [...allPairs].filter(t => t.gain !== null && t.gain > 0).sort((a, b) => b.gain - a.gain).slice(0, 10);
    if (filterType === 'consecutive_losses') {
      const result = []; let streak = [];
      for (const t of allPairs) { if (t.gain !== null && t.gain < 0) { streak.push(t); } else { if (streak.length >= 2) result.push(...streak); streak = []; } }
      if (streak.length >= 2) result.push(...streak);
      return result;
    }
    return allPairs;
  }, [allPairs, filterType]);

  // Streak calculation — matches MarketVibes exactly
  // First loss/gain in a streak seeds the multiplier but shows null (dash)
  // Second+ compounds and shows the running total
  const tradesChronological = useMemo(() => {
    let streakMultiplier = 1;
    let inLossStreak = false;
    let gainStreakMultiplier = 1;
    let inGainStreak = false;

    // Must compute on chronological order (allPairs), not filteredPairs
    return filteredPairs.map((trade, idx) => {
      const isLoss = trade.gain !== null && trade.gain !== undefined && trade.gain < 0;
      const isGain = trade.gain !== null && trade.gain !== undefined && trade.gain > 0;
      const prevTrade = idx > 0 ? filteredPairs[idx - 1] : null;
      const prevWasLoss = prevTrade && prevTrade.gain !== null && prevTrade.gain < 0;
      const prevWasGain = prevTrade && prevTrade.gain !== null && prevTrade.gain > 0;

      let lossStreak = null;
      let gainStreak = null;

      // Track loss streaks
      if (isLoss) {
        if (prevWasLoss) {
          streakMultiplier = streakMultiplier * (1 + trade.gain / 100);
          inLossStreak = true;
        } else {
          streakMultiplier = (1 + trade.gain / 100);
          inLossStreak = false;
        }
        lossStreak = inLossStreak ? (streakMultiplier - 1) * 100 : null;
        gainStreakMultiplier = 1;
        inGainStreak = false;
      } else {
        streakMultiplier = 1;
        inLossStreak = false;
      }

      // Track gain streaks
      if (isGain) {
        if (prevWasGain) {
          gainStreakMultiplier = gainStreakMultiplier * (1 + trade.gain / 100);
          inGainStreak = true;
        } else {
          gainStreakMultiplier = (1 + trade.gain / 100);
          inGainStreak = false;
        }
        gainStreak = inGainStreak ? (gainStreakMultiplier - 1) * 100 : null;
      } else if (!isLoss) {
        gainStreakMultiplier = 1;
        inGainStreak = false;
      }

      return { ...trade, lossStreak, gainStreak };
    });
  }, [filteredPairs]);

  const trades = useMemo(() => sortOrder === 'desc' ? [...tradesChronological].reverse() : tradesChronological, [tradesChronological, sortOrder]);

  // Build streak groups from ALL pairs (not filtered), so each streak is separate
  const lossStreakGroups = useMemo(() => {
    if (filterType !== 'consecutive_losses') return null;
    return orderStreakGroups(buildLossStreakGroups(allPairs), sortOrder);
  }, [allPairs, filterType, sortOrder]);

  const hideStreakColumn = filterType === 'losses' || filterType === 'top10losses' || filterType === 'top10profits' || filterType === 'consecutive_losses';
  const hasMAE = trades.some(t => t.mae != null);

  const completedTrades = allPairs.filter(t => !t.is_open).length;
  const filteredTradeCount = filteredPairs.filter(t => !t.is_open).length;

  // Range stat card inputs — from the exact pairs the table renders
  const rangeCounted = useMemo(() => countedTrades(filteredPairs, data?.open_trade), [filteredPairs, data]);
  const rangeStats = useMemo(() => rangeCounted.comp.length ? computeSymbolStats(rangeCounted.comp) : null, [rangeCounted]);
  const rangeIndexRet = useMemo(() => indexReturnPct(sliceSeries(data?.price_series, txWindow.from, txWindow.to), basisBefore(data?.price_series, txWindow.from)), [data, txWindow]);
  const rangeBounds = useMemo(() => seriesWindow(data?.price_series, txWindow.from, txWindow.to), [data, txWindow]);
  const rangeInMkt = useMemo(() => computeInMarketPct(rangeCounted.comp, rangeBounds.from, rangeBounds.to), [rangeCounted, rangeBounds]);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center space-x-3">
          {isStrategy ? <Target className="w-8 h-8 text-primary-400" /> : <FileText className="w-8 h-8 text-primary-400" />}
          <h1 className="text-3xl font-bold text-white">{isStrategy ? 'Strategy' : 'Performance Reports'}</h1>
        </div>

        {/* Top-level tabs: Individual Indexes / All Indexes + Reload (Reports only) */}
        <div className="flex items-center justify-between">
          {/* Strategy has no subtitle (Apal 2026-10-04: "the text doesnt do much") —
              an empty slot keeps Reload on the right */}
          {isStrategy ? (
            <div />
          ) : (
          <div className="flex items-center space-x-1 bg-dark-800 p-1 rounded-lg w-fit">
            <button onClick={() => setTopTab('individual')}
              className={`px-5 py-2.5 rounded-md text-sm font-medium transition-all ${topTab === 'individual' ? 'bg-primary-600 text-white shadow-lg' : 'text-dark-300 hover:text-white hover:bg-dark-700'}`}>
              Individual Indexes
            </button>
            <button onClick={() => setTopTab('all_indexes')}
              className={`px-5 py-2.5 rounded-md text-sm font-medium transition-all ${topTab === 'all_indexes' ? 'bg-primary-600 text-white shadow-lg' : 'text-dark-300 hover:text-white hover:bg-dark-700'}`}>
              All Indexes
            </button>
          </div>
          )}
          <button onClick={() => setReloadKey(k => k + 1)} className="flex items-center gap-2 px-4 py-2 bg-dark-700 border border-dark-500 hover:bg-dark-600 hover:border-dark-400 rounded-lg text-dark-200 hover:text-white transition-colors text-sm font-medium">
            <RefreshCw className="w-3.5 h-3.5" />
            <span>Reload</span>
          </button>
        </div>

        {/* All Indexes (facts) and Strategy (decision tools) share the machinery */}
        {(topTab === 'all_indexes' || topTab === 'strategy') && <AllIndexesTab section={isStrategy ? 'strategy' : 'facts'} strategy={strategy} setStrategy={setStrategy} reportSource={reportSource} setReportSource={setReportSource} allIdxTab={allIdxTab} setAllIdxTab={setAllIdxTab} netCosts={netCosts} setNetCosts={setNetCosts} costConfig={costConfig} setCostConfig={setCostConfig} costSettingsOpen={costSettingsOpen} setCostSettingsOpen={setCostSettingsOpen} userHasShorts={userHasShorts} executionMode={executionMode} setExecutionMode={setExecutionMode} reloadKey={reloadKey} />}

        {/* Individual Indexes tab */}
        {topTab === 'individual' && (loading ? <IndividualSkeleton tab={reportTab} onTab={setReportTab} strategy={strategy} /> : (
          <>
            {/* ZONE A: Primary Controls — Index + Strategy + Reload (elevated bar) */}
            <div className="flex items-center justify-between bg-dark-800 border border-dark-600/40 rounded-xl px-5 py-4 shadow-sm">
              <div className="flex flex-wrap items-end gap-5">
                {/* Scope: which index */}
                <div>
                  <label className="text-dark-400 text-[11px] uppercase tracking-widest font-semibold mb-1.5 block">Index</label>
                  <SearchableIndexDropdown regions={regions} selectedSymbol={selectedSymbol} handleSymbolChange={handleSymbolChange} />
                </div>
                <div className="w-px h-10 bg-dark-600/50 self-stretch" />
                {/* The 4 data-changing selections — Model / Strategy / Costs / Execution */}
                <SegmentControl label="Model" value={reportSource} onChange={setReportSource}
                  options={[
                    { key: 'published', label: 'Published', activeBg: 'bg-sky-600 text-white', title: 'Report from the stored history — what clients see today' },
                    { key: 'latest', label: 'Latest', activeBg: 'bg-fuchsia-600 text-white', title: "MV's latest current-model scores — may differ from Published" },
                  ]} />
                <SegmentControl label="Strategy" value={strategy} onChange={setStrategy}
                  options={[
                    { key: '1x', label: 'Longs', activeBg: 'bg-emerald-600 text-white' },
                    ...(selectedSymbol && userShortsSymbols.includes(selectedSymbol.name) ? [
                      { key: 'shorts_only', label: 'Shorts', activeBg: 'bg-orange-600 text-white' },
                      { key: 'with_shorts', label: 'Both', activeBg: 'bg-violet-600 text-white' },
                    ] : []),
                  ]} />
                {['1x', 'with_shorts', 'shorts_only'].includes(strategy) && (
                  <>
                    <SegmentControl label="Costs" value={netCosts ? 'net' : 'gross'} onChange={(v) => setNetCosts(v === 'net')}
                      options={[
                        { key: 'gross', label: 'Gross', activeBg: 'bg-dark-500 text-white', title: 'Raw strategy returns, no costs' },
                        { key: 'net', label: 'Net', activeBg: 'bg-amber-500 text-white', title: 'Apply impact + carry + interest costs to strategy returns' },
                      ]}
                      trailing={
                        <button onClick={() => setCostSettingsOpen(true)} className="p-1.5 rounded-md text-dark-400 hover:text-amber-300 hover:bg-dark-700 transition-colors" title="Configure cost inputs">
                          <Settings2 className="w-4 h-4" />
                        </button>
                      } />
                    <SegmentControl label="Execution" value={executionMode} onChange={setExecutionMode}
                      options={EXECUTION_OPTIONS} />
                  </>
                )}
              </div>
            </div>

            {/* Status bar — truthful source per mode: BOTH are generated on demand
                (Published from DB history, Latest from MV's pushed current-model
                scores). Never show a file name — no mode reads the xlsx. */}
            {(() => {
              const activeData = reportTab === 'yearly_stats' ? yearlyData : reportTab === 'performance' ? perfData : data;
              const isLatest = reportSource === 'latest';
              const sourceLabel = isLatest ? 'MarketVibes model push (Latest)' : 'ClarityBox database (Published)';
              const updatedAt = activeData?.last_updated;
              if (!activeData) return null;
              return (
                <div className="flex items-center gap-3 px-3 py-2">
                  <FileText className="w-3.5 h-3.5 text-dark-500" />
                  <span className="text-xs text-dark-500">Source: <span className={isLatest ? 'text-fuchsia-300' : 'text-dark-400'}>{sourceLabel}</span></span>
                  <div className="w-px h-3.5 bg-dark-700" />
                  <span className="text-xs text-dark-500">{isLatest ? 'Pushed' : 'Updated'}: <span className="text-white font-medium">{updatedAt ? formatHumanTime(updatedAt) : 'generated live from the database'}</span></span>
                  {data?.current_model_revision != null && (
                    <>
                      <div className="w-px h-3.5 bg-dark-700" />
                      <span className="text-xs text-dark-500">
                        Model <span className="text-indigo-300 font-semibold">v{data.current_model_revision}</span>
                        <span className="text-dark-400"><ModelAge since={data.current_model_revision_since} original={data.current_model_revision_original} testId="report-model-age" /></span>
                      </span>
                    </>
                  )}
                  {reportSource !== 'latest' && modelDiff && modelDiff.latest_available !== false && modelDiff.changed && (
                    <>
                      <div className="w-px h-3.5 bg-dark-700" />
                      <span className="text-xs font-semibold text-amber-300" title={`The current model would change ${modelDiff.trades_changed} trade(s)${modelDiff.first_change_date ? ` from ${modelDiff.first_change_date}` : ''}. Switch to Latest to see it.`}>● Current model differs ({modelDiff.trades_changed})</span>
                    </>
                  )}
                </div>
              );
            })()}

            {/* Latest-mode banner — unmistakable cue that this is preview data, not Published */}
            {reportSource === 'latest' && (
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3 px-4 py-2.5 rounded-lg bg-fuchsia-500/10 border border-fuchsia-500/40">
                <div className="flex items-center gap-2 text-fuchsia-200 text-sm font-semibold">
                  <span className="px-2 py-0.5 rounded bg-fuchsia-600 text-white text-[11px] uppercase tracking-wide">Latest</span>
                  Current-model preview — not the published report
                </div>
                {!modelDiff && modelDiffLoading && <Skel className="h-3 w-64" testId="latest-verdict-pending" />}
                {modelDiff && (
                  modelDiff.latest_available === false
                    ? <span className="text-xs text-dark-400">No Latest data pushed yet for this index</span>
                    // the live model moved on since MV's last Latest push — never "Matches"
                    : modelDiff.latest_behind
                      ? <span data-testid="latest-behind" className="text-xs text-dark-300">Latest is v{modelDiff.model_revision}, live is v{modelDiff.published_revision} — not compared until a fresh push</span>
                    : modelDiff.changed
                      ? <span className="text-xs font-semibold text-amber-300">● Differs from Published — {modelDiff.trades_changed} trade{modelDiff.trades_changed === 1 ? '' : 's'} would change{modelDiff.first_change_date ? ` (from ${modelDiff.first_change_date})` : ''}</span>
                      : <span className="text-xs font-semibold text-emerald-300">✓ Matches Published — nothing changed</span>
                )}
              </div>
            )}

            {/* Main Report Card */}
            <div className={`dashboard-card ${reportSource === 'latest' ? 'ring-2 ring-fuchsia-500/50' : ''}`}>
              {/* Sub-tabs (underline style — visually subordinate) */}
              <div className="flex items-center gap-0 border-b border-dark-700 -mx-6 px-6 mb-5">
                {[
                  { id: 'yearly_stats', label: 'Yearly Statistics', icon: BarChart3 },
                  { id: 'performance', label: 'Performance Summary', icon: TrendingUp },
                  { id: 'transactions', label: 'All Transactions', icon: FileText },
                ].map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button key={tab.id} onClick={() => setReportTab(tab.id)}
                      className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px ${
                        reportTab === tab.id
                          ? 'border-primary-400 text-primary-300'
                          : 'border-transparent text-dark-500 hover:text-dark-300 hover:border-dark-600'
                      }`}>
                      <Icon className="w-3.5 h-3.5" />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Yearly Statistics */}
              {reportTab === 'yearly_stats' && (<>
                <ProcessingStatus active={yearlyLoading} title={yearlyData ? undefined : 'Loading results'} what={indivWhat} />
                <div className={dimClass(yearlyLoading && !!yearlyData)}>
                  {!yearlyData && yearlyLoading ? (
                    <YearlySkeleton strategy={strategy} />
                  ) : !yearlyData ? (
                    <div className="text-center py-12"><p className="text-dark-400">No yearly statistics available</p></div>
                  ) : (() => {
                    const yr = yearlyData.yearly_returns || [];

                    // Classify years by index return
                    const classify = (indexRet, year) => {
                      if (year >= 2024) return 'recent';
                      if (indexRet === null) return 'recent';
                      if (indexRet < -10) return 'bear';
                      if (indexRet >= -10 && indexRet < -5) return 'correction';
                      if (indexRet >= -5 && indexRet <= 5) return 'sideways';
                      return 'bull';
                    };

                    // Filter
                    let filtered = yr;
                    if (perfFilter === 'best') filtered = yr.filter(y => y.strategy_return > 0 && y.index_return !== null && y.strategy_return > y.index_return);
                    else if (perfFilter === 'worst') filtered = yr.filter(y => y.strategy_return < 0 || (y.index_return !== null && y.strategy_return < y.index_return));
                    else if (perfFilter === 'critical') filtered = yr; // shown grouped

                    // Sort (non-critical)
                    const sorted = perfFilter === 'critical' ? filtered : [...filtered].sort((a, b) => yearlySortOrder === 'desc' ? b.year - a.year : a.year - b.year);

                    // Group for critical view
                    const categoryOrder = ['bear', 'correction', 'sideways', 'bull', 'recent'];
                    const categoryLabels = { bear: 'Bear Markets', correction: 'Corrections', sideways: 'Sideways', bull: 'Bull Markets', recent: 'Recent' };
                    const categoryColors = {
                      bear: 'text-red-400 bg-red-900/20 border-red-800/50',
                      correction: 'text-orange-400 bg-orange-900/20 border-orange-800/50',
                      sideways: 'text-yellow-400 bg-yellow-900/20 border-yellow-800/50',
                      bull: 'text-green-400 bg-green-900/20 border-green-800/50',
                      recent: 'text-sky-400 bg-sky-900/20 border-sky-800/50',
                    };
                    const grouped = {};
                    if (perfFilter === 'critical') {
                      filtered.forEach(y => {
                        const cat = classify(y.index_return, y.year);
                        if (!grouped[cat]) grouped[cat] = [];
                        grouped[cat].push(y);
                      });
                    }

                    const yearsBeaten = filtered.filter(y => y.strategy_return !== null && y.index_return !== null && y.strategy_return > y.index_return).length;
                    const hasMAE = yr.some(y => y.avg_mae != null);

                    // Flagged years — the study list (Apal 2026-09-24). Thresholds
                    // fitted on the full dataset; running year exempt (no verdict yet).
                    const flags = flagYears(yr, new Date().getFullYear());
                    const flaggedList = Object.keys(flags).map(Number).sort((a, b) => b - a);
                    const jumpToYear = (year) => {
                      const el = document.getElementById(`year-row-${year}`);
                      if (!el) return;
                      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
                      el.classList.add('ring-2', 'ring-amber-400');
                      setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400'), 2600);
                    };

                    const renderYearRow = (y) => {
                      const beat = y.strategy_return !== null && y.index_return !== null && y.strategy_return > y.index_return;
                      const flag = flags[y.year];
                      return (
                        <tr key={y.year} id={`year-row-${y.year}`}
                          className={`border-t border-dark-700/50 hover:bg-dark-800/50 ${beat ? 'border-l-2 border-l-green-400' : 'border-l-2 border-l-red-400'} ${flag ? 'bg-amber-500/[0.07]' : ''}`}>
                          <td className="py-2.5 px-2 font-medium cursor-pointer text-primary-400 hover:text-primary-300 hover:underline transition-colors" onClick={() => setYearPopup(y)} title={`View ${y.year} transactions`}>{y.year}</td>
                          <td className="py-2.5 px-2 text-center text-white">{y.total_trades}</td>
                          {strategy === 'with_shorts' && (
                            <td className="py-2.5 px-2 text-center">
                              <span className="text-emerald-400">{y.long_trades || y.total_trades}</span>
                              <span className="text-dark-600 mx-1">/</span>
                              <span className="text-orange-400">{y.short_trades || 0}</span>
                            </td>
                          )}
                          <td className={`py-2.5 px-2 text-right font-medium ${y.strategy_return > 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {y.strategy_return > 0 ? '+' : ''}{y.strategy_return?.toFixed(2)}%
                          </td>
                          <td className="py-2.5 px-2 text-right text-blue-400">{y.index_return !== null ? `${y.index_return > 0 ? '+' : ''}${y.index_return.toFixed(2)}%` : '-'}</td>
                          <td className="py-2.5 px-2 text-center text-green-400">{y.winning_trades}</td>
                          <td className="py-2.5 px-2 text-center text-red-400">{y.losing_trades}</td>
                          <td className={`py-2.5 px-2 text-right ${y.win_rate >= 50 ? 'text-green-400' : 'text-red-400'}`}>{y.win_rate}%</td>
                          <td className="py-2.5 px-2 text-right text-teal-400">{y.time_in_market_pct}%</td>
                          {hasMAE && <td className="py-2.5 px-2 text-right text-orange-400">{y.avg_mae?.toFixed(2) ?? '-'}%</td>}
                          {hasMAE && <td className="py-2.5 px-2 text-right text-orange-400">{y.max_mae?.toFixed(2) ?? '-'}%</td>}
                          <td className="py-2.5 px-3 text-left">
                            {flag && (
                              <span data-testid={`year-flag-${y.year}`} title={flag.sentence}
                                className="inline-flex items-center gap-1 bg-amber-500/15 text-amber-400 text-[11px] font-medium px-2.5 py-0.5 rounded-full whitespace-nowrap cursor-help">
                                <Flag className="w-3 h-3" />{flag.reason}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    };

                    return (
                      <div className="space-y-4">
                        {/* Header with filter + counter */}
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-semibold text-white">Year-by-Year Comparison</h3>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2 text-xs">
                              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-green-500/30 border border-green-500/50 inline-block"></span>Beat</span>
                              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-500/30 border border-red-500/50 inline-block"></span>Under</span>
                            </div>
                            <span className="text-dark-400 text-sm">{filtered.length}/{yr.length} years</span>
                          </div>
                        </div>

                        {/* Flagged years — the study list, visible before scrolling */}
                        {flaggedList.length > 0 && (
                          <div data-testid="flagged-years-strip" className="flex items-center gap-2.5 flex-wrap bg-amber-500/[0.08] border border-amber-500/35 rounded-lg px-4 py-2.5">
                            <span className="flex items-center gap-1.5 text-amber-400 text-sm font-medium">
                              <Flag className="w-4 h-4" />{flaggedList.length} flagged year{flaggedList.length > 1 ? 's' : ''} to study
                            </span>
                            {flaggedList.map(year => (
                              <button key={year} onClick={() => jumpToYear(year)} title={flags[year].sentence}
                                className="bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 text-xs px-2.5 py-1 rounded-full transition-colors">
                                {year} · {flags[year].reason}
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Performance filter */}
                        <div className="flex items-center space-x-1 bg-dark-700 rounded-lg p-1 w-fit">
                          {[
                            { key: 'all', label: 'All Years' },
                            { key: 'critical', label: 'Critical Years' },
                            { key: 'best', label: 'Best' },
                            { key: 'worst', label: 'Worst' },
                          ].map(f => (
                            <button key={f.key} onClick={() => setPerfFilter(f.key)}
                              className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                                perfFilter === f.key ? 'bg-dark-600 text-white' : 'text-dark-400 hover:text-dark-200'
                              }`}>{f.label}</button>
                          ))}
                        </div>

                        {/* Table */}
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead><tr className="text-dark-400 text-xs uppercase tracking-wider">
                              <th className="text-left py-3 px-2 font-medium cursor-pointer hover:text-white" onClick={() => perfFilter !== 'critical' && setYearlySortOrder(s => s === 'desc' ? 'asc' : 'desc')}>
                                Year {perfFilter !== 'critical' ? (yearlySortOrder === 'desc' ? '↓' : '↑') : ''}
                              </th>
                              <th className="text-center py-3 px-2 font-medium">Trades</th>
                              {strategy === 'with_shorts' && <th className="text-center py-3 px-2 font-medium text-xs"><span className="text-emerald-400">L</span> / <span className="text-orange-400">S</span></th>}
                              <th className="text-right py-3 px-2 font-medium">Strategy Return</th>
                              <th className="text-right py-3 px-2 font-medium">Index Return</th>
                              <th className="text-center py-3 px-2 font-medium">Wins</th>
                              <th className="text-center py-3 px-2 font-medium">Losses</th>
                              <th className="text-right py-3 px-2 font-medium">Win Rate</th>
                              <th className="text-right py-3 px-2 font-medium">% In Market</th>
                              {hasMAE && <th className="text-right py-3 px-2 font-medium">Avg MAE</th>}
                              {hasMAE && <th className="text-right py-3 px-2 font-medium">Max MAE</th>}
                              <th className="text-left py-3 px-3 font-medium">Flag</th>
                            </tr></thead>
                            <tbody>
                              {perfFilter === 'critical' ? (
                                categoryOrder.filter(cat => grouped[cat]?.length > 0).map(cat => (
                                  <React.Fragment key={cat}>
                                    <tr><td colSpan={9 + (hasMAE ? 2 : 0)} className={`py-2 px-3 border-l-4 ${categoryColors[cat]}`}>
                                      <span className="font-semibold">{categoryLabels[cat]}</span>
                                      <span className="text-dark-400 ml-2 text-xs">({grouped[cat].length} years)</span>
                                    </td></tr>
                                    {grouped[cat].map(renderYearRow)}
                                  </React.Fragment>
                                ))
                              ) : sorted.map(renderYearRow)}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}
                </div></>
              )}

              {/* Year Transactions Popup */}
              {yearPopup && (yearPopup.trades?.length > 0) && (() => {
                const yr = String(yearPopup.year);
                // Render the backend's per-year breakdown directly. Each row is the
                // slice earned in THIS year: a position carried across New Year starts
                // on the year's first trading day at its year-start basis, so the rows
                // compound exactly to the header (both come from the same source).
                const yearTrades = yearPopup.trades;
                const totalGain = yearPopup.strategy_return ?? 0;
                const wins = yearPopup.winning_trades ?? 0;
                const losses = yearPopup.losing_trades ?? 0;
                const tradeCount = yearPopup.total_trades ?? yearTrades.length;
                return (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setYearPopup(null)}>
                    <div className="bg-dark-800 border border-dark-600 rounded-xl shadow-2xl w-full max-w-6xl max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-between px-6 py-4 border-b border-dark-600">
                        <div>
                          <h3 className="text-lg font-bold text-white">{yr} Transactions</h3>
                          <p className="text-xs text-dark-400 mt-0.5">
                            {tradeCount} trades · {wins}W / {losses}L ·
                            <span className={totalGain >= 0 ? ' text-green-400' : ' text-red-400'}> {totalGain > 0 ? '+' : ''}{totalGain.toFixed(2)}%</span>
                          </p>
                        </div>
                        <button onClick={() => setYearPopup(null)} className="p-2 rounded-lg text-dark-400 hover:text-white hover:bg-dark-700 transition-colors">
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="overflow-auto max-h-[calc(80vh-80px)] p-4">
                        {(() => {
                          // Compute streaks — running sum of consecutive same-direction gains
                          const popupTrades = [];
                          let streakSum = 0;
                          let streakDir = null; // 'win' or 'loss'
                          for (let i = 0; i < yearTrades.length; i++) {
                            const t = yearTrades[i];
                            const g = t.gain;
                            let streak = null;
                            if (g != null) {
                              const dir = g > 0 ? 'win' : 'loss';
                              if (dir === streakDir) {
                                streakSum += g;
                                streak = streakSum;
                              } else {
                                streakSum = 0;
                                streakDir = dir;
                              }
                            } else {
                              streakSum = 0;
                              streakDir = null;
                            }
                            popupTrades.push({ ...t, _gain: g, _streak: streak });
                          }
                          return (
                        <table className="w-full">
                          <thead className="sticky top-0 bg-dark-800">
                            <tr className="text-dark-400 text-xs uppercase tracking-wider">
                              <th className="text-left py-3 px-2 font-medium">Trade #</th>
                              <th className="text-left py-3 px-2 font-medium">Version</th>
                              {strategy === 'with_shorts' && <th className="text-left py-3 px-2 font-medium">Direction</th>}
                              <th className="text-left py-3 px-2 font-medium">Entry Date</th>
                              <th className="text-left py-3 px-2 font-medium">Exit Date</th>
                              <th className="text-right py-3 px-2 font-medium">Entry Price</th>
                              <th className="text-right py-3 px-2 font-medium">Exit Price</th>
                              <th className="text-right py-3 px-2 font-medium">Gain %</th>
                              <th className="text-right py-3 px-2 font-medium">Streak</th>
                              <th className="text-right py-3 px-2 font-medium">Days Held</th>
                              <th className="text-right py-3 px-2 font-medium">MAE %</th>
                            </tr>
                          </thead>
                          <tbody>
                            {popupTrades.map((t, idx) => {
                              const cL = t._gain != null && t._gain < 0 && idx > 0 && popupTrades[idx - 1]._gain != null && popupTrades[idx - 1]._gain < 0;
                              const cG = t._gain != null && t._gain > 0 && idx > 0 && popupTrades[idx - 1]._gain != null && popupTrades[idx - 1]._gain > 0;
                              return (
                              <tr key={idx} className={`border-t border-dark-700/50 hover:bg-dark-800/50 ${cL ? 'bg-red-900/20' : cG ? 'bg-green-900/20' : ''}`}>
                                <td className="py-3 px-2 text-white">{t.trade_number}</td>
                                <td className="py-3 px-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border ${versionChipClass(t.model_revision ?? 1)}`}>v{t.model_revision ?? 1}</span></td>
                                {strategy === 'with_shorts' && <td className="py-3 px-2">
                                  {t.direction === 'SHORT'
                                    ? <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-orange-600 text-white">↘ SHORT</span>
                                    : <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-600 text-white">↗ LONG</span>}
                                </td>}
                                <td className="py-3 px-2 text-dark-200">
                                  {formatDate(t.entry_date)}
                                  {t.carried_in && (
                                    <span className="ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-medium bg-dark-700 text-dark-400 border border-dark-600" title={`Position opened ${formatDate(t.orig_entry_date)} and carried into ${yr}; shown from the ${yr} open so only the ${yr} return is counted`}>carried in</span>
                                  )}
                                </td>
                                <td className="py-3 px-2">
                                  {t.is_open ? <OpenChip pending={data?.open_trade?.pending} />
                                  : t.carried_out ? <span className="text-dark-200">{formatDate(t.exit_date)} <span className="text-xs text-dark-500" title={`Still held at ${yr} year-end; carried into ${Number(yr) + 1}`}>(held)</span></span>
                                  : <span className="text-dark-200">{formatDate(t.exit_date)}</span>}
                                </td>
                                <td className="py-3 px-2 text-right text-dark-200">{t.entry_price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td className="py-3 px-2 text-right">
                                  {t.exit_price != null ? <span className={t.is_open ? 'text-emerald-400' : 'text-dark-200'}>{t.exit_price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                  : <span className="text-dark-400">-</span>}
                                </td>
                                <td className={`py-3 px-2 text-right font-medium ${t.gain >= 0.005 ? 'text-green-400' : t.gain <= -0.005 ? 'text-red-400' : 'text-dark-400'}`}>
                                  {t.gain != null ? signedPct2(t.gain) : '-'}
                                </td>
                                <td className={`py-3 px-2 text-right font-bold ${
                                  t._streak != null && t._streak < 0 ? (t._streak <= -5 ? 'bg-red-600/40 text-red-300 rounded' : 'bg-orange-600/30 text-orange-300 rounded')
                                  : t._streak != null && t._streak > 0 ? (t._streak >= 5 ? 'bg-green-600/40 text-green-300 rounded' : 'bg-teal-600/30 text-teal-300 rounded')
                                  : 'text-dark-500'
                                }`}>
                                  {t._streak != null ? `${t._streak > 0 ? '+' : ''}${t._streak.toFixed(2)}%` : '-'}
                                </td>
                                <td className={`py-3 px-2 text-right ${t.is_open ? 'text-emerald-400' : 'text-dark-300'}`}>{t.days_held ?? '-'}</td>
                                <td className={`py-3 px-2 text-right font-medium ${t.mae != null ? 'text-orange-400' : 'text-dark-400'}`}>{t.mae != null ? `${t.mae.toFixed(2)}%` : '-'}</td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Performance Summary */}
              {reportTab === 'performance' && (<>
                <ProcessingStatus active={perfLoading} title={perfData ? undefined : 'Loading results'} what={indivWhat} />
                <div className={dimClass(perfLoading && !!perfData)}>
                  {!perfData && perfLoading ? (
                    <PerfSkeleton />
                  ) : !perfData ? (
                    <div className="text-center py-12"><p className="text-dark-400">No performance data available</p></div>
                  ) : (() => {
                    const p = perfData.performance;
                    const ts = perfData.trade_statistics;
                    const pd = perfData.period;
                    const hasMAE = ts.avg_mae != null || ts.max_mae != null;

                    return (
                      <div className="space-y-5">
                        {/* Trading Period + Updated */}
                        <div className="flex items-center justify-between bg-dark-800/50 rounded-lg px-4 py-3 border border-dark-700">
                          <div>
                            <span className="text-dark-400 text-xs uppercase tracking-wider">Trading Period</span>
                            <div className="text-white font-semibold mt-0.5">
                              {formatDate(pd.first_trade_date)} to {formatDate(pd.last_trade_date)}
                              <span className="text-dark-400 font-normal ml-2">({pd.years} years)</span>
                            </div>
                          </div>
                          {perfData?.last_updated && (
                            <div className="text-right">
                              <span className="text-dark-400 text-xs uppercase tracking-wider">Updated</span>
                              <div className="text-dark-300 text-sm mt-0.5">{formatHumanTime(perfData.last_updated)}</div>
                            </div>
                          )}
                        </div>

                        {/* Two-column layout */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                          {/* LEFT: Performance Comparison */}
                          <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
                            <h4 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                              <TrendingUp className="w-5 h-5 text-primary-400" />
                              <span>Performance Comparison</span>
                              {perfData?.net_costs && <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-amber-900/40 text-amber-300 border border-amber-700/50">After Costs</span>}
                            </h4>
                            {/* Decade periods table */}
                            {p.periods && p.periods.length > 0 && (
                              <table className="w-full mb-5">
                                <thead>
                                  <tr className="border-b border-dark-600">
                                    <th className="py-3 px-4 text-left text-dark-400 text-xs uppercase tracking-wider font-semibold">Period</th>
                                    <th className="py-3 px-4 text-right text-blue-400 text-xs uppercase tracking-wider font-semibold">Index CAGR</th>
                                    <th className="py-3 px-4 text-right text-green-400 text-xs uppercase tracking-wider font-semibold">CL CAGR</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {p.periods.map((period, i) => {
                                    const isOverall = period.label.startsWith('Since');
                                    return (
                                      <tr key={i} className={isOverall ? 'border-t-2 border-primary-500/30' : 'border-b border-dark-700/50'}>
                                        <td className={`py-3.5 px-4 font-medium ${isOverall ? 'text-primary-400' : 'text-white'}`}>{period.label}</td>
                                        <td className="py-3.5 px-4 text-right text-blue-400 font-semibold tabular-nums">{period.index_cagr != null ? `${period.index_cagr}%` : '—'}</td>
                                        <td className="py-3.5 px-4 text-right text-green-400 font-bold tabular-nums text-lg">{period.strategy_cagr != null ? `${period.strategy_cagr}%` : '—'}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                          </div>

                          {/* RIGHT: Trade Statistics */}
                          <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
                            <h4 className="text-lg font-semibold text-white mb-4">Trade Statistics</h4>
                            <table className="w-full">
                              <tbody>
                                <tr className="border-b border-dark-800/50">
                                  <td className="py-3.5 px-4 text-white font-medium">Total Trades</td>
                                  <td className="py-3.5 px-4 text-right text-white font-bold text-lg">
                                    {ts.total_trades}
                                    {strategy === 'with_shorts' && ts.long_trades != null && ts.short_trades != null && (
                                      <span data-testid="perf-direction-split" className="ml-2 text-sm font-normal">
                                        (<span className="text-emerald-400">{ts.long_trades} long</span>
                                        <span className="text-dark-500 mx-1">/</span>
                                        <span className="text-orange-400">{ts.short_trades} short</span>)
                                      </span>
                                    )}
                                  </td>
                                </tr>
                                <tr className="border-b border-dark-800/50">
                                  <td className="py-3.5 px-4 text-white font-medium">Trades per Year</td>
                                  <td className="py-3.5 px-4 text-right text-primary-400 font-bold text-lg">{ts.trades_per_year}</td>
                                </tr>
                                <tr className="border-b border-dark-800/50">
                                  <td className="py-3.5 px-4 text-white font-medium">Win Rate</td>
                                  <td className="py-3.5 px-4 text-right text-green-400 font-bold text-lg">{ts.win_rate}%</td>
                                </tr>
                                <tr className="border-b border-dark-800/50">
                                  <td className="py-3.5 px-4 text-white font-medium">Wins / Losses</td>
                                  <td className="py-3.5 px-4 text-right text-base font-semibold">
                                    <span className="text-green-400">{ts.winning_trades}</span>
                                    <span className="text-dark-400 mx-2">/</span>
                                    <span className="text-red-400">{ts.losing_trades}</span>
                                  </td>
                                </tr>
                                <tr className="border-b border-dark-800/50">
                                  <td className="py-3.5 px-4 text-white font-medium">Avg Return/Trade</td>
                                  <td className="py-3.5 px-4 text-right text-primary-400 font-bold text-lg">{ts.avg_return_per_trade > 0 ? '+' : ''}{ts.avg_return_per_trade}%</td>
                                </tr>
                                <tr className="border-b border-dark-800/50">
                                  <td className="py-3.5 px-4 text-white font-medium">Avg Gain / Loss</td>
                                  <td className="py-3.5 px-4 text-right">
                                    <div className="font-bold text-base">
                                      <span className="text-green-400">+{ts.avg_gain_wins}%</span>
                                      <span className="text-dark-400 mx-2">/</span>
                                      <span className="text-red-400">{ts.avg_loss_losses}%</span>
                                    </div>
                                    {ts.win_loss_ratio && (
                                      <div className="text-sm text-yellow-400 font-semibold mt-1">Ratio: {ts.win_loss_ratio}x</div>
                                    )}
                                  </td>
                                </tr>
                                <tr className="border-b border-dark-800/50">
                                  <td className="py-3.5 px-4 text-white font-medium">Max Gain</td>
                                  <td className="py-3.5 px-4 text-right">
                                    <div className="text-green-400 font-bold text-lg">+{ts.max_gain}%</div>
                                    {ts.max_gain_date && <div className="text-xs text-dark-400 mt-0.5">{formatDate(ts.max_gain_date)}</div>}
                                  </td>
                                </tr>
                                <tr className="border-b border-dark-800/50">
                                  <td className="py-3.5 px-4 text-white font-medium">Max Loss</td>
                                  <td className="py-3.5 px-4 text-right">
                                    <div className="text-red-400 font-bold text-lg">{ts.max_loss}%</div>
                                    {ts.max_loss_date && <div className="text-xs text-dark-400 mt-0.5">{formatDate(ts.max_loss_date)}</div>}
                                  </td>
                                </tr>
                                <tr className="border-b border-dark-800/50">
                                  <td className="py-3.5 px-4 text-white font-medium">Avg Days Held</td>
                                  <td className="py-3.5 px-4 text-right text-white font-bold text-lg">{ts.avg_days_held}</td>
                                </tr>
                                {hasMAE && (
                                  <>
                                    <tr className="border-b border-dark-800/50">
                                      <td className="py-3.5 px-4 text-white font-medium">Avg MAE</td>
                                      <td className="py-3.5 px-4 text-right text-orange-400 font-bold text-lg">{ts.avg_mae}%</td>
                                    </tr>
                                    <tr>
                                      <td className="py-3.5 px-4 text-white font-medium">Max MAE</td>
                                      <td className="py-3.5 px-4 text-right">
                                        <div className="text-orange-400 font-bold text-lg">{ts.max_mae}%</div>
                                        {ts.max_mae_date && <div className="text-xs text-dark-400 mt-0.5">{formatDate(ts.max_mae_date)}</div>}
                                      </td>
                                    </tr>
                                  </>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div></>
              )}

              {/* All Transactions */}
              {reportTab === 'transactions' && data?.error && (
                <div className="flex items-center justify-center py-16">
                  <div className="text-center">
                    <FileText className="w-12 h-12 text-dark-600 mx-auto mb-3" />
                    <p className="text-dark-300 font-medium mb-1">Report Not Available</p>
                    <p className="text-dark-500 text-sm">{data.error}</p>
                  </div>
                </div>
              )}
              {reportTab === 'transactions' && !data?.error && (<>
                <ProcessingStatus active={txLoading} title={data ? undefined : 'Loading results'} what={indivWhat} />
                <div className={dimClass(txLoading && !!data)}>
                  {/* Filters + Export header */}
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-white">
                        {/* the counts are what the eye looks for — bright, bold, in the
                            Strategy colours (Apal 2026-10-29: "it looks so GREY
                            that it's not readable") */}
                        Transaction History {!data && txLoading ? <Skel className="h-3.5 w-28 ml-1" testId="tx-counts-pending" /> : <span data-testid="tx-header-counts" className="text-dark-300 font-normal text-base">
                          (<span className="text-white font-semibold tabular-nums">{filterType !== 'all' ? `${filteredTradeCount} of ${completedTrades}` : completedTrades}</span> trades
                          {strategy === 'with_shorts' && allPairs.some(t => t.direction === 'SHORT') && (
                            <> · <span className="text-emerald-400 font-semibold tabular-nums">{allPairs.filter(t => t.direction === 'LONG').length} long</span>
                              {' / '}<span className="text-orange-400 font-semibold tabular-nums">{allPairs.filter(t => t.direction === 'SHORT').length} short</span></>
                          )})
                        </span>}
                      </h3>
                    </div>
                    <div className="flex items-center gap-3">
                      <SwitchControl inline label="Analysis" checked={txAnalysis} onChange={setTxAnalysis} testid="tx-analysis"
                        title="Splits the trades into periods — Exceptional, Good, Average or Poor — with how long each lasted, what it made and how the index did" />
                      {txAnalysis && <GroupBySelect id="tx-group-by" value={txTarget} onChange={setTxTarget} testid="tx-group-by" />}
                      <ExportDropdown selectedSymbol={selectedSymbol} allPairs={allPairs} strategy={strategy} executionMode={executionMode} />
                    </div>
                  </div>

                  {/* Filter row */}
                  <div className="flex items-center justify-between flex-wrap gap-3 mb-5 pb-4 border-b border-dark-700">
                    <div className="flex items-center space-x-1 bg-dark-700 rounded-lg p-1">
                      <button onClick={() => setFilterType('all')} className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${filterType === 'all' ? 'bg-dark-600 text-white' : 'text-dark-400 hover:text-dark-200'}`}>All</button>
                      <button onClick={() => setFilterType('losses')} className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${filterType === 'losses' ? 'bg-red-600 text-white' : 'text-dark-400 hover:text-dark-200'}`}>Losses</button>
                      <button onClick={() => setFilterType('top10losses')} className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${filterType === 'top10losses' ? 'bg-red-600 text-white' : 'text-dark-400 hover:text-dark-200'}`}>Top 10 Losses</button>
                      <button onClick={() => setFilterType('consecutive_losses')} className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${filterType === 'consecutive_losses' ? 'bg-orange-600 text-white' : 'text-dark-400 hover:text-dark-200'}`}>Consecutive Losses</button>
                      <button onClick={() => setFilterType('top10profits')} className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${filterType === 'top10profits' ? 'bg-green-600 text-white' : 'text-dark-400 hover:text-dark-200'}`}>Top 10 Profits</button>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-1 bg-dark-700 rounded-lg p-1">
                        <span className="text-xs text-dark-400 px-2">Year:</span>
                        <select data-testid="tx-year-select" value={selectedYear} onChange={(e) => { setSelectedYear(e.target.value); if (e.target.value) { setStartDate(''); setEndDate(''); } }} className={`bg-dark-600 border-none text-xs rounded px-2 py-1.5 focus:outline-none cursor-pointer ${selectedYear ? 'text-primary-400 font-medium' : 'text-dark-300'}`}>
                          <option value="">All</option>
                          {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                      </div>
                      <span className="text-dark-500 text-xs">or</span>
                      <div className="flex items-center space-x-2 bg-dark-700 rounded-lg p-1">
                        <Calendar className="w-3.5 h-3.5 text-dark-400 ml-1" />
                        <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); if (e.target.value) setSelectedYear(''); }} className={`bg-dark-600 border-none text-xs rounded px-2 py-1 focus:outline-none w-28 ${startDate ? 'text-primary-400' : 'text-dark-400'}`} />
                        <span className="text-dark-500 text-xs">→</span>
                        <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); if (e.target.value) setSelectedYear(''); }} className={`bg-dark-600 border-none text-xs rounded px-2 py-1 focus:outline-none w-28 ${endDate ? 'text-primary-400' : 'text-dark-400'}`} />
                        {(startDate || endDate) && <button onClick={() => { setStartDate(''); setEndDate(''); }} className="text-dark-400 hover:text-white text-xs px-1">✕</button>}
                      </div>
                    </div>
                  </div>

                  {/* Range stat card — Signal Analysis' per-index card, for the
                      visible window/filter. Same calc module, so the two pages
                      can never disagree on the same range. */}
                  {!data && txLoading && <TxRangeStatsSkeleton />}
                  {rangeStats && (
                    <div data-testid="tx-range-stats" className="bg-dark-800 border border-dark-600/60 rounded-lg px-4 py-3 mb-5">
                      {/* every stat is a label-over-value block — one shared
                          grammar with the hero trio, so the row reads as one
                          organized band instead of a wrapping sentence */}
                      {(() => {
                        const Stat = ({ label, value, cls, extra, testid }) => (
                          <div className="min-w-[68px]">
                            <div className="text-dark-500 text-[9px] uppercase tracking-wider whitespace-nowrap">{label}</div>
                            <div data-testid={testid} className={`text-base font-bold tabular-nums whitespace-nowrap ${cls}`}>{value}{extra}</div>
                          </div>
                        );
                        return (
                      <div className="flex items-end justify-between flex-wrap gap-y-3">
                        <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
                          <Stat label="Gross Gains" value={`+${rangeStats.gross_gains}%`} cls="text-green-400" />
                          <Stat label="Gross Losses" value={`${rangeStats.gross_losses}%`} cls="text-red-400" />
                          <Stat label="Trades" testid="tx-stat-trades" value={`${rangeStats.winning}W / ${rangeStats.losing}L`} cls="text-white" />
                          <Stat label="Win Rate" value={`${rangeStats.win_rate}%`} cls={rangeStats.win_rate >= 50 ? 'text-green-400' : 'text-red-400'} />
                          <Stat label="Avg Win" value={`+${rangeStats.avg_win}%`} cls="text-green-400" />
                          <Stat label="Avg Loss" value={`${rangeStats.avg_loss}%`} cls="text-red-400" />
                          {rangeStats.best_trade != null && <Stat label="Best" value={`${rangeStats.best_trade > 0 ? '+' : ''}${rangeStats.best_trade}%`} cls="text-green-400" />}
                          {rangeStats.worst_trade != null && <Stat label="Worst" value={`${rangeStats.worst_trade > 0 ? '+' : ''}${rangeStats.worst_trade}%`} cls="text-red-400" />}
                          {rangeStats.avg_mae != null && <Stat label="MAE" value={`${rangeStats.avg_mae}%`} cls="text-yellow-400" />}
                          {rangeStats.avg_days != null && (
                            <Stat label="Avg Days" value={rangeStats.avg_days} cls="text-white"
                              extra={rangeStats.avg_days_long != null && rangeStats.avg_days_short != null
                                ? <span className="text-lime-400/80 font-semibold text-xs"> (long {rangeStats.avg_days_long} / short {rangeStats.avg_days_short})</span> : null} />
                          )}
                        </div>
                        <div className="ml-auto flex items-end gap-5 pl-5 border-l border-dark-600/60">
                          {rangeInMkt != null && (
                            <div className="text-right">
                              <div className="text-dark-500 text-[9px] uppercase tracking-wider">% In Mkt</div>
                              <div data-testid="tx-stat-inmkt" className={`text-xl font-bold tabular-nums ${rangeInMkt > 66.7 ? 'text-red-400' : 'text-teal-400'}`}>{rangeInMkt}%</div>
                            </div>
                          )}
                          {rangeIndexRet != null && (
                            <div className="text-right">
                              <div className="text-dark-500 text-[9px] uppercase tracking-wider">Index</div>
                              <div data-testid="tx-stat-index" className={`text-xl font-bold tabular-nums ${rangeIndexRet >= 0 ? 'text-blue-400' : 'text-blue-300'}`}>{rangeIndexRet > 0 ? '+' : ''}{rangeIndexRet}%</div>
                            </div>
                          )}
                          <div className="text-right">
                            <div className="text-dark-500 text-[9px] uppercase tracking-wider">Strategy</div>
                            <div data-testid="tx-stat-strategy" className={`text-xl font-bold tabular-nums ${rangeStats.strat_return >= 0 ? 'text-green-400' : 'text-red-400'}`}>{rangeStats.strat_return > 0 ? '+' : ''}{rangeStats.strat_return}%</div>
                          </div>
                        </div>
                      </div>
                        );
                      })()}
                    </div>
                  )}

                  {txAnalysis && txPeriods.periods.length > 0 && <PeriodSummary {...txPeriods} />}

                  {/* Table */}
                  {!data && txLoading ? <TableSkeleton testId="tx-skeleton" columns={txColumns(strategy)} />
                  : !trades.length ? <div className="text-center py-8"><p className="text-dark-400">No trades found</p></div>
                  : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead><tr className="text-dark-400 text-xs uppercase tracking-wider">
                          <th className="text-left py-3 px-2 font-medium cursor-pointer hover:text-white transition-colors" onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}>
                            Trade # {sortOrder === 'desc' ? '↓' : '↑'}
                          </th>
                          <th className="text-left py-3 px-2 font-medium">Version</th>
                          {strategy === 'with_shorts' && <th className="text-left py-3 px-2 font-medium">Direction</th>}
                          <th className="text-left py-3 px-2 font-medium">Entry Date</th>
                          <th className="text-left py-3 px-2 font-medium">Exit Date</th>
                          <th className="text-right py-3 px-2 font-medium">Entry Price</th>
                          <th className="text-right py-3 px-2 font-medium">Exit Price</th>
                          <th className="text-right py-3 px-2 font-medium">Gain %</th>
                          {!hideStreakColumn && <th className="text-right py-3 px-2 font-medium">Streak</th>}
                          <th className="text-right py-3 px-2 font-medium">Days Held</th>
                          {hasMAE && <th className="text-right py-3 px-2 font-medium">MAE %</th>}
                        </tr></thead>
                        <tbody>
                          {/* newest first: a trade waiting to enter leads the list */}
                          {sortOrder === 'desc' && (data?.pending_entries || []).map((p) => <PendingEntryRow key={p.verb} p={p} colSpan={8 + (strategy === 'with_shorts' ? 1 : 0) + (hideStreakColumn ? 0 : 1) + (hasMAE ? 1 : 0)} />)}
                          {filterType === 'consecutive_losses' && lossStreakGroups ? (
                            lossStreakGroups.map((group) => (
                              <React.Fragment key={`group-${group.id}`}>
                                <tr className="border-t-2 border-dark-600 bg-dark-800">
                                  <td colSpan={8 + (strategy === 'with_shorts' ? 1 : 0) + (hideStreakColumn ? 0 : 1) + (hasMAE ? 1 : 0)} className="py-3 px-3">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center space-x-4">
                                        <span className="text-orange-400 font-semibold">Streak #{group.id}</span>
                                        <span className="text-dark-400 text-sm">{group.trades.length} consecutive losses</span>
                                        <span className="text-dark-500 text-sm">{formatDate(group.startDate)} → {group.endDate ? formatDate(group.endDate)
                                          : <span className="text-amber-400" title="The last trade in this streak is the OPEN position — its loss is the running mark at the last stored close, not a booked exit">ongoing{group.markedDate ? ` · marked ${formatDate(group.markedDate)}` : ''}</span>}</span>
                                      </div>
                                      <div className={`px-4 py-1.5 rounded-lg font-bold text-lg ${group.totalLoss <= -10 ? 'bg-red-600/30 text-red-400 border border-red-500/50' : 'bg-orange-600/30 text-orange-400 border border-orange-500/50'}`}>
                                        {group.totalLoss.toFixed(2)}%
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                                {group.trades.map((t, idx) => (
                                  <tr key={`${group.id}-${idx}`} className="border-t border-dark-700/50 hover:bg-dark-800/50 bg-orange-900/10">
                                    <td className="py-3 px-2 text-white">{t.trade_number}</td>
                                    <td className="py-3 px-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border ${versionChipClass(t.model_revision ?? 1)}`}>v{t.model_revision ?? 1}</span></td>
                                    {strategy === 'with_shorts' && <td className="py-3 px-2">
                                      {t.direction === 'SHORT'
                                        ? <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-orange-600 text-white">↘ SHORT</span>
                                        : <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-600 text-white">↗ LONG</span>}
                                    </td>}
                                    <td className="py-3 px-2 text-dark-200">{formatDate(t.entry_date)}</td>
                                    <td className="py-3 px-2 text-dark-200">
                                      {String(t.exit_type || '').includes('Forced')
                                        ? <>{formatDate(t.exit_date)} <span className="text-xs text-amber-400" title="Position still open — this is the running mark at the last stored close, not a real exit">(Close)</span></>
                                        : formatDate(t.exit_date)}
                                    </td>
                                    <td className="py-3 px-2 text-right text-dark-200">{t.entry_price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    <td className="py-3 px-2 text-right text-dark-200">{t.exit_price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '-'}</td>
                                    <td className="py-3 px-2 text-right font-medium text-red-400">{t.gain !== null ? signedPct2(t.gain) : '-'}</td>
                                    <td className="py-3 px-2 text-right text-dark-300">{t.days_held ?? '-'}</td>
                                    {hasMAE && <td className="py-3 px-2 text-right font-medium text-orange-400">{t.mae != null ? `${t.mae.toFixed(2)}%` : '-'}</td>}
                                  </tr>
                                ))}
                              </React.Fragment>
                            ))
                          ) : trades.map((t, idx) => {
                            const cL = t.gain !== null && t.gain < 0 && idx > 0 && trades[idx - 1].gain !== null && trades[idx - 1].gain < 0;
                            const cG = t.gain !== null && t.gain > 0 && idx > 0 && trades[idx - 1].gain !== null && trades[idx - 1].gain > 0;
                            // Model-version boundary: a separator between adjacent rows whose version differs.
                            // Suppressed in magnitude-sorted views (Top 10) where versions aren't contiguous.
                            const prevT = idx > 0 ? trades[idx - 1] : null;
                            const versionBoundary = prevT && (t.model_revision ?? 1) !== (prevT.model_revision ?? 1)
                              && filterType !== 'top10losses' && filterType !== 'top10profits';
                            const newerRev = versionBoundary ? Math.max(t.model_revision ?? 1, prevT.model_revision ?? 1) : null;
                            const boundarySince = versionBoundary && newerRev === data?.current_model_revision ? data?.current_model_revision_since : null;
                            const boundaryCols = 8 + (strategy === 'with_shorts' ? 1 : 0) + (hideStreakColumn ? 0 : 1) + (hasMAE ? 1 : 0);
                            return (
                              <React.Fragment key={idx}>
                                {txAnalysis && periodOfTrade.has(t.trade_number) && (idx === 0 || periodOfTrade.get(prevT?.trade_number) !== periodOfTrade.get(t.trade_number)) && (
                                  <PeriodHeaderRow period={txPeriods.periods[periodOfTrade.get(t.trade_number)]} colSpan={boundaryCols} bar={txPeriods.bar} target={txPeriods.target} />
                                )}
                                {/* model boundaries say nothing about gains and losses — hidden in Analysis (Apal 2026-10-17) */}
                                {versionBoundary && !txAnalysis && (
                                  <tr>
                                    <td colSpan={boundaryCols} className="p-0">
                                      <div className={`flex items-center gap-3 px-3 py-2.5 my-2 border-y border-l-4 ${versionRailClass(newerRev)} border-y-dark-600/40 ${versionBandClass(newerRev)}`}>
                                        <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider rounded-md px-2.5 py-1 border ${versionChipClass(newerRev)}`}>
                                          <span className="opacity-70">◆</span>
                                          Model v{newerRev} boundary{boundarySince ? ` · live since ${new Date(boundarySince + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                                        </span>
                                        <div className="h-px flex-1 bg-current opacity-25" />
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              <tr className={`border-t border-dark-700/50 ${txAnalysis && periodOfTrade.has(t.trade_number)
                                  /* Analysis: the period's colour runs down the sheet; amber/red periods wash their rows */
                                  ? `border-l-4 ${PERIOD_TONE[txPeriods.periods[periodOfTrade.get(t.trade_number)].grade].edge} ${PERIOD_TONE[txPeriods.periods[periodOfTrade.get(t.trade_number)].grade].row}`
                                  : `border-l-2 ${versionRailClass(t.model_revision ?? 1)} ${cL ? 'bg-red-900/20' : cG ? 'bg-green-900/20' : ''}`} hover:bg-dark-800/50`}>
                                <td className="py-3 px-2 text-white">{t.trade_number}</td>
                                <td className="py-3 px-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border ${versionChipClass(t.model_revision ?? 1)}`}>v{t.model_revision ?? 1}</span></td>
                                {strategy === 'with_shorts' && <td className="py-3 px-2">
                                  {t.direction === 'SHORT'
                                    ? <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-orange-600 text-white">↘ SHORT</span>
                                    : <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-600 text-white">↗ LONG</span>}
                                </td>}
                                <td className="py-3 px-2 text-dark-200">{formatDate(t.entry_date)}</td>
                                <td className="py-3 px-2">
                                  {t.is_open ? <OpenChip pending={data?.open_trade?.pending} />
                                  : t.exit_type?.includes('Forced') ? <span className="text-dark-200">{formatDate(t.exit_date)} <span className="text-xs text-amber-400">(Close)</span></span>
                                  : <span className="text-dark-200">{formatDate(t.exit_date)}</span>}
                                </td>
                                <td className="py-3 px-2 text-right text-dark-200">{t.entry_price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td className="py-3 px-2 text-right">
                                  {t.is_open && t.last_close ? <span className="text-emerald-400">{t.last_close.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                  : t.exit_price ? <span className="text-dark-200">{t.exit_price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                  : <span className="text-dark-400">-</span>}
                                </td>
                                <td className={`py-3 px-2 text-right font-medium ${t.is_open ? (data?.open_trade?.current_gain >= 0 ? 'text-emerald-400' : 'text-red-400') : t.gain >= 0.005 ? 'text-green-400' : t.gain <= -0.005 ? 'text-red-400' : 'text-dark-400'}`}>
                                  {t.is_open && data?.open_trade?.current_gain != null ? signedPct2(data.open_trade.current_gain)
                                  : t.gain !== null ? signedPct2(t.gain) : '-'}
                                </td>
                                {!hideStreakColumn && (
                                  <td className={`py-3 px-2 text-right font-bold ${
                                    t.lossStreak !== null ? (t.lossStreak <= -5 ? 'bg-red-600/40 text-red-300 rounded' : 'bg-orange-600/30 text-orange-300 rounded')
                                    : t.gainStreak !== null ? (t.gainStreak >= 5 ? 'bg-green-600/40 text-green-300 rounded' : 'bg-teal-600/30 text-teal-300 rounded')
                                    : 'text-dark-500'
                                  }`}>
                                    {t.lossStreak !== null ? `${t.lossStreak.toFixed(2)}%` : t.gainStreak !== null ? `+${t.gainStreak.toFixed(2)}%` : '-'}
                                  </td>
                                )}
                                <td className={`py-3 px-2 text-right ${t.is_open ? 'text-emerald-400' : 'text-dark-300'}`}>{t.days_held ?? '-'}</td>
                                {hasMAE && <td className={`py-3 px-2 text-right font-medium ${t.mae != null ? 'text-orange-400' : 'text-dark-400'}`}>{t.mae != null ? `${t.mae.toFixed(2)}%` : '-'}</td>}
                              </tr>
                              </React.Fragment>
                            );
                          })}
                          {sortOrder !== 'desc' && (data?.pending_entries || []).map((p) => <PendingEntryRow key={p.verb} p={p} colSpan={8 + (strategy === 'with_shorts' ? 1 : 0) + (hideStreakColumn ? 0 : 1) + (hasMAE ? 1 : 0)} />)}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div></>
              )}
            </div>
          </>
        ))}
      </div>
      {costSettingsOpen && <CostSettingsModal costConfig={costConfig} setCostConfig={setCostConfig} onClose={() => setCostSettingsOpen(false)} allowedSymbols={regions.flatMap(r => r.symbols?.map(s => s.name) || [])} />}
    </Layout>
  );
};

export default Reports;
export { SignalAnalysis, CompareIndexes, AllIndexesTab }; // dev-only preview harnesses (/dev-preview/signal-analysis, /dev-preview/compare)
